/**
 * settings.ts — раздел настроек кабинета (WEB-1874, этап 3, порция S2).
 *
 * Что сюда входит: личные настройки (профиль, пароль, двухфакторная защита,
 * опасная зона), настройки проекта (ключи API, вебхуки, постбэк, S2S, льготный
 * период, синхронизация провайдеров, команда и приглашения), подключение
 * приложения, организации и передача владения, контекст проекта, письма-
 * последовательности и CAPI.
 *
 * ПОЧЕМУ ОДИН СЛОВАРЬ НА ВСЁ ЭТО. Это один раздел меню («Настройки») и один
 * набор экранов, которые делят между собой карточки и модалки: подтверждение
 * удаления живёт и в проекте, и в организации; таблица участников — и в
 * настройках проекта, и на экране команды. Разрезать словарь по файлам значило
 * бы завести те же надписи дважды и получить ровно ту смесь языков, ради
 * которой карточка и заводилась.
 *
 * ЧЕГО ЗДЕСЬ НЕТ. Общие слова («Отмена», «Сохранить», «Удалить», «Назад»,
 * «Далее», «Закрыть», «Готово») — в `common.ts`: семнадцатая копия слова
 * «Отмена» и есть способ рассинхронизировать язык. Содержимое писем покупателю
 * (`email-sequences`) не переводится вовсе — это письма на языке ВОРОНКИ, мы
 * переводим только редактор вокруг них.
 */
import { defineDictionary } from '../define';
import { plural } from '../plural';
import { WEBHOOK_SECRET_GRACE_HOURS } from '@/constants/webhookSecretGrace';
import {
  SDK_IOS_IMPORT_LINE,
  SDK_SERVER_SNIPPET_GUID,
  sdkAndroidConfigureCall,
  sdkIosConfigureCall,
} from '@/components/project/app-connection/sdkSnippetForms';

/**
 * WEB-2338 — окно перекрытия при ротации секрета подписи: число берётся из
 * зеркала серверной константы (`constants/webhookSecretGrace.ts`), а не
 * вписывается руками. Одна формулировка на подсказку при правке получателя
 * и на подтверждение ротации — раньше первая обещала «перестанет подходить
 * сразу», вторая — «некоторое время».
 */
const GRACE_H = WEBHOOK_SECRET_GRACE_HOURS;
const GRACE_HOURS_EN = `${GRACE_H} ${plural('en', GRACE_H, { one: 'hour', other: 'hours' })}`;
const GRACE_HOURS_RU = `${GRACE_H} ${plural('ru', GRACE_H, { one: 'час', few: 'часа', many: 'часов' })}`;
/**
 * WEB-3757 юнит 4: окно дожития ключа доступа после ротации — копия серверной
 * `DEFAULT_ROTATE_GRACE_HOURS` (`backend/src/projects/services/api-keys.service.ts`);
 * совпадение держит случай «часы дожития…» в `components/settings/__tests__/ApiKeysSection.test.tsx`.
 */
const KEY_GRACE_H = 72;
const KEY_GRACE_HOURS_EN = `${KEY_GRACE_H} ${plural('en', KEY_GRACE_H, { one: 'hour', other: 'hours' })}`;
const KEY_GRACE_HOURS_RU = `${KEY_GRACE_H} ${plural('ru', KEY_GRACE_H, { one: 'час', few: 'часа', many: 'часов' })}`;

/** Значения текста «Инструкция для разработчика» (WEB-3757 юнит 2): их собирает код раздела. */
export interface AcMainGuide {
  projectId: string;
  apiBase: string;
  /** Строка `Web2App.configure(…)` из `sdkSnippetForms.ts`. */
  configure: string;
  entitlementUrl: string;
  ios: string;
  android: string;
  docsUrl: string;
}
const ROTATE_GRACE_EN = `The old secret keeps working for another ${GRACE_HOURS_EN} so your server can switch to the new one, then it stops.`;
const ROTATE_GRACE_RU = `Старый секрет будет подходить ещё ${GRACE_HOURS_RU}, чтобы ваш сервер успел перейти на новый, потом перестанет.`;

export const settingsStrings = defineDictionary(
  {
    // ── Личные настройки: оболочка ──────────────────────────────────────────
    pageTitle: 'Personal settings',
    tabAccount: 'Account',
    tabOrganization: 'Organization',

    // ── Профиль ─────────────────────────────────────────────────────────────
    profileUsernameLabel: 'Name',
    profileUsernameEmpty: 'The name cannot be empty',
    profileAvatarLabel: 'Avatar',
    profileAvatarUrlInvalid: 'Invalid URL — it has to start with https://',
    profileAvatarUrlPlaceholder: 'https://',
    profileAvatarUpload: 'Upload',
    profileAvatarUploaded: 'Avatar uploaded',
    profileAvatarUploadFailed: 'Could not upload the image',
    profileSaved: 'Saved',
    profileAvatarSaved: 'Avatar saved',
    profileSaveFailed: 'Could not save the profile',

    // ── Требования к паролю (зеркало серверного @IsStrongPassword) ──────────
    pwdRuleLen: 'At least 8 characters',
    pwdRuleUpper: 'An uppercase Latin letter (A–Z)',
    pwdRuleLower: 'A lowercase Latin letter (a–z)',
    pwdRuleDigit: 'A digit',
    pwdRuleSpecial: 'A special character (for example ! @ # $ %)',

    // ── Пароль ──────────────────────────────────────────────────────────────
    pwdChangeTitle: 'Change the password',
    pwdAddTitle: 'Add a password',
    pwdTooManyAttempts: 'Too many attempts. Try again later.',
    pwdCurrentWrong: 'Wrong current password.',
    pwdCodeInvalid: 'The code is wrong or has expired.',
    pwdGenericError: 'Something went wrong. Try again.',
    pwdGoogleOnlyHint:
      'You sign in with Google — there is no password yet. Add one to sign in by email and to confirm dangerous actions.',
    pwdCodeSentTo: (email: string) => `Code sent to ${email}`,
    pwdCurrentLabel: 'Current password',
    pwdEmailCodeLabel: 'Code from the email',
    // WEB-3758 заход 28: кнопка отправки кода в окне пароля (эталон me-k.jsx стр. 47)
    pwdSendCode: 'Send a code by email',
    pwdSixDigits: '6 digits',
    pwdNewLabel: 'New password',
    pwdPasswordLabel: 'Password',
    pwdRepeatLabel: 'Repeat the password',
    pwdMismatch: 'The passwords do not match',
    pwdChangeSubmit: 'Change the password',
    pwdChangedToast: 'Password changed',
    pwdAddedToast: 'Password added',
    pwdChangeViaEmail: 'Change by email',

    // ── Двухфакторная защита ────────────────────────────────────────────────
    twoFactorTitle: 'Two-factor authentication',
    twoFactorDisable: 'Turn off 2FA',
    twoFactorEnable: 'Turn on 2FA',
    twoFactorSetupTitle: '2FA setup',
    twoFactorDisabledToast: 'Two-factor authentication turned off',
    twoFactorDisableFailed: 'Could not turn off two-factor authentication',
    twoFactorRegenFailed: 'Could not refresh the recovery codes',
    twoFactorDisableConfirm: 'Turn off',
    twoFactorDisableHint:
      'To turn it off, enter your password and the current code:',
    twoFactorRegenTitle: 'Refreshing the recovery codes',
    twoFactorRegenOk: 'Refresh',
    twoFactorRegenHint:
      'Enter the current 2FA code. This invalidates your previous recovery codes.',
    // WEB-1247: у резервного кода теперь СВОЁ поле, поэтому подсказки под
    // одноразовый и резервный код разведены — прежний текст приглашал ввести
    // резервный код туда, где его было некуда положить.
    twoFactorDisableHintRecovery:
      'To turn it off, enter one of your recovery codes:',
    twoFactorRegenHintRecovery:
      'Enter one of your recovery codes. This invalidates your previous recovery codes.',
    twoFactorUseRecovery: 'I have a recovery code',
    twoFactorUseCode: 'Enter a confirmation code instead',
    twoFactorRecoveryPlaceholder: 'xxxxx-xxxxx',
    twoFactorSendCode: 'Email me a code',
    twoFactorCodeSentTo: (email: string) => `Code sent to ${email}`,
    twoFactorSendCodeFailed: 'Could not send the code',
    twoFactorCodeRequired: 'Enter the code',
    twoFactorRecoveryRequired: 'Enter a recovery code',
    twoFactorStepMethod: 'Method',
    twoFactorStepQr: 'QR code',
    twoFactorStepConfirm: 'Confirmation',
    twoFactorStepRecovery: 'Recovery codes',
    twoFactorSetupFailed: 'Could not start the 2FA setup',
    twoFactorWrongCode: 'Wrong code',
    twoFactorMethodTotp: 'Authenticator app (TOTP) — recommended',
    twoFactorMethodEmail: 'Code by email',
    twoFactorConfirmPasswordHint:
      'Confirm your password to turn on the second factor.',
    twoFactorGoogleHint:
      'You sign in with Google — confirm this is your account.',
    twoFactorGoogleConfirmed: 'Google account confirmed',
    twoFactorGoogleFailed: 'Could not confirm through Google',
    twoFactorGoogleUnavailable:
      'Could not load Google sign-in — contact support to turn on two-factor protection.',
    twoFactorManualKey: 'Or enter the key manually:',
    twoFactorEmailCodesTo: (email: string) => `Codes will arrive at ${email}.`,
    twoFactorFinish: 'Done — codes saved',

    // ── Резервные коды (WEB-3758 заход 28, возврат: кнопки кодов — решение владельца 27.10) ──
    recoveryCopied: 'Recovery codes copied',
    recoveryCopyFailed: 'Could not copy the recovery codes',
    recoveryShownOnceTitle: 'Shown only once',
    recoveryShownOnceText:
      'Save the codes somewhere safe — each one works only once.',
    recoveryCopyAll: 'Copy all',
    recoveryDownload: 'Download .txt',

    // ── Опасная зона личного аккаунта ───────────────────────────────────────
    dangerZoneTitle: 'Danger zone',
    accountDeleteHeading: 'Delete the account',
    accountDeleteText:
      'The account, the organizations without other owners and their projects will be deleted permanently.',
    accountDeleteNoPasswordTip: 'Add a password first (the tab above)',
    accountDeleteConfirmTitle: 'Delete the account?',
    accountDeleteConfirmBody:
      'The account and the organizations without other owners (with all their projects) will be deleted. This cannot be undone.',
    accountDeletedToast: 'Account deleted',

    // ── Настройки проекта: оболочка и «Основные» ────────────────────────────
    projectSettingsTitle: 'Project settings',
    tabGeneral: 'General',
    tabMembers: 'Members',
    tabPayments: 'Payments',
    tabIntegrations: 'Analytics and pixels',
    tabContext: 'Context',
    tabAppConnection: 'App connection',
    // WEB-2126: на пути «приложение не подключаю» вкладка называется по тому,
    // что в ней осталось, — иначе человек без моста в неё просто не зайдёт, а
    // ключи ему нужны.
    tabKeysWebhooks: 'Keys and webhooks',
    generalReadOnly: 'Your role only lets you view the settings',
    // WEB-4116 (сверка вида 31.10, мелочь 26): короче, как у эталона settings-k.jsx.
    bridgeConfigLoadError: 'Could not load the store links. The fields are locked',
    projectNameLabel: 'Project name',
    projectNameRequired: 'Enter the project name',
    projectNamePlaceholder: 'My project',
    storeUrlInvalid: 'Invalid link',
    storeFallbackHint:
      'Store fallback: visitors are sent here when the funnel is off or broken',
    projectSaved: 'Project saved',
    storeUrlSaveFailed: 'Could not save the store links.',
    nameSavedStoreFailed: (details: string) => `The name is saved. ${details}`,
    settingsSaveFailed: 'Could not save the settings',
    deleteProject: 'Delete the project',
    deleteCascadeList: (quizzes: number, paywalls: number, invites: number) =>
      `${quizzes} ${quizzes === 1 ? 'quiz' : 'quizzes'}, ` +
      `${paywalls} ${paywalls === 1 ? 'paywall' : 'paywalls'}, ` +
      `${invites} ${invites === 1 ? 'invite' : 'invites'}`,
    deleteProjectCascade: (list: string) =>
      `The following will be deleted: ${list}. `,
    deleteProjectGeneric:
      'Deleting the project cannot be undone — every funnel, quiz and paywall will be deleted. ',
    deleteProjectDomainsNote: 'Paid domains stay at the organization level.',
    // WEB-2438 Ж8: и владелец организации.
    deleteProjectTip:
      'The project can be deleted by its owner or by an organization owner or administrator',
    membersCardTitle: 'Project members',
    inviteButton: 'Invite',
    activeInvitesTitle: 'Active invites',
    membersReadOnly:
      'Member and RM do not manage the team — the list is read-only',
    inviteLinkCopied: 'Invitation link copied',
    // WEB-3756 юнит 1: каркас настроек проекта нового вида — подпись ряда
    // вкладок-облачков для чтения с экрана (эталон settings-k.jsx, Lens).
    projectSettingsTabsLabel: 'Settings sections',
    // WEB-3756 юнит 6: «Платежи» на ките — список слева, строка Stripe, окно «События»
    // (эталон settings-k.jsx: Pay, Conn, окно events). Сноски pay-helper и pay-footnote,
    // подвкладки и свёртка «Скоро» сняты вместе с ключами (владелец 25.10 пп. 69, 72).
    payListSystems: 'Payment systems',
    payListSoon: 'Coming soon',
    payListRules: 'Rules',
    payStripeNoteNone: 'not connected',
    payStripeNoteSandbox: 'sandbox',
    payStripeNoteLive: 'live mode',
    payEnvRow: 'Environment',
    payConnectLead:
      'Paste the keys from your Stripe dashboard. Want to start with test keys — enter a pk_test_ and sk_test_ pair here.',
    payWebhookEventsBtn: (count: number) => `Events · ${count}`,
    payEventsTitle: 'Stripe events',
    // WEB-3756 юнит 9: «Деньги покупателей» на ките — журнал списаний окном по кнопке
    // (владелец 25.10 п. 63); заголовок окна — прежний `payJournalBranch`. Сводки
    // веток сняты вместе с ключами `payTrialOnceSummary`, `payJournalTotal`,
    // `payMoneyLockedChip` (п. 62: всё раскрыто сразу).
    payJournalOpen: 'Open the log',
    // WEB-3756 юнит 8: окна «Платежей» на ките. Окно отключения Stripe — текстом макета
    // (settings-k.jsx, unbindAll; владелец 25.10 п. 71): вопрос с числом активных из
    // предпросмотра и одна строка о ключах и разовых покупках; подпись выбора ветки.
    payUnbindAllQuestion: (count: number) =>
      `What to do with the ${count} active ${plural('en', count, { one: 'subscription', other: 'subscriptions' })}?`,
    payUnbindAllKeysLine:
      'Live and test keys are removed; one-time purchases end.',
    payUnbindChoiceLabel: 'What to do with the subscriptions',
    // WEB-3756 доводка 1: подписи и пояснения трёх веток судьбы подписок и заголовок
    // красной плашки «Оставить в Stripe» — текстом эталона (settings-k.jsx, unbindAll;
    // владелец 25.10 п. 71). Прежние `cancelNowTitle` и соседи в billing.ts берёт окно
    // `StripeConnectionCard.tsx` — их не трогаем.
    payUnbindBranchNowTitle: 'Cancel now',
    payUnbindBranchNowText: 'Access ends at once.',
    payUnbindBranchEndTitle: 'Cancel at period end',
    payUnbindBranchEndText: 'Access lasts until the paid period ends.',
    payUnbindBranchKeepTitle: 'Keep in Stripe',
    payUnbindBranchKeepText: 'Charges continue, no access.',
    payUnbindKeepWarnTitle: 'You will have to cancel them in Stripe yourself',
    // WEB-3756 доводка 1: одна кнопка льготного периода сохранила оба поля.
    payGraceBothSaved: 'Saved',
    // WEB-2027: `paymentsInfoTitle` / `paymentsInfoText` сняты. Справка вкладки
    // «Платежи» была синим алертом страницы; по макету переверстки это
    // приглушённая строка внутри раздела с другим текстом — ключ `payHelper`.
    // Старые ключи не читал больше никто (проверено обходом `frontend/src`).
    // WEB-2121: `pixelsInfoTitle` / `pixelsInfoText` сняты вместе с плашкой
    // раздела — после деления на подвкладки её место заняли две подписи-шапки
    // (`analyticsTabAdsIntro` / `analyticsTabAnalyticsIntro`; вторая снята в
    // WEB-4116 вместе с абзацем над Amplitude). Других
    // потребителей у пары не было (проверено обходом `frontend/src`).
    subtabDev: 'For developers',
    // WEB-1198 (решение владельца 12.09, так же в макете): подвкладка
    // называется «Логи SDK», а не «Журнал SDK». Ключ живёт ровно в одном
    // месте — у корешка подвкладки, поэтому меняем значение пары, а не
    // заводим второй ключ.
    subtabSdkLogs: 'SDK logs',
    // WEB-1198, блок 1: новая подвкладка и вопрос первого входа. Тексты —
    // дословно из задания 24 §5.0; английская половина своя, в терминах
    // словаря §1 задания («наша библиотека» = our library, техимя SDK рядом).
    subtabMain: 'Main',
    acFirstVisitTitle:
      'Who decides in your app that a person has a subscription?',
    acPathLibTitle: 'Our library in the app',
    acPathLibTech: 'SDK',
    acPathLibDesc: 'Your developer adds our code (SDK)',
    acPathServerTitle: 'Own server',
    acPathServerDesc: 'Your backend asks us with a key',
    acPathProviderTitle: 'Subscriptions live in a third-party service',
    acPathProviderDesc: 'Adapty, RevenueCat and the like',
    // WEB-2126: четвёртый путь. Формулировка НЕ «приложения нет» — приложение
    // у клиента вполне может быть, он просто не связывает его с квизами
    // (указание владельца 17.09).
    acPathNoneTitle: 'Not connecting an app',
    acPathNoneDesc: 'Quizzes and the app stay separate',
    acPathLineLead: 'Your path:',
    acPathLineChange: 'Change',
    acDevStatusNoBridgeTitle: 'How to take the data to your side',
    acDevStatusNoBridgeText:
      'A key for requests from your server, events to your address and reports to an ad tracker.',
    // Строка «Не знаете? <ссылка> — или пропустите…» разрезана на три ключа:
    // ссылка стоит в середине предложения, поэтому одной строкой её не
    // собрать без вёрстки внутри словаря.

    // WEB-1198, исполнитель A: ствол подвкладки «Основное» — блоки 2, 3, 4, 5
    // и 8. Тексты — задание 24 §5.1–5.4 и §5.6; с WEB-3757 юнит 2 тексты, которых
    // нет в эталоне рескина, сняты, новые — блоком «WEB-3757 юнит 2» ниже.
    // Блок 2 — полоса состояния связки и строка пути.
    acMainStatusDemoText:
      'The app receives a “subscription is active” answer to any request — real purchases are not read.',
    acMainStatusDemoOff: 'Turn off',
    // WEB-1225: выключение в черновике, на сервере режим ещё включён.
    acMainStatusDemoOffPending: 'It will turn off once you save the settings.',
    acMainStatusNever: 'The app has not contacted us yet.',
    acMainStatusOkText: (when: string) =>
      `The app reported an install: ${when}`,
    acMainStatusWarnTitle: 'What is missing for a full link:',
    acMainMissingIos: 'App Store link',
    acMainMissingAndroid: 'Google Play link',
    acMainMissingSecret: 'The signing secret has not been issued',
    acMainPathNameAll: 'Not chosen',
    acMainFailText:
      'Saving is off so that already saved values are not overwritten.',
    acMainCopy: 'Copy',
    acMainCopied: 'Copied',
    // Блок 3 — секция «Подключение».
    acMainConnectTitle: 'Connection',
    acMainProjectIdLabel: 'Project ID',
    acMainApiBaseLabel: 'Our API address',
    acMainSecretLabel: 'Signing secret',
    acMainSecretIssuedValue: 'issued · the value was shown once',
    acMainSecretNotIssuedValue: 'not issued',
    acMainSecretIssue: 'Issue',
    acMainSecretRotate: 'Reissue',
    acMainSecretNotNeeded:
      'The signing secret is not needed with your method — the vendor profile reaches us unsigned.',
    acMainSecretIssuedUnused:
      'The signing secret is issued, but is not used with your method.',
    acMainSecretIssueAnyway: 'Issue it anyway',
    acMainSecretShow: 'Show',
    acMainSecretForeignHint:
      'Not needed with your method: the vendor profile reaches us unsigned. You will only need it if you want to look a buyer up by email from your side.',
    acMainShowCode: 'Show the code',
    acMainDlgClose: 'Close',
    acMainDlgCodeTitle: 'Code for the app',
    acMainDlgCodeHint: 'One line at app start:',
    acMainDlgSecretNewTitle: 'The signing secret is issued',
    acMainDlgSecretAgainTitle: 'The secret is reissued',
    acMainDlgSecretHint:
      'We show it once. Save it on your server — we will not show it a second time.',
    acMainDlgSecretGrace: `The old secret keeps working for ${GRACE_HOURS_EN} so the app has time to update.`,
    acMainDlgSecretOk: 'I have saved the secret — close',
    // Блок 4 — две ссылки на магазины.
    acMainStoresTitle: 'Where the button leads after payment',
    acMainIosLabel: 'App Store',
    acMainAndroidLabel: 'Google Play',
    acMainIosPlaceholder: 'https://apps.apple.com/app/id…',
    acMainAndroidPlaceholder: 'https://play.google.com/store/apps/details?id=…',
    // Блок 5 — «Оплата из приложения».
    acMainWebPayTitle: 'Payment from inside the app',
    acMainWebPayLabel: 'Your payment page address',
    acMainWebPayFieldHint:
      'A published page with prices. Empty — we do not hand out the link, you lead to the payment yourself.',
    acMainWebPayPlaceholder: 'https://pay.example.com/offer',
    // Блок 8 — хвост подвкладки.
    acMainSave: 'Save settings',
    // Окно «уйти с несохранёнными правками» (WEB-2343). Три действия: сохранить
    // и уйти · уйти без сохранения · остаться.
    acMainLeaveTitle: 'The settings have changed but are not saved',
    acMainLeaveBody:
      'You changed the connection settings and did not save them. Save the changes before leaving?',
    acMainLeaveSave: 'Save',
    acMainLeaveDiscard: 'Do not save',
    acMainLeaveStay: 'Stay',
    acMainSaveFailed: 'Could not save the settings',
    // WEB-3757 юнит 2 — «Основное» на ките, тексты эталона settings-app.jsx
    // (спек-репо ce823f8f, стр. 121–177 и окна стр. 392–425); замок тарифа — без
    // названия тарифа (решение владельца 27.10 № 8, как у «Контекста» и A/B).
    acMainBarTestTitle: 'Test mode is on',
    acMainBarOkTitle: 'The link works',
    acMainLockText: 'App connection is not available on your plan',
    acMainLockPay: 'Pay for a plan',
    // WEB-1168: условие сборщика ссылки установки — нет пакета Android и пригодного трекера.
    acMainInstallLinkUnbuildable:
      'The install link cannot be built: no Android package name is set and no tracker is selected — the buyer will only get an email',
    // WEB-2458 п.4: у кого нет права на деньги — текст вместо кнопки тарифа.
    acTierAskOwner:
      'The plan is changed by the organization owner or administrator — forward this page to them',
    acMainLoadFailTitle: 'Could not load the settings',
    acMainLeaveLoadFail:
      'Could not load the settings. Saving is off so that already saved values are not overwritten.',
    acMainSecretHelp:
      'Your server signs requests to us with it. Without a signature we do not return a buyer by email — protection from outside access. Do not keep the secret in the app code: it can be pulled out from there.',
    acMainGuideBtn: 'Developer instructions',
    acMainGuideLead:
      'A ready text. Copy it and pass it to the developer — by email, in a messenger or as a tracker task, as you like.',
    acMainGuideText: (g: AcMainGuide) =>
      'Hi! I am setting up browser payment with access granted in the app (web2app).\n' +
      'One step from you — add the library and check the response once.\n\n' +
      `Project ID: ${g.projectId}\nAPI address: ${g.apiBase}\n\n` +
      `One line at app start:\n  ${g.configure}\n\n` +
      `Access check — one GET, no key:\n  ${g.entitlementUrl}\n` +
      'Access exists if grants has at least one with status: "active".\n' +
      'The library fills in the GUID.\n\n' +
      `Libraries: iOS ${g.ios} · Android ${g.android}\nDocs: ${g.docsUrl}\n\n` +
      'I will send the signing secret separately — it is needed only if requests go from our server.',
    acMainGuideNoSecretTitle: 'The signing secret is left out on purpose.',
    acMainGuideNoSecretText:
      'It opens access to buyer data, so it does not belong in a chat — pass it separately and through a different channel.',
    acMainGuideCopy: 'Copy the text',
    acMainGuideCopied: 'Text copied',
    acMainCodeVersions: (ios: string, android: string) =>
      `Library versions: iOS ${ios} · Android ${android}`,

    // ── Названия ролей ──────────────────────────────────────────────────────
    // Проектные роли по решению WEB-665 короткие и английские в ОБЕИХ
    // половинах — так их зовут и в макете, и в разговоре команды; переводится
    // только «Ретеншн-менеджер», у которого английского короткого имени нет.
    roleProjectOwner: 'Owner',
    roleProjectAdmin: 'Admin',
    roleProjectMember: 'Member',
    roleRm: 'Retention manager',
    roleOrgOwner: 'Owner',
    // WEB-4116: как у эталона me-k.jsx ROLE.
    roleOrgAdmin: 'Admin',
    roleOrgMember: 'Project member',

    // ── Таблица участников проекта ──────────────────────────────────────────
    // WEB-3756 юнит 5a: тексты — из макета (settings-k.jsx); параметры остаются, не используются.
    memberRoleChanged: (_name: string, _role: string) => 'Role changed',
    memberRoleChangeFailed: 'Could not update the role',
    memberRemoved: 'Member removed',
    memberRemoveFailed: 'Could not remove the member',
    ownerNowIs: (_name: string) => 'Ownership transferred',
    transferFailed: 'Could not transfer ownership',
    leftProject: 'You left the project',
    leaveFailed: 'Could not leave the project',
    colMember: 'Name',
    tagYou: 'you',
    colRole: 'Role',
    // WEB-1918: пометка рядом с ролью и пояснение к ней.
    roleOrgOwnerNote: 'organization owner',
    roleOrgOwnerHint:
      'The project role here is the direct one, but owner-level actions are available because this person owns the organization the project belongs to.',
    colJoined: 'Added',
    ownerRowTooltip: 'The owner cannot be changed on this row',
    leaveButton: 'Leave',
    makeOwner: 'Make owner',
    removeMemberConfirmTitle: (name: string) =>
      `Remove ${name} from the project?`,
    removeMemberConfirmBody: 'Access to the project is revoked at once.',
    transferConfirmTitle: (name: string) =>
      `Transfer project ownership: ${name}?`,
    transferConfirmBody: (_name: string) => 'You will become a project admin.',
    // WEB-3756 юнит «участники»: нажал владелец организации, не владелец проекта.
    transferConfirmBodyOther: (owner: string) =>
      `${owner} will become a project admin. Your role stays the same.`,
    transferConfirmBodyOtherUnknown:
      'The current owner will become a project admin. Your role stays the same.',
    leaveConfirmTitle: 'Leave the project?',
    leaveConfirmBody: 'You can come back only with a new invite.',
    // WEB-3756 юнит 5a
    colMemberEmail: 'Email',
    transferConfirmGo: 'Transfer',
    memberActions: 'Actions',
    membersLoadFailed: 'Could not load the members',
    // WEB-3756 юнит 5a, сверка вида круг 1: пункт меню и кнопка окна удаления.
    memberRemove: 'Remove',

    // ── Таблица приглашений ─────────────────────────────────────────────────
    inviteForever: 'No expiry',
    // WEB-4116 (сверка вида 31.10, мелочь 23): статус живого инвайта в таблице.
    inviteStatusActive: 'Active',
    inviteExpired: 'Expired',
    inviteUsedOf: (used: number, max: number) => `${used} of ${max}`,
    inviteNoLimit: 'no limit',
    inviteRevokedToast: 'Invitation revoked',
    inviteRevokeFailed: 'Could not revoke the invitation',
    inviteDeletedToast: 'Invitation deleted',
    inviteDeleteFailed: 'Could not delete the invitation',
    inviteResentToast: 'The email has been sent again',
    inviteResendFailed: 'Could not send the email. Try again later.',
    colInviteKind: 'Method',
    inviteKindLink: 'Link',
    colInviteUsed: 'Used',
    colInviteExpires: 'Valid until',
    colInviteStatus: 'Status',
    inviteStatusRevoked: 'Revoked',
    inviteDeliveryFailedTip:
      'The email did not reach the recipient. Send it again.',
    inviteStatusNotDelivered: 'Not delivered',
    inviteResend: 'Send again',
    inviteCopy: 'Copy',
    inviteRevoke: 'Revoke',

    // ── Окно приглашения в проект ───────────────────────────────────────────
    inviteTtl1d: '1 day',
    inviteTtl7d: '7 days',
    inviteTtl30d: '30 days',
    inviteLinkCreated: 'Invitation link created',
    inviteLinkCreateFailed: 'Could not create the invitation link',
    inviteLimitHint: (max: number, count: number) =>
      `Up to ${max} addresses at once, entered ${count}`,
    // WEB-3756 юнит 5b: текст макета; параметр лишних остаётся, не используется.
    inviteOverLimitError: (max: number, _excess: number) =>
      `More than ${max} addresses`,
    inviteInvalidEmails: (list: string) => `Invalid email addresses: ${list}`,
    inviteSentCount: (delivered: number) => `Sent ${delivered}`,
    inviteMailFailedPart: (list: string) =>
      `email not delivered: ${list} — you can send it again from the invitations table`,
    inviteSkippedPart: (list: string) =>
      `not sent: ${list} — details are in the invitations table`,
    inviteSendFailed: 'Could not send the invitations',
    inviteModalTitle: (project: string) => `Invite to the project “${project}”`,
    inviteFieldTtl: 'Valid for',
    inviteFieldMaxUses: 'Max uses',
    inviteCreateLink: 'Create a link',
    inviteFieldRecipients: 'To (several allowed)',
    inviteFieldRoleForAll: 'Role for everyone',
    inviteSend: 'Send',

    // ── Ключи API проекта ───────────────────────────────────────────────────
    // WEB-1198: подписи меток в таблице ключей — строчными, как в макете
    // («активен», «отозван», «тест»), и без внутреннего слова grace.
    apiKeyRevoked: 'revoked',
    apiKeyActive: 'active',
    apiKeyGraceUntil: (until: string) => `grace until ${until}`,
    apiKeyGraceTooltip:
      'The key has been rotated: it works until the end of the grace window, then it stops',
    apiKeyRevokeNow: 'Revoke now',
    apiKeyNameRequired: 'Enter a key name',
    apiKeyIssueFailed: 'Could not issue the key',
    apiKeyRotateFailed: 'Could not rotate the key',
    apiKeyRevokedToast: 'Key revoked',
    apiKeyRevokeFailed: 'Could not revoke the key',
    apiKeyCopied: 'Key copied',
    colName: 'Name',
    colPrefix: 'Key',
    apiKeyModeTest: 'test',
    colCreated: 'Created',
    colLastUsed: 'Last request',
    colActions: 'Actions',
    apiKeyRotate: 'Rotate',
    reloadOrTryLater: 'Reload the page or try again later.',
    apiKeyCreate: 'Create a key',
    apiKeyCreateTitle: 'Create an access key',
    apiKeyNamePlaceholder: 'Prod backend',
    apiKeyModeLabel: 'Mode',
    apiKeyModeLive: 'Live',
    apiKeyModeTestRadio: 'Test',
    apiKeyRevealTitle: 'New key',
    apiKeyRevealClose: 'I saved the key — close',
    apiKeyCopyAria: 'Copy the key',
    apiKeyRevokeConfirmTitle: 'Revoke the key now?',
    apiKeyRotateConfirmTitle: 'Rotate the key?',
    apiKeyRevokeConfirmOk: 'Yes, continue',
    apiKeyRotateConfirmOk: 'Yes, rotate',
    apiKeyRevokeConfirmBody:
      'The key stops working instantly. This cannot be undone. Do this if the key leaked — without waiting for the grace window to end.',
    apiKeyRotateConfirmBody:
      `We show the new key once. The old one works for ${KEY_GRACE_HOURS_EN} more.`,

    // ── Подпись входящих событий ────────────────────────────────────────────
    webhookSecretIssueFailed: 'Could not issue the secret',
    webhookSecretCopied: 'Secret copied',
    webhookSecretStatusLoadFailed: 'Could not load the secret status.',
    webhookSecretTitle: 'Incoming event signature',
    webhookSecretHint:
      'The secret for the HMAC signature of incoming server-to-server requests. The project ID is public; keep the secret on your server only.',
    webhookSecretConfigured: 'Secret connected',
    webhookSecretRotatedAt: (moment: string) => `Last rotation: ${moment}`,
    webhookSecretNotConfigured: 'Secret not set up',
    webhookSecretRotate: 'Rotate the secret',
    webhookSecretIssue: 'Issue a secret',
    webhookSecretReadOnlyNote:
      'Only the project owner or an administrator can issue or rotate the secret.',
    webhookSecretModalClose: 'I saved the secret — close',
    webhookSecretSaveNowTitle: 'Save the secret now',
    webhookSecretCopyAria: 'Copy the secret',

    // ── Постбэк S2S ─────────────────────────────────────────────────────────
    postbackSaved: 'Postback saved',
    postbackSaveFailed: 'Could not save the postback',
    // WEB-2408 п.Д2: повтор после таймаута остаётся (решение 2387-Р13).
    postbackHintTxid:
      'Add {txid}: it stays the same when we retry a postback after a timeout, so your network can drop the duplicate.',
    // WEB-2642: предупреждение под полем адреса (сохранить можно).
    postbackNoTxidWarning:
      'There is no {txid} in the URL. Without a transaction id your network cannot match a postback we resend after a timeout, so duplicates are possible. You can still save it.',
    postbackUrlInvalid: 'Enter a valid http/https URL',

    // ── Данные интеграции ───────────────────────────────────────────────────
    integrationDataTitle: 'Integration data',
    integrationDataHint:
      'Needed for server-to-server requests from your backend and for setting up the SDK (see the “Authentication” section of the docs). These values are public — the signing secret is issued below.',
    integrationApiBaseLabel: 'API base URL (FUNNEL_API_BASE)',
    integrationProjectIdLabel: 'Project ID (FUNNEL_PROJECT_ID)',

    // ── Льготный период ─────────────────────────────────────────────────────
    graceDays: (days: number) => `${days} ${days === 1 ? 'day' : 'days'}`,
    graceSaved: 'Grace period saved',
    graceSaveFailed: 'Could not save the grace period',
    graceTitle: 'Grace period after a failed payment',
    graceRbac: 'Only the project owner can change this setting',
    graceHint: (defaultDays: string) =>
      `How many days a customer keeps access to what they bought when the next subscription charge fails — the card is declined, there are not enough funds and so on. The default is ${defaultDays}: that is how long it lasts for projects where the field was never filled in.`,
    graceFieldLabel: 'Grace period, days',
    graceFieldExtra: (min: number, max: number, defaultDays: string) =>
      `A whole number from ${min} to ${max}. Leave the field empty to go back to the default (${defaultDays}).`,
    graceFieldError: (min: number, max: number) =>
      `A whole number from ${min} to ${max}, or empty`,
    graceFieldPlaceholder: (defaultDays: number) => `default (${defaultDays})`,

    // ── Пробный период ──────────────────────────────────────────────────────
    trialOnceOnToast: 'The trial is granted only once',
    trialOnceOffToast: 'A repeat trial is allowed',
    trialOnceSaveFailed: 'Could not save the setting',
    trialOnceTitle: 'Trial period',
    trialOnceSwitchLabel: 'Grant the trial only once',
    trialOnceHint:
      'A customer who already took a trial in this project pays right away when subscribing again. It protects you from endless free access through “subscribe — cancel — subscribe again”.',

    // ── Серверные возвраты ──────────────────────────────────────────────────
    s2sRefundOnToast: 'Server-side refunds turned on',
    s2sRefundOffToast: 'Server-side refunds turned off',
    s2sRefundToggleFailed: 'Could not change the refund setting',
    s2sRefundTitle: 'Server-side refunds',
    s2sOwnerAdminOnly: 'Only Owner and Admin can change this setting',
    s2sBillingAccessRequired:
      'Payment data is not available to you — the organization owner grants that access',
    s2sRefundWarningTitle:
      'Turning this on allows money to be refunded with the server key',
    s2sRefundWarningBefore:
      'While the switch is off, a refund request is rejected for every key. Once it is on, your server key (',
    s2sRefundWarningAfter:
      ') may refund customers and revoke the access they were granted. A leaked key means your revenue wiped out, so turning the switch off works as an emergency stop: the right is taken away from every key of the project at once. The refund right is separate from the charge right — allowing refunds does NOT allow charges, and the other way round.',
    s2sRefundSwitchLabel: 'Allow server-side refunds',
    // WEB-2002 (owner's wording, decision Р6 12.09.2026): the switch gates
    // the S2S API only — it has never gated the cabinet's own "Refund"
    // buttons, but the label used to read as if it did.
    s2sRefundCabinetUnaffectedHint:
      'Does not affect the “Refund” buttons in the dashboard — you can always refund from here.',

    // ── Subscription management via API ─────────────────────────────────────
    s2sSubscriptionManageOnToast: 'Subscription management via API turned on',
    s2sSubscriptionManageOffToast: 'Subscription management via API turned off',
    s2sSubscriptionManageToggleFailed:
      'Could not change the subscription management setting',
    s2sSubscriptionManageTitle: 'Subscription management via API',
    s2sSubscriptionManageWarningTitle:
      'Your server keys will be able to manage customer subscriptions',
    s2sSubscriptionManageWarningText:
      'This right covers cancelling a renewal, pausing collection, resuming, and issuing a link to the subscription management page. A leaked key could use it against a customer, so turning the switch off works as an emergency stop: the right is taken away from every key of the project at once.',
    s2sSubscriptionManageSwitchLabel:
      'Allow your server to manage customer subscriptions',

    // ── Серверные списания с сохранённых карт ───────────────────────────────
    moneyFractionMessage: 'No more than two decimal places',
    moneyZeroDecimalFraction:
      'Your price currency has no fractional part — enter a whole amount',
    countRangeMessage: (min: string, max: string) =>
      `A whole number from ${min} to ${max}, or empty`,
    moneyRangeMessage: (min: string, max: string) =>
      `An amount from ${min} to ${max}, or empty`,
    s2sAmountRangeHint: (min: string, max: string) =>
      ` Allowed range: ${min} to ${max}.`,
    moneyPrecisionHintZero: ' This currency has no fractional part.',
    moneyPrecisionHintTwo: ' No more than two decimal places.',
    s2sMaxAmountExtraCurrency: (
      currencyLabel: string,
      example: string,
      moneyExample: string,
      defaultMoney: string,
    ) =>
      `An amount in ${currencyLabel}, the currency of your prices. For example, ${example} = ${moneyExample}. Empty means the built-in conservative default (${defaultMoney}).`,
    s2sMaxAmountExtraNeutral:
      'An amount in the main units of your price currency, not in hundredths. Empty means the built-in conservative default (100 in your price currency).',
    s2sProjectDailyAmountExtraCurrency: (
      currencyLabel: string,
      defaultMoney: string,
    ) =>
      `All customers together, an amount in ${currencyLabel}. Empty means the default of ${defaultMoney} per day.`,
    s2sProjectDailyAmountExtraNeutral:
      'All customers together, an amount in the main units of your price currency. Empty means the default of 5 000 per day in your price currency.',
    s2sMixedCurrenciesNote:
      ' The project has prices in several currencies — the cap is shared across all of them and the amounts are added up as they are.',
    s2sChargeOnToast: 'Server-side charges turned on',
    s2sChargeOffToast: 'Server-side charges turned off',
    s2sChargeToggleFailed: 'Could not change the charge setting',
    s2sLimitsSaved: 'Charge limits saved',
    s2sLimitsSaveFailed: 'Could not save the limits',
    s2sChargeTitle: 'Server-side charges on saved cards',
    s2sChargeWarningTitle:
      'Turning this on allows charges on your customers’ saved cards',
    s2sChargeWarningBefore:
      'While the switch is off, a charge request is rejected for every key. Once it is on, your server key (',
    s2sChargeWarningAfter:
      ') may charge your customers’ saved cards without their involvement. A leaked key means charges you never made, so keep the limits below as strict as you can — and turning the switch off works as an emergency stop: the right is taken away from every key of the project at once.',
    s2sChargeSwitchLabel: 'Allow server-side charges',
    s2sMaxAmountLabel: 'Cap for a single operation',
    s2sDefaultPlaceholder: 'default',
    s2sDailyLimitLabel: 'Cap on successful charges per customer per day',
    s2sDailyLimitExtra: (min: string, max: string) =>
      `Applies to a single customer. Empty means the built-in conservative default. A whole number from ${min} to ${max} is allowed.`,
    s2sProjectDailyLimitLabel:
      'Daily cap on the number of charges for the whole project',
    s2sProjectDailyLimitExtra: (min: string, max: string) =>
      `All customers together. Empty means the default of 100 operations per day. A whole number from ${min} to ${max} is allowed.`,
    s2sProjectDailyLimitPlaceholder: 'default (100)',
    s2sProjectDailyAmountLabel:
      'Daily cap on the charged amount for the whole project',
    s2sDefaultPlaceholderWith: (value: string) => `default (${value})`,
    s2sSaveLimits: 'Save the limits',

    // ── Журнал серверных списаний ───────────────────────────────────────────
    chargeChargebackLabel: 'Taken back by the bank',
    chargeStatusSucceeded: 'Charged',
    chargeStatusRefunded: 'Refunded',
    chargeStatusPending: 'In progress',
    chargeStatusRequiresAction: '3DS required',
    chargeStatusFailed: 'Declined',
    chargeStatusRefunding: 'Refunding…',
    chargeNoPaymentToRefund: 'There was no charge — nothing to refund',
    chargeRefundFallback: 'Could not make the refund. Try again later.',
    chargeRefundConfirmTitle: 'Refund the charge in full?',
    chargeRefundOk: 'Refund the money',
    chargeRefundDone: 'Refund completed',
    colWhen: 'When',
    chargeOutcomeUnknown: 'Outcome unknown',
    serverChargesLoadFailed: 'Could not load the charges',

    // ── Строка провайдера (Stripe, пиксели) ─────────────────────────────────
    providerDisconnectOwnerOnly:
      'Only the project owner can disconnect a provider',
    providerDisconnectHint: 'Switch off to disconnect the provider',
    providerConnected: 'Connected',
    providerNotConnected: 'Not connected',
    pixelDisconnectHint:
      'The pixel can be disconnected by the project owner or an administrator',
    stripeSince: (key: string, date: string) => `${key} · since ${date}`,
    stripeModeSandbox: 'Sandbox',
    stripeModeLive: 'Live mode',
    stripePaymentsAria: 'Accepting payments',
    stripePaymentsOn: 'Accepting payments',
    stripePaymentsOff: 'Payments are off',
    stripeDisabledAlertTitle:
      'Payments are off — customers see an error on the paywall',
    stripeDisabledAlertText:
      'The keys and settings are saved. To accept payments again, switch payments back on in the Stripe row.',
    stripeSandboxAlertTitle:
      'The sandbox is active — there are no real charges',
    stripeSandboxAlertText:
      'Payments run on test keys (pretend cards). To take live payments, connect your live keys in the “Key management” block below.',

    // ── Выдача доступа в Adapty / RevenueCat ────────────────────────────────
    providerSyncSaved: 'Adapty/RevenueCat settings saved',
    providerSyncSaveFailed: 'Could not save — try again',
    providerSyncKeySet: (mask: string) => `set: ${mask}`,
    providerSyncKeyWillClear: 'will be cleared',
    providerSyncReplacePlaceholder: 'new key (replacement)',
    providerSyncKeep: 'Keep',
    providerSyncClear: 'Clear',
    providerSyncTitle: 'Adapty / RevenueCat: granting access after payment',
    providerSyncOn: 'on',
    providerSyncNotConfigured: 'not set up',
    providerSyncLoadFailed: 'Could not load the settings',
    providerSyncLoadFailedText:
      'Saving is disabled so the already saved values are not overwritten.',
    retry: 'Try again',
    providerSyncIntroBefore:
      'A purchase on the web paywall automatically grants access in your subscription platform (and revokes it on a refund). The app tells us its profile id through the',
    providerSyncIntroAfter:
      'parameter in the address of the embedded page — the details are in the developer docs.',
    providerSyncAdaptyStatus: 'Sync to Adapty: ',
    providerSyncRevenuecatStatus: 'Sync to RevenueCat: ',
    providerSyncStatusOn: 'on (keys connected)',
    providerSyncStatusOff: 'off (no keys set)',
    providerSyncAdaptyIosKey: 'Adapty — iOS app secret key',
    providerSyncAdaptyAndroidKey: 'Adapty — Android app secret key',
    providerSyncAdaptyLevel: 'Adapty — access level',
    providerSyncAdaptyLevelWarning:
      'The Adapty key is set but the access level is empty — access will not be granted after a purchase.',
    providerSyncRevenuecatKey: 'RevenueCat — secret API key',
    providerSyncRevenuecatEntitlement: 'RevenueCat — entitlement',
    providerSyncRevenuecatWarning:
      'The RevenueCat key is set but the entitlement is empty — access will not be granted after a purchase.',
    providerSyncTestMode: 'Test mode (sandbox) — optional',
    providerSyncSandboxConfigured: 'sandbox is set up',
    providerSyncTestIntro:
      'Separate keys for the test Adapty/RevenueCat apps let you check the integration before launch: a test purchase grants access in your sandbox, not in the live platform.',
    providerSyncTestIntro2:
      'Test purchases go to the sandbox only when the keys here are set — while the fields are empty nothing is synced anywhere (just as before). Live purchases always go through the live keys above.',
    providerSyncAdaptyIosTestKey: 'Adapty — iOS app test key',
    providerSyncAdaptyAndroidTestKey: 'Adapty — Android app test key',
    providerSyncRevenuecatTestKey: 'RevenueCat — test API key',
    providerSyncTestLevelNote:
      'The access level and the entitlement are the same as set above — create an access level with the same identifier in the test app.',
    providerSyncEncryptedNote:
      'Keys are stored encrypted and are never shown in full — only the last 4 characters.',

    // ── Подписи типов исходящих вебхуков (WEB-1536) ─────────────────────────
    whEntitlementGranted: 'Access granted',
    whEntitlementRenewed: 'Access renewed',
    whEntitlementRevoked: 'Access revoked',
    whPurchaseCompleted: 'Purchase paid',
    whRefundProcessed: 'Refund processed',
    whSubPaymentFailed: 'Subscription charge failed',
    whSubPaymentRecovered: 'Subscription charge went through after a failure',
    whChargebackOpened: 'The customer disputed the payment with their bank',
    whChargebackResolved: 'The payment dispute is settled',
    whSubPaused: 'Subscription paused (billing suspended)',
    whSubResumed: 'Subscription resumed',
    whSubPlanChanged: 'The customer switched plan',
    whQuizCompleted: 'A visitor finished the quiz',
    whUserPropertyUpdated: 'A customer property was created or updated',
    whUserPropertiesCompleted: 'All customer properties on quiz completion',
    whFunnelAnalyticsEvent: 'Funnel analytics (views, steps, answers)',
    whChargeCompleted: 'A charge on a saved card went through',
    whChargeFailed: 'A charge on a saved card was declined',
    whChargeRequiresAction: 'The charge needs confirmation from the cardholder',
    whLeadCaptured: 'A visitor left their email',

    // ── Подписи аналитических событий вебхука ───────────────────────────────
    whAnQuizView: 'Quiz view',
    whAnQuizStart: 'Quiz start',
    whAnScreenView: 'Screen view',
    whAnScreenAnswer: 'Answer on a screen',
    whAnQuizComplete: 'Quiz finished',
    whAnEmailSubmit: 'Email left',
    whAnPaywallView: 'Paywall view',
    whAnPriceClick: 'Price click',
    whAnCheckoutStart: 'Went to checkout',
    whAnTraffback: 'Traffic returned (traffback)',
    whAnPaymentUnavailable: 'Payment unavailable (payments are not connected)',
    whAnCycleGuardTripped: 'Screen transitions looping',

    // ── Исходящие вебхуки ───────────────────────────────────────────────────
    colEvent: 'Event',
    deliveryIncompleteTip:
      'The customer could not be identified, the event was sent, but the field for them is empty. This is a failure on our side, not on the receiver’s — there is nothing to look for at your end.',
    // WEB-2202 — причины «we decided not to send» (closed list, mirrored from
    // backend/src/webhooks/webhook-skip-reasons.ts).
    whSkipConsentDenied: 'visitor declined tracking',
    whSkipNotSubscribed: 'this event type is off for this receiver',
    whSkipBridgeTierExpired: 'the paid period has ended',
    whSkipDuplicateEvent: 'this event has already been queued',
    whSkipNotLiveTraffic: 'service traffic (speed check) — not sent',
    whSkipRateLimited: 'rate limit: over 300 events per minute per address',
    // WEB-2222 — «и что теперь». The reason alone does not say what to do:
    // «declined tracking» is a reason to do nothing, «the type is off» is a
    // reason to open the receiver settings.
    whSkipFixConsentDenied: 'nothing to do — this is how consent works',
    whSkipFixNotSubscribed:
      'turn it on in the receiver settings if you need it',
    whSkipFixBridgeTierExpired: 'events resume once the plan is paid',
    whSkipFixRateLimited:
      'events above the ceiling are dropped without retries',
    whSkipFixDuplicateEvent: 'nothing to do — the event is already on its way',
    whSkipFixNotLiveTraffic:
      'nothing to do — our own speed checks of the funnel are never sent to receivers',
    deliverySkipTimes: (count: number) => `×${count}`,
    deliverySkipTag: 'not sent',
    colOutcome: 'Outcome',
    delivFilterAll: 'All',
    delivFilterDelivered: 'Delivered',
    delivFilterSkipped: 'Not sent',
    delivFilterFailed: 'Failed',
    delivPeriodDay: '24 hours',
    delivPeriodWeek: '7 days',
    delivPeriodMonth: '30 days',
    deliveriesMore: 'Show more',
    deliveriesEmptyPeriod: 'No such deliveries within the period',
    // Ревизия 44 (В4): оба числа приходят снаружи — срок хранения
    // (`WEBHOOK_DELIVERY_DEFAULT_PERIOD_DAYS`) и число попыток
    // (`WEBHOOK_DELIVERY_MAX_ATTEMPTS`, у него сторож паритета с очередью
    // бэкенда). Литералы в словаре пережили бы смену настройки очереди молча.
    deliveriesKeepHint: (days: number, attempts: number) =>
      `Kept for ${days} ${plural('en', days, { one: 'day', other: 'days' })}. ` +
      `A failed delivery is retried ${attempts} ${plural('en', attempts, { one: 'time', other: 'times' })} within an hour.`,
    deliveriesLoadFailed: 'Could not load the delivery log',
    // WEB-2955: класс отказа `ssrf_blocked` (адрес получателя ведёт во
    // внутреннюю сеть, WEB-2731) — человеческая подпись вместо машинного кода.
    deliveryErrorSsrfBlocked: 'internal network address — not sent',
    webhookUrlRequired: 'Enter a URL',
    webhookEventRequired: 'Pick at least one event',
    webhookAnalyticsAllowlistRequired:
      'Pick at least one allowed event for “funnel analytics”',
    webhookCreateFailed: 'Could not create the endpoint',
    webhookUpdateFailed: 'Could not update it',
    webhookRotateFailed: 'Could not rotate the secret',
    webhookDeletedToast: 'Endpoint deleted',
    webhookDeleteFailed: 'Could not delete it',
    webhookEventsHint: 'Pick at least one.',
    // Заголовки групп списка событий (макет `AC_HOOK_GROUPS`).
    webhookEventGroupAccess: 'Access',
    webhookEventGroupMoney: 'Money',
    webhookEventGroupSubscription: 'Subscription',
    webhookEventGroupBehaviour: 'Behaviour',
    webhookAnalyticsOff: ' (off: no allowed events)',
    webhookAllProperties: ' (all properties)',
    webhookRotateSecret: 'Rotate the secret',
    webhookLoadFailed: 'Could not load the receivers',
    webhookAdd: 'Add a receiver',
    webhookEnable: 'Turn on',
    webhookDisable: 'Turn off',
    webhookLogOpen: 'Log',
    webhookLogClose: 'Hide the log',
    // WEB-2224 п.2: один модал на создание и на изменение — заголовок и
    // кнопка переключаются, поля те же.
    webhookCreateTitle: 'Add an event receiver',
    webhookEditTitle: 'Edit the event receiver',
    webhookEditAction: 'Edit',
    webhookUpdatedToast: 'Changes saved',
    webhookSecretStaysHint: `The signing secret stays the same. To replace it use “Rotate the secret” in the row menu: we will show the new one once. ${ROTATE_GRACE_EN}`,
    create: 'Create',
    webhookUrlLabel: 'Your server address',
    webhookVolumeWarningTitle: 'These events are very frequent.',
    webhookVolumeWarningText:
      'Your server gets one request per subscribed event per visitor; above 300 per minute per address, events are dropped without retries.',
    webhookSecretShownOnce:
      'We will show the signing secret once, right after creation.',
    webhookRevealTitle: 'Webhook signing secret',
    webhookRevealSaveText:
      'We will not show this secret again. You use it to verify the signature of the webhooks we send.',
    webhookDeleteConfirmTitle: 'Delete the receiver?',
    webhookRotateConfirmTitle: 'Rotate the receiver secret?',
    webhookDeleteConfirmOk: 'Yes, continue',
    webhookDeleteConfirmBody:
      'We stop sending events to this address. This cannot be undone.',
    webhookRotateConfirmBody: ROTATE_GRACE_EN,

    // ── Подключение приложения ──────────────────────────────────────────────
    mmpHintAppsflyer: 'AppsFlyer — expected *.onelink.me or app.appsflyer.com',
    mmpHintAdjust: 'Adjust — expected app.adjust.com, *.go.link or *.adj.st',
    bridgeSettingsSaved: 'Settings saved',
    saved: 'Saved',
    connectionMethodSaveFailed: 'Could not save the connection method',
    copied: 'Copied',
    copy: 'Copy',
    portalPaywallHint:
      'The subscription cancellation portal paywall — the subscription management link from the API and the email leads here. Build it from the “Subscription portal” blocks and publish it: an unpublished paywall cannot be selected, the link would lead to a page the customer cannot open.',
    portalNotAssigned: 'No portal assigned',
    portalUnpublishedOption: (name: string) => `${name} — not published`,
    portalUnpublishedWarning:
      'The selected paywall is not published — publish it or pick another one, otherwise the settings will not be saved.',
    portalUnavailableWarning:
      'The assigned portal paywall is unavailable — most likely it was deleted. Pick another one or clear the field, otherwise the settings of this tab will not be saved.',
    portalListFailed:
      'The paywall list did not load — you cannot pick a portal or check the assigned one right now. Reload the page.',
    mmpPairNoUrl:
      'Fill in the other half of the pair: the selected tracker has no link — the “Download” link for iPhone cannot be built, and a customer on iPhone will have to type their email.',
    mmpPairNoProvider:
      'Fill in the other half of the pair: the link has no tracker selected — it is not used, and the “Download” link for iPhone cannot be built.',
    mmpPairMismatch: (hint: string) =>
      `The link does not match the selected tracker. ${hint}`,
    mmpAdjustNoAdjT:
      'The link has no adj_t — Adjust will answer 404. Copy the full link from Campaign Lab.',
    // WEB-1930: цена выбирается из каталога проекта, а не печатается руками.
    priceLevelPricePlaceholder: 'Pick a price',
    // Ревизия пачки 24, находка 2: пустой список — три разных факта, и у
    // каждого свой текст (загрузка, отказ, действительно пустой каталог).
    priceLevelPriceMissing: (priceId: string) =>
      `${priceId} · not in the catalog`,
    priceLevelPriceInactive: 'inactive',
    // Ревизия пачки 24, находка 6: одна цена в двух парах схлопывается в карте.
    priceLevelPriceTaken: 'already assigned',
    // WEB-1225 (решение владельца А10 от 06.09): предупреждение прямо под
    // тумблером. Раньше о включённом тест-режиме говорил только серверный
    // журнал, куда владелец проекта не смотрит.
    // ── Журнал SDK ──────────────────────────────────────────────────────────
    sdkLogLevelDebug: 'Debug',
    sdkLogLevelInfo: 'Info',
    sdkLogLevelWarn: 'Warnings',
    sdkLogLevelError: 'Errors',
    colTimeUtc: 'Time (UTC)',
    colLevel: 'Level',
    colMessage: 'Message',
    colPlatform: 'Platform',
    colDeviceGuid: 'Device (guid)',
    sdkLogsLoadFailed: 'Could not load the SDK log',
    sdkLogsNothingMatches: 'Nothing matches the filter',
    sdkLogsNothingMatchesText:
      'Loosen the conditions or reset the filters to see the whole log.',
    resetFilters: 'Reset the filters',
    sdkLogsEmpty: 'No log entries yet',
    sdkLogsEmptyText:
      'The SDK starts sending steps after the app is first launched with the project connected.',
    sdkLogsAllLevels: 'All levels',
    sdkLogsEventPlaceholder: 'Event, e.g. identify.',
    sdkLogsGuidPlaceholder: 'device guid',
    sdkLogsContext: 'Context',
    sdkLogsDevice: 'Device',
    sdkLogsClientClock: (moment: string) =>
      `Device clock: ${moment} (may drift from the server time)`,

    // WEB-1198 — подвкладка «Логи SDK»; с WEB-3757 юнит 6 — на ките по эталону
    // settings-app.jsx, новые надписи — блоком «WEB-3757 юнит 6» ниже.
    acLogsModeDevices: 'By device',
    // Худший уровень сессии устройства — подсказка точки в её шапке.
    acLogsWorstError: 'has errors',
    acLogsWorstWarn: 'has warnings',
    acLogsWorstOk: 'no errors',
    acLogsSessionEvents: (n: number) =>
      `${n} ${plural('en', n, { one: 'event', other: 'events' })}`,
    acLogsLoadFailed: 'Could not load the logs.',

    // ── Ящик «Как подключить приложение» ────────────────────────────────────
    sdkDrawerTitle: 'How to connect the app',
    sdkDrawerIntroBefore:
      'Our SDK links your web funnel with the app: a person goes through the funnel, installs the app — and it immediately knows',
    sdkDrawerIntroWho: 'who this is',
    sdkDrawerIntroAnd: 'and',
    sdkDrawerIntroWhat: 'what they paid for',
    sdkDrawerIntroAfter:
      ', so it can open the paid content. We do the “funnel → install” matching ourselves.',
    sdkDrawerProjectId: 'Your Project ID',
    sdkDrawerPasteOnInit: 'Paste it when initializing the SDK',
    sdkDrawerForProject: (name: string) => ` — project “${name}”`,
    sdkDrawerNoSdkBefore:
      'Integrating through a tracker only (AppsFlyer/Adjust), without our SDK? The token arrives in the deeplink: AppsFlyer —',
    sdkDrawerNoSdkMiddle: '(Unified Deep Linking), Adjust —',
    sdkDrawerNoSdkAfter:
      '(parse the query of the incoming deferred deeplink). Parameters and code samples are in the full documentation below.',
    sdkDrawerInstall: (ios: string, android: string) =>
      `Installing the SDK (iOS ${ios} / Android ${android}), initialization, funnel events, the built-in paywall and quiz display — in the full developer documentation:`,
    sdkDrawerFullDocs: 'Full developer documentation →',

    // ── Карточки метода подключения ─────────────────────────────────────────
    methodFullBridgeTitle: 'Our SDK',
    methodFullBridgeText:
      'Our SDK + email sign-in + (optionally) ads. Seamless.',
    methodApiOnlyTitle: 'Your own API integration',
    methodApiOnlyText:
      'The access right over the API — the integration is on your side.',
    methodTestTitle: 'Demo / check',
    methodTestText: 'Fake access for checking.',
    methodWebOnlyText: 'It works — set it up on the “For developers” tab.',
    methodGroupAria: 'App connection method',
    methodTitle: 'How the app learns about a payment',
    methodSubtitle: 'It decides how the app finds out that a person has paid.',
    methodRecommended: 'Recommended',
    methodSoon: 'Coming soon',

    // ── Чужой домен веб-пейвола ─────────────────────────────────────────────
    webPaywallForeignWarning:
      'The address is not on a domain of this project — check that this is deliberate. You can still save it: that is how it should be for tracking domains and redirectors.',

    // ── Организация: общие настройки и закрытие ─────────────────────────────
    orgRenamed: 'The organization name has been updated',
    orgRenameFailed: 'Could not update the organization name',
    orgDeletedToast:
      'The organization has been deleted together with its projects',
    orgDeleteFailed: 'Could not delete the organization',
    projectsCount: (count: number) =>
      `${count} ${count === 1 ? 'project' : 'projects'}`,
    orgRenameOwnerOnly: 'Only the organization owner can rename it',
    orgDeleteWithProjects: (projects: string) =>
      `The organization will be deleted permanently together with ${projects} — their funnels, publications and settings.`,
    orgDeleteNoProjects:
      'The organization will be deleted permanently together with its billing and domains.',
    // ── Правило закрытия организации: ОДИН текст на два экрана ─────────────
    // Слова живут на сервере (backend/src/organizations/constants/org-closure.ts,
    // ORG_CLOSURE_PAYMENT_HISTORY_RULE) — оттуда они попадают в текст отказа.
    // Кабинет обязан повторять их дословно НА ОБОИХ ЯЗЫКАХ: расхождение держит
    // сторож api/__tests__/orgClosureTexts.test.ts, читающий серверный исходник.
    orgClosurePaymentHistoryRule:
      'An organization whose projects have taken buyer payments can be closed a year after the last payment.',
    orgPreviewFailedTitle: 'Could not count what will disappear',
    orgPreviewFailedText:
      'While the list has not loaded, deleting blind is not allowed. Close the window and try again.',
    orgDeleteBlockersTitle: 'Deleting is not possible yet',

    // ── Организация: администраторы и передача владения ─────────────────────
    adminAdded: (email: string) => `Administrator added: ${email}`,
    adminAddFailed: 'Could not assign the administrator (check the email)',
    adminInviteTitle: (org: string) => `Invite an administrator to “${org}”`,
    adminAssign: 'Make administrator',
    adminEmailLabel: 'User email',
    adminEmailHelp: 'The user has to be registered in Subster.ai',
    adminFullAccessNote:
      'An organization admin gets full access to billing, domains and every project.',
    adminBillingAccessLabel: 'Access to payment data',
    adminBillingAccessOn:
      'Sees and changes payment keys, refunds, subscription cancellations',
    adminBillingAccessOff:
      'Sees the state of the payment connection, but not the keys, and cannot change anything',
    orgTransferTitle: 'Transfer organization ownership',
    orgTransferDone: 'Organization ownership transferred',
    orgTransferNoticeTitle: 'After the transfer you become an administrator',
    orgTransferNoticeText:
      'The new owner gets the exclusive rights: deleting the organization, assigning administrators and transferring ownership.',
    orgTransferNoAdminsTitle: 'No eligible recipients',
    orgTransferNoAdminsText:
      'Assign at least one administrator first — only administrators can become the new owner.',
    orgTransferNewOwner: 'New owner',
    orgTransferPickAdmin: 'Pick an administrator',

    // ── Создание рабочего пространства ──────────────────────────────────────
    orgCreatedSwitchFailed: (name: string) =>
      `The organization “${name}” was created, but we could not switch to it — pick it manually in the organization list.`,
    orgCreated: (name: string) => `The organization “${name}” has been created`,
    orgCreateFailed: 'Could not create the organization',
    orgCreateTitle: 'Create a workspace',
    orgCreateNameRequired: 'Enter a name',
    orgCreateNameLabel: 'Name',
    orgCreateNamePlaceholder: 'My team',
    orgCreateNoticeTitle: 'You become the owner of the new workspace',
    // WEB-3796: бесплатного тарифа больше нет — строка по эталону (`ws.ownD`).
    orgCreateNoticeText:
      'Projects, team, plan and balance are separate per workspace.',
    orgGateTitle: 'Create a workspace',
    orgGateNameRequired: 'Enter the organization name',
    orgGateNameTooLong: 'The name must not exceed 255 characters',
    orgGateNamePlaceholder: 'Company name',
    orgGateSubmit: 'Create a workspace',
    // WEB-1314: первая загрузка списка организаций не удалась — это НЕ «у вас
    // их нет». Отдельный экран вместо окна создания.
    // WEB-4091: тексты экрана — по эталону рескина (app.jsx:185-186).
    // WEB-4117: тексты экрана — эталона `ws-fail` (app.jsx `failT`/`failD`/`failS`).
    orgBootstrapFailedTitle: 'Could not load',
    orgBootstrapFailedHint: 'Your data is safe — try again.',
    orgBootstrapFailedSupport: 'Still failing? Contact support.',
    orgBootstrapRetry: 'Try again',
    orgBootstrapLogout: 'Log out',

    // ── Переключатели организации и проекта ─────────────────────────────────
    /**
     * WEB-4117 (решение владельца 28.10 «текст отказа на пределе — короткий, по
     * эталону», `orgMaxS` app.jsx:178): только счёт и «предел»; полное правило —
     * отказу сервера (`serverErrorsStrings.orgLimitReached`).
     * WEB-4091 (решение владельца 30.10): max — настоящий предел
     * MAX_ORGS_PER_USER. Без двоеточия, как в эталоне (решение оркестратора
     * 31.10 при слиянии WEB-4117).
     */
    orgLimitReason: (owned: number, max: number) =>
      `Organizations ${owned} of ${max}. Limit reached.`,
    workspaceSwitched: 'Workspace switched',
    /**
     * WEB-1368 п.1.10: запасной локальный путь сработал, а выбор на сервере
     * не сохранился — раньше об этом молчали и показывали зелёный успех.
     */
    workspaceSwitchedLocally:
      'Workspace switched, but the choice was not saved: it will apply until you reload the page',
    workspaceSwitchFailed: 'Could not switch the workspace',
    createWorkspace: 'Create a workspace',
    projectSwitchFailed: 'Could not switch the project',
    createProject: 'Create project',
    allProjects: 'All projects',

    // ── Команда проекта в организации ───────────────────────────────────────
    projectTeamLoadFailed: 'Could not load the organization’s project list',
    projectTeamLoadFailedText: 'Check the connection and try again.',
    // WEB-3758 организация 2: «Команды проектов» и «Команда организации» на ките
    // (эталон design/cabinet-rescin/sections/me-k.jsx, стр. 125–129)
    projectTeamsTitle: 'Project teams',
    projectRowLabel: 'Project',
    projectMembersOpen: 'Project members',
    projectTeamNoProjects: 'The organization has no projects yet',
    orgAssignAdminButton: 'Add an admin',
    orgColPaymentData: 'Payment data',
    orgBillingOwnerAlways: 'The owner always has access',

    // ── Команда организации ─────────────────────────────────────────────────
    orgMembersLoadFailed: 'Could not load the member list',
    orgAdminDemoted: 'Administrator rights revoked',
    orgRoleChangeFailed: 'Could not change the member’s role',
    orgMemberRemoved: 'The member has been removed from the organization',
    orgMemberRemoveFailed: 'Could not remove the member from the organization',
    orgBillingAccessChangeFailed: 'Could not change access to the payment data',
    orgLeft: 'You have left the organization',
    orgLeaveFailed: 'Could not leave the organization',
    colPayments: 'Payments',
    orgOwnerImmutable: 'The organization owner cannot be changed',
    orgDemote: 'Revoke the role',
    orgTeamTitle: 'Organization team',
    roleOrgMemberShort: 'Member',
    orgDemoteConfirmTitle: (email: string) =>
      `Revoke the administrator role from ${email}?`,
    orgDemoteConfirmBody:
      'The user loses access to billing, domains and the management of the organization’s projects. Membership in the projects stays.',
    orgRemoveConfirmTitle: (email: string) =>
      `Remove ${email} from the organization?`,
    orgRemoveConfirmBody:
      'The user loses access to every project of the organization — they are removed from the organization team and from all of its projects. This action cannot be undone on your own.',
    orgLeaveNote: 'You will lose access to every project of this organization.',
    orgLeaveOwnerTip: 'Transfer the ownership first to leave the organization',
    orgLeaveButton: 'Leave the organization',
    orgLeaveConfirmTitle: 'Leave the organization?',
    orgLeaveConfirmBody:
      'This action cannot be undone on your own — a new invitation will be needed.',

    // ── Профиль контекста проекта ───────────────────────────────────────────
    ctxColorPrimary: 'Primary',
    ctxColorBg: 'Background',
    ctxColorAccent: 'Accent',
    ctxOriginAuto: 'auto',
    ctxOriginAi: 'AI',
    ctxOriginManual: 'manual',
    ctxStoreDrift: (value: string) =>
      `Updated in the store: ${value} — apply it through editing or keep your own value.`,
    ctxBrandColors: 'Brand colors',
    ctxScreenshots: 'Store screenshots',
    ctxScreenshotsCount: (count: number) => `Store screenshots (${count})`,
    ctxScreenshotAlt: (index: number) => `Screenshot ${index}`,
    ctxLoadFailed: 'Could not load the context profile.',
    ctxUrlInvalid:
      'A link like https://apps.apple.com/… or to the app website is needed',
    ctxProfileUpdated: (cost: string) => `Profile updated${cost}`,
    ctxReanalyzeFailed: 'Could not update the analysis. Try again later.',
    ctxSaved: 'Context saved',
    ctxSaveFailed: 'Could not save the changes.',
    ctxTabIdentity: 'Profile',
    ctxTabBranding: 'Branding',
    ctxTabAudience: 'Audience',
    ctxTabFeatures: 'Features',
    ctxTabReviews: 'Reviews',
    ctxTabMedia: 'Media',
    ctxAppName: 'App name',
    ctxCategory: 'Category',
    ctxTagline: 'One-line pitch',
    ctxDescription: 'Description',
    ctxWebsite: 'Website',
    ctxPlatforms: 'Platforms',
    ctxFont: 'Font',
    ctxLogoUrl: 'Logo link',
    ctxColorPrimaryPlaceholder: 'primary',
    ctxColorBgPlaceholder: 'background',
    ctxColorAccentPlaceholder: 'accent',
    ctxPersona: 'Audience persona',
    ctxGeoLanguage: 'Geo / Language',
    ctxPains: 'Pains and motivation',
    ctxKeyFeatures: 'Key features',
    ctxUsp: 'What sets it apart from competitors',
    ctxMonetization: 'Monetization model',
    ctxRating: 'Rating',
    ctxReviewCount: 'Number of reviews',
    ctxReviewSamples: 'Sample reviews',
    ctxBestQuotes: 'Best quotes',
    ctxScreenshotsField: 'Screenshots',
    ctxPromoVideo: 'Promo video',
    ctxNotUsedByAi: 'AI does not use this yet',
    // WEB-4116: подпись над списком подразделов — как у эталона Ctx.
    ctxCompleteness: (pct: number) => `${pct}% complete`,
    ctxEditRbac: 'Owner and Admin can edit the context',
    ctxEdit: 'Edit',
    // WEB-2385 п.2: подсказка кнопок разбора — по ПРАВУ, а не по роли:
    // участник команды его имеет и разбор ему открыт.
    ctxAnalyzeRbac: 'Requires the permission to edit funnels',
    ctxReanalyzePaid: (cost: string) =>
      `Updating the analysis is paid${cost}: it is charged on every run`,
    ctxReanalyze: 'Update the analysis',
    ctxAnalyzeRunning:
      'The app analysis by link is running — the profile will show up here when it finishes.',
    ctxAnalyzeFailed: (reason: string) => `The analysis did not run: ${reason}`,
    ctxAnalyzeFailedText:
      'You can fill the context profile in by hand or start the analysis again.',
    ctxEmpty:
      'There is no context profile yet. Start the analysis to fill it in automatically, or fill it in by hand.',
    ctxStartAnalysis: 'Start the analysis',
    // WEB-2465 п.1: действие под заглушкой пустого профиля — ручное заполнение.
    ctxFillManually: 'Fill in manually',
    ctxProTitle: 'The context profile is a Pro feature',
    // WEB-3756 доводка 1: замок «Контекста» без тарифа — только заголовок
    // (`ctxProText` снят парой) и кнопка «Оплатить тариф» (решение владельца
    // 27.10 № 8; без названий тарифов — п. 75).
    ctxPayPlan: 'Pay for a plan',
    ctxReanalyzeTitle: 'Update the store page analysis?',
    ctxReanalyzeOk: 'Update',
    ctxReanalyzeNote: (cost: string) =>
      `Re-scanning${cost}. The “auto” fields will be overwritten, manual edits will stay.`,
    ctxReanalyzeUrlLabel: 'Link to the app in the store or to the website',
    ctxCostTag: (cost: number) => `≈ ${cost} Cr`,
    ctxCostSuffix: (cost: number) => ` (−${cost} Cr)`,
    ctxCostDash: (cost: number) => ` — ≈ ${cost} Cr`,
    // WEB-3756 юнит 15: «Контекст» на ките — число заполненных полей раздела
    // пояснением пункта; процент заполненности — ctxCompleteness (WEB-4116,
    // ctxPct снят).
    ctxSectionFilled: (filled: number, total: number) =>
      `${filled} of ${total} filled`,

    // ── Мастер создания проекта ─────────────────────────────────────────────
    wizardSourceWebsite: 'Website',
    wizardSourceNone: 'I am not connecting an app',
    wizardStepAbout: 'About the app',
    wizardStepAnalysis: 'Analysis',
    wizardStepConfirm: 'Confirm',
    wizardPreviewUnparseable:
      'Could not parse the app page by that link. Check the link.',
    wizardAnalysisFailed:
      'Could not run the analysis. Check the link and try again.',
    wizardNameRequired: 'Enter the project name',
    wizardUrlRequired: 'Enter a valid link',
    wizardCreateFailed: 'Could not create the project. Try again.',
    wizardNameLabel: 'Project name',
    wizardSourceLabel: 'The app we will connect to the quizzes',
    wizardUrlLabel: 'Link to the app',
    wizardUrlHint:
      'We fill the brand context in automatically from that link (name, category, description, media).',
    wizardAnalysisPaidWithCost: (cost: number) =>
      `The analysis is paid: about ${cost} cr. will be charged to the balance.`,
    wizardAnalysisPaid:
      'The analysis is paid: it is charged to the credit balance.',
    wizardContinue: 'Continue',
    wizardContinueNoAnalysis: 'Continue without the analysis',
    wizardAnalyzing:
      'Analyzing the store page: name, category, description, screenshots…',
    wizardSummaryNoApp: 'No app connected — fill the context in later',
    wizardSummaryWillFill: (source: string) =>
      `${source} · the context will be filled in automatically after creation`,
    // WEB-3760 юнит 2б: мало кредитов на разбор, отказ предпросмотра, сводка и
    // итог создания (эталон рескина design/cabinet-rescin/sections/org.jsx).
    wizardNoBalanceTitle: (cost: number, have: number) =>
      `Not enough credits for the analysis: it needs about ${cost} cr., the balance has ${have} cr.`,
    wizardNoBalanceText:
      'Top up the balance or create the project without the analysis — fill the context in later on the “Context” tab.',
    wizardTopUp: 'Top up',
    wizardSummaryNoBalance: (source: string) =>
      `${source} · without the analysis: not enough credits`,
    wizardSummaryFailed: (source: string) =>
      `${source} · without the analysis: the page was not parsed`,
    wizardSumName: 'Name',
    wizardSumSource: 'App',
    wizardSumMoney: 'Payments',
    wizardCreated: 'Project created',
    wizardCreate: 'Create the project',
    wizardTitle: 'New project',
    wizardOrgLimit: (max: string) => `The organization project limit — ${max}`,
    wizardLimitText: 'Delete an unused project or upgrade the plan.',
    wizardUpgrade: 'Upgrade the plan',
    wizardCategorySuffix: (category: string) => ` · ${category}`,
    // WEB-2126: второй вопрос мастера — про деньги, и ни у одного из двух нет
    // умолчания (продукт больше не решает за человека, что приложение есть).
    wizardMoneyLabel: 'How do you take payments?',
    wizardMoneyOurs: 'Through us',
    wizardMoneyOwn: 'On my own',
    wizardSourceRequired: 'Choose whether we are connecting an app',
    wizardMoneyRequired: 'Choose how you take payments',
    wizardOwnAllTitle: 'The cabinet will adjust to you',
    wizardOwnAllText:
      'We will not show the money sections and the money funnel steps — instead of endless zeros you keep what we really measure: views, answers, leads and the hand-off to your payment page.',
    // WEB-2234: «платежи через нас» + «приложение не подключаю» — законное
    // сочетание ровно для одного случая (доступ к своему веб-продукту клиент
    // выдаёт сам), но покупателя после оплаты надо куда-то вести.
    wizardPayUsNoAppTitle: 'A buyer has to be taken somewhere after paying',
    wizardPayUsNoAppText:
      'You are not connecting an app, so you grant access yourself: the purchase event will arrive at your server. Where to send the person after payment is set in the paywall.',

    ctxAnalyzeFailedFallback:
      'Could not parse the app by that link. Try running the analysis again.',

    // ── Письма-последовательности (интерфейс редактора; сами письма — на
    //    языке ВОРОНКИ и не переводятся) ─────────────────────────────────────
    seqTriggerLeadRegistration: 'Lead registration',
    seqTriggerAnyPurchase: 'Any purchase',
    seqTriggerPurchase: 'Purchase',
    seqTriggerSubscribe: 'Subscription',
    seqTriggerStartTrial: 'Trial started',
    seqTriggerAppInstalled: 'App installed',
    seqClassMarketing: 'Marketing email (not sent to people who unsubscribed)',
    seqClassTransactional:
      'Transactional email (always sent — access to the purchase, a receipt)',
    seqTransactionalWarning:
      'The trigger of this sequence is not tied to a purchase, and a transactional email bypasses the unsubscribe list. Use this class only for emails about what the person paid for — access, a download link, a receipt.',
    // WEB-2717: галочка «слать после покупки» и причины остановки цепочки.
    seqContinueAfterPurchaseLabel: 'Keep sending after a purchase',
    seqContinueAfterPurchaseHelp:
      'Off: if the recipient buys anything in this project (a one-time purchase or a subscription) after the sequence starts, the remaining emails are not sent. Turn on if buyers should get the whole sequence too.',
    seqContinueAfterPurchaseNotApplicable:
      'Does not apply to this sequence: a purchase never stops transactional emails or sequences started by a purchase.',
    seqContinueAfterPurchaseSaved: 'Setting saved',
    seqSkipStoppedInactive: 'Not sent: the sequence was turned off',
    seqSkipStoppedPurchase: 'Not sent: the recipient has already bought',
    seqCreated: 'Sequence created',
    seqCreateFailed: 'Could not create the sequence',
    seqActivated: 'Sequence activated',
    seqDeactivated: 'Sequence deactivated',
    seqUpdateFailed: 'Could not update the sequence',
    seqDeleted: 'Sequence deleted',
    seqDeleteFailed: 'Could not delete the sequence',
    seqColTrigger: 'Trigger',
    seqTriggerUnsupported:
      'the trigger is not supported — the sequence will not run',
    seqColActive: 'Active',
    seqColSteps: 'Steps',
    seqEditAria: 'Edit the sequence',
    seqDeleteAria: 'delete',
    seqPageTitle: 'Email sequences',
    seqCreate: 'Create a sequence',
    seqEmpty: 'No email sequences yet',
    seqCreateFirst: 'Create the first sequence',
    seqCreateTitle: 'Create an email sequence',
    seqNameLabel: 'Sequence name',
    seqNameRequired: 'Enter a name',
    seqTriggerRequired: 'Pick a trigger',
    seqTriggerPlaceholder: 'Pick a trigger',
    // WEB-2126: проект, который принимает деньги сам, покупок нам не шлёт.
    seqTriggersOwnMoneyNote:
      'Purchase triggers are not on the list: the project settings say you ' +
      'take payments yourself, so we never learn about a purchase. ' +
      'Change it in the project payment settings.',
    seqClassLabel: 'Email class',
    seqDeleteConfirmTitle: 'Delete this sequence?',
    seqDeleteConfirmBody:
      'Every step and log will be deleted. This cannot be undone.',
    seqClassSaved: 'Email class saved',
    seqLoadFailed: 'Could not load the sequence',
    seqBackToList: 'Back to the sequences',
    seqNotFound: 'Sequence not found',
    seqNotFoundText: 'Could not load this email sequence',
    seqTabSteps: 'Steps',
    seqTabLogs: 'Send history',
    seqBackAria: 'Back to the sequences',
    seqOn: 'On',
    seqOff: 'Off',
    seqTestSendAria: 'Send a test email',
    seqTestSend: 'Send a test',
    seqLogFilterAll: 'All',
    seqLogSent: 'Sent',
    seqLogFailed: 'Failed',
    seqLogSkipped: 'Skipped',
    seqColRecipient: 'Recipient',
    seqColSubject: 'Subject',
    seqUnknownStep: 'Unknown step',
    seqColFailReason: 'Failure reason',
    seqStatusPlaceholder: 'Status',
    seqRefreshLogsAria: 'Refresh the logs',
    seqRefresh: 'Refresh',
    seqLogsEmpty: 'No logs yet',
    seqLogsFailed: 'Could not load the send log',
    seqRetry: 'Retry',
    seqStepUpdated: 'Step updated',
    seqStepAdded: 'Step added',
    seqStepSaveFailed: 'Could not save the step',
    seqSaveStepAria: 'Save the step',
    seqEditStepTitle: 'Edit the step',
    seqAddStepTitle: 'Add a step',
    seqSubjectLabel: 'Subject',
    seqSubjectRequired: 'Enter a subject',
    seqSubjectPlaceholder: 'Email subject',
    // WEB-2311: сервер считает задержку каждого шага от события, запустившего
    // цепочку (все шаги встают в очередь разом), а не от предыдущего письма.
    seqDelayLabel: 'Delay (hours after the event)',
    seqDelayHelp:
      'Counted from the event that started the sequence (for example, the visitor left their email or made a purchase), not from the previous email. To send one email a day, set 24, 48, 72.',
    seqBodyLabel: 'Email body (HTML)',
    seqHidePreviewAria: 'Hide the preview',
    seqShowPreviewAria: 'Show the preview',
    seqEditMode: 'Edit',
    seqPreviewMode: 'Preview',
    seqBodyRequired: 'Enter the email body',
    seqBodyNotEmpty: 'The email body must not be empty',
    seqBodyPlaceholder: '<p>The email HTML…</p>',
    seqPreviewTitle: 'Email preview',
    seqStepDeleted: 'Step deleted',
    seqStepDeleteFailed: 'Could not delete the step',
    seqStepsEmpty: 'No steps yet',
    seqAddStepAria: 'Add a step',
    seqAddStep: 'Add a step',
    seqEditStepAria: 'Edit the step',
    seqEditStep: 'Edit',
    seqDeleteStepAria: 'Delete the step',
    seqAfterHours: (hours: number) => `After ${hours}h`,
    seqDeleteStepConfirmTitle: 'Delete this step?',
    seqDeleteStepConfirmBody: 'This action cannot be undone.',
    seqTestSent: 'The test email has been sent',
    seqTestSendFailed: 'Could not send the test email',
    seqTestSendTitle: 'Send a test email',
    seqRecipientLabel: 'Recipient email',
    seqEmailRequired: 'Enter an email address',
    seqEmailInvalid: 'Enter a valid email address',
    seqListLoadFailed: 'Could not load the email sequences',
    seqStepAddFailed: 'Could not add the step',
    seqLogsLoadFailed: 'Could not load the send history',

    // ── Пиксели и Conversions API ───────────────────────────────────────────
    capiTokenLabelMeta: 'Access token (CAPI)',
    capiTokenLabelTiktok: 'Access token (Events API)',
    capiOpenHint: 'Enter the details and press “Connect”',
    capiEventPurchase: 'Payment',
    capiEventStartCheckout: 'Payment started',
    capiEventProgress90: 'Reached 90% of the quiz',
    capiEventLead: 'Left an email',
    // WEB-2272 §9: the row no longer drives any funnel event — it names the
    // TEST event, and it is now labelled as what it actually does.
    capiEventView: 'Test event',
    // WEB-2413 п.12 — подписи остальных наших событий справочника сервера
    // (`GET /capi/event-catalog`); таблица кодов — capiEventLabels.ts.
    capiOurEventQuizStart: 'Started the quiz',
    capiOurEventQuizProgress50: 'Reached 50% of the quiz',
    capiOurEventQuizComplete: 'Finished the quiz',
    capiOurEventPaywallView: 'Opened the pricing screen',
    capiOurEventPaywallHandoff: 'Went to checkout on your site',
    capiOurEventTrialStart: 'Started a trial',
    capiOurEventRepeatPurchase: 'Renewal, upgrade or in-app purchase',
    capiOurEventTrialConverted: 'Paid after the trial',
    capiOurEventRefund: 'Refund or lost dispute',
    capiOurEventAppInstalled: 'Installed the app',
    capiOurEventRegistration: 'Signed up in the app',
    capiStatusLoadFailed: 'Could not load the connection status',
    // Ревизия пачки 38 (К1), то же правило, что WEB-2006 у Stripe: the status
    // request failed and we have never had a confirmed answer — a dedicated
    // "unknown" state, not the honest "not connected" and not the connect form.
    capiStatusUnknownTitle: 'Could not check the connection',
    capiStatusUnknownText:
      "The server did not respond — this doesn't mean the pixel is disconnected.",
    capiDisconnectTitle: 'Disconnect the provider?',
    capiDisconnectBody:
      'Conversion events will stop being sent to this network.',
    capiDisconnectOk: 'Disconnect',
    capiDisconnected: (network: string) => `${network} disconnected`,
    capiDisconnectFailed: 'Failed to disconnect',
    capiNotConnectedReadonly: (network: string) =>
      `${network} is not connected. Ask the project owner or an administrator to set it up.`,
    capiConnected: (network: string) => `${network} connected`,
    // WEB-3757 (эталон спек-репо 8acd1074 settings-k.jsx NetView): режим одним
    // словом, пояснение — в «?» (capiAdvPiiHint / capiAdvPiiOff); «Подключено»
    // без двоеточия.
    capiPiiTitle: 'Personal data',
    capiPiiSuppressed: 'Suppressed',
    capiPiiNotSuppressed: 'Standard',
    capiEventMapping: 'Event names',
    capiConnectedAt: 'Connected',
    capiEditSettings: 'Change the settings',
    capiAdvancedTitle: (network: string) => `Advanced \u00b7 ${network}`,
    // WEB-3756 сверка пикселей 1, М10 — подпись эталона settings-k.jsx Net.
    capiAdvPiiSwitch: 'Hashed email and phone',
    // WEB-1364 п.3 — см. русскую половину: текст приведён к факту.
    capiAdvPiiHint:
      'Email, phone and external_id are sent hashed (SHA-256); IP address and browser string go unhashed — Meta and TikTok require them raw.',
    // WEB-2413 п.14 (решение владельца 26.09.2026): при выключенном тумблере
    // уходят хеш идентификатора посетителя, метки клика, идентификаторы браузера сети и адрес страницы — см. русскую половину.
    capiAdvPiiOff:
      'Email, phone, IP address and browser string are not sent. What goes out: a hash of the visitor identifier (external_id, SHA-256), ad click IDs (fbc, ttclid), the ad network’s browser IDs (fbp, ttp) and the page URL — the event is tied to the ad click, but the network does not get the buyer’s email, phone or IP address',
    capiAdvMappingHint: (network: string) =>
      `Our event → the ${network} event. The defaults are already set — reassign them if you need to.`,
    capiConnectFailed: 'Connection failed',
    /**
     * WEB-2272: the drawer's own “Save” now performs the SAME save as the
     * form, so both the “not saved by themselves” banner and the “will apply
     * later” toast are gone — they described a two-step save that no longer
     * exists (and, after WEB-2235, claimed an access token was needed when it
     * was not).
     */
    capiAdvancedNameSwitchNote:
      'The “Reached 90% of the quiz” row now sets the name of that event itself. Before this update the name came from the “Test event” row, so check that the value here is the one your campaign optimises for.',
    // WEB-2491 кабинетная часть (К6б): строки-пары «наше событие → имя в
    // сети» на серверном справочнике (`CapiEventPairsSection.tsx`).
    capiPairsLoading: 'Loading the event list…',
    capiPairsLoadFailed:
      'Could not load the event list. Saved names are shown as they are and will not change.',
    capiPairAdd: '+ Add a pair',
    capiPairAddPlaceholder: 'Choose our event',
    capiPairRemove: 'Remove',
    capiPairOutsideNote: 'saved earlier; cannot be changed in this form',
    capiPairCustomOption: (name: string) => `Custom event: ${name}`,
    // WEB-3756 сверка пикселей 1, М10: пометка той же строкой « · custom» (эталон Net).
    capiPairCustomTag: 'custom',
    capiPairCustomHint:
      'Need a different name? Start typing to enter your own.',
    capiPairGroupCustom: (_network: string) => 'Custom events',
    capiPairGroupStandard: (network: string) => `Standard ${network} events`,
    capiPairLegacyTag: 'former name',
    capiPairSimilarStandard: (name: string) =>
      `looks like the standard ${name} — check the spelling: the network treats a different spelling as a separate event`,
    capiPairNameEmpty: 'Enter the event name',
    capiPairNameChars:
      'Only Latin letters, digits, spaces, “_” and “-” are allowed',
    capiPairNameTooLong: (max: number) => `No longer than ${max} characters`,
    capiPairNameTiktok:
      'TikTok: starts with a Latin letter, then only Latin letters, digits, “_” and “-”',
    capiPairNamePurchase: 'A purchase name is only for events with a payment',
    capiPairTestEventNote: 'name for the “Send a test event” button',
    capiPairsFixBeforeSave:
      'Fix the event names highlighted above — the server will not accept them',
    /**
     * Ревизия 46 (Н7): кнопка «Сохранить» ШТОРКИ отправляла пустой Pixel ID
     * на сервер — отказ приходил оттуда и был не про то поле.
     */
    capiPixelIdRequired: 'Enter the Pixel ID',
    // WEB-4116: кнопка копирования Pixel ID — как у эталона Net.
    capiPixelIdCopy: 'Copy',
    capiTestEventCode: 'Test Event Code (optional)',
    // WEB-2408 п.5: код уходит только с проверочным событием.
    capiTestEventCodeHint:
      'The code is attached only to the test event sent by the button below; live conversions go out without it.',
    capiConnect: 'Connect',
    // ── WEB-2218 / WEB-2220 / WEB-2224 / WEB-2235: строка сети и её форма ────
    pixelStateLiveTag: 'Working',
    // WEB-4116 (сверка вида 31.10, мелочь 13): строки состояния — как у эталона Net;
    // «затихло» и «ещё не было» различает метка, а не строка.
    pixelStateLiveNote: (ago: string) => `last event ${ago}`,
    pixelStateIdleTag: 'No events yet',
    pixelStateIdleNote: 'no event has reached the network yet',
    // WEB-2413 п.3 (пачка 49, А-8): a pixel that WAS working and then went
    // quiet for over a day must not look identical to "never had an event" —
    // that hides the exact signal the status was built to show (WEB-2220).
    pixelStateSilentTag: 'Went quiet',
    pixelStateSilentNote: (ago: string) => `last event ${ago}`,
    pixelStateErrorTag: 'Error',
    pixelStateErrorNote: (reason: string) => `the network refuses: ${reason}`,
    pixelStateUnknownTag: 'Could not check',
    pixelStateUnknownNote: 'the connection is still in place',
    pixelStateRetry: 'Retry',
    /** WEB-4127: шапка формы правки — счёт за сутки (эталон settings-k.jsx:457). */
    pixelEditCount24h: (n: number) => `${n} in 24 h`,
    pixelEditNoErrors: 'no errors',
    pixelEditErrors: (n: number) => `errors: ${n}`,
    pixelTokenSaved: 'Saved',
    pixelTokenReplace: 'Replace',
    pixelTokenNoReenter:
      'You do not need to enter it again to change the other settings.',
    pixelPurchasesTitle: 'Which payments to send',
    // WEB-3756 сверка пикселей 1 — подписи эталона settings-k.jsx Net.
    pixelPurchasesAll: 'All, with renewals',
    pixelPurchasesFirst: 'First only',
    pixelRefundsTitle: 'Refunds and lost disputes as a conversion cancel',
    pixelRefundsHint:
      'Without this the network learns from purchases whose money came back.',
    pixelMultiplierTitle: 'Purchase value multiplier',
    pixelMultiplierHint:
      'The network sees the amount multiplied by this number. Values above one are used when renewals usually follow the first payment.',
    pixelProbeSend: 'Send a test event',
    // WEB-2413 п.10 — проверочное событие сервер шлёт по СОХРАНЁННОМУ
    // подключению, поэтому при несохранённых правках кнопка неактивна.
    pixelProbeNeedsSave:
      'Save your changes first — the test event is sent for the saved pixel.',
    pixelProbeSending: 'Sending…',
    pixelProbeSentTitle: 'The event was sent, the network answered “accepted”',
    pixelProbeSentText:
      'That does not mean it is already visible in the network dashboard: check it in the event-testing section — it shows up there within a few minutes.',
    pixelProbeFailed: 'Could not send the test event',
    // ── WEB-2220/WEB-2218: шапка строки сети — живой статус, плашка ошибки ──
    // Причина отказа приходит машинной строкой `<статус>[:<код>]`; словами её
    // переводит `components/capi/capiPixelHealth.ts`.
    pixelFailureAuth: (code: string) => `the token is not valid (code ${code})`,
    pixelFailureAuthNoCode: 'the token is not valid',
    pixelFailureRejected: (code: string) =>
      `the dispatch was refused, code ${code}`,
    pixelFailureRejectedNoCode: 'the dispatch was refused',
    pixelFailureTransport: 'we could not reach the network',
    // WEB-2413 п.4 (пачка 49, А-7): the network DID answer with a temporary
    // error (429/5xx) — this is not "we could not reach the network", and the
    // code must stay visible so the client can look it up.
    pixelFailureTransientCode: (code: string) =>
      `the network is temporarily refusing (code ${code})`,
    // WEB-2413 п.15 (пачка 49, А-6 остаток): the network answered 2xx but did
    // not confirm receipt (TikTok without a `code`) — distinct from both "not
    // reached" and "refused".
    pixelFailureUnconfirmed: (code: string) =>
      `the network did not confirm receipt (code ${code})`,
    pixelErrorSince: (when: string) =>
      `Events have not been arriving since ${when}`,
    pixelErrorAdviceAuth: (reason: string) =>
      `The network answers: ${reason}. Issue a new token in the network dashboard and paste it here.`,
    // The server has put the dispatches on hold until a new token arrives
    // (`failed_awaiting_token`): besides "replace the token" the client must
    // learn that nothing is lost yet.
    pixelErrorAdviceAwaitingToken: (reason: string) =>
      `The network answers: ${reason}. Issue a new token in the network dashboard and paste it here — the events that are waiting for it will be sent automatically.`,
    pixelErrorAdviceRejected: (reason: string) =>
      `The network answers: ${reason}. Check the pixel id and the token in the network dashboard, then send a test event.`,
    pixelErrorAdviceTransport:
      'We could not reach the network. Usually this clears up by itself; if it does not, send a test event and look at the answer.',
    pixelErrorAdviceTransientCode: (reason: string) =>
      `The network answers: ${reason}. This usually clears up by itself once the network's load or rate limit eases; if it keeps happening, check the network dashboard.`,
    pixelErrorAdviceUnconfirmed: (reason: string) =>
      `The network answers: ${reason}. We will retry automatically; no action needed unless it keeps happening.`,
    pixelDeliveryLog: 'Dispatch log',
    // Исход проверочного события судится по `outcome`, а не по коду ответа:
    // ручка отвечает 200 и на отказ сети, и на «не достучались» (WEB-2220).
    pixelProbeRejectedTitle: 'The network refused the test event',
    pixelProbeRejectedText: (code: string) =>
      `The network answered with a refusal: ${code}. Check the pixel id and the token — the event did not reach the network dashboard.`,
    pixelProbeRejectedTextNoCode:
      'The network answered with a refusal and gave no code. Check the pixel id and the token — the event did not reach the network dashboard.',
    pixelProbeNoAnswerTitle: 'The event did not reach the network',
    pixelProbeNoAnswerText:
      'We got no answer from the network. Nothing was changed in your keys: try again in a minute.',
    // WEB-3756 сверка пикселей 1: «Дополнительно» одним словом (эталон Net).
    pixelAdvancedLinkNew: 'Advanced',
    pixelSaved: 'Saved',
    capiConnectTitle: (network: string) => `Connect ${network}`,
    capiConnectSubtitle:
      'Enter the details to send conversion events through the Conversions API.',

    createProjectDenied:
      'Only the organization owner or an admin can create projects',

    storeUrlMustBeHttps: 'Store links have to start with https://',
    webPaywallMustBeHttps: 'The web paywall address has to start with https://',
    returnSchemeInvalid:
      'Return scheme: lowercase letters, digits and +-. only, starting with a letter, without “://”',

    // ── WEB-1198, исполнитель B: группа веток «Основного» (блоки 6-7) ───────
    // Тексты — из задания 24 §5.5 (оно главнее макета) и из макета
    // `design/project-settings-mockup/src/app-connection/branches/*`.
    acBrGroupTitle: 'What else the connection can do',
    acBrExpandAll: 'Expand all',
    acBrCollapseAll: 'Collapse all',
    acBrForeignTag: 'not needed for your path',

    acBrRecogTitle: 'How the app recognizes your buyer',
    acBrRecogSummaryPlatform: 'platform',
    acBrRecogSummaryTracker: 'tracker',
    acBrRecogSummaryOff: 'off',
    acBrRecogStoreRules:
      'App Store review may ask about device matching.',
    acBrRecogAutoTitle: 'Automatically, by the platform',
    acBrRecogTrackerTitle: 'Through an ad tracker',
    acBrRecogTrackerChip: 'in addition',
    acBrRecogOffTitle: 'Do not recognize by device',
    acBrRecogMmpLabel: 'Which tracker you use',
    acBrRecogMmpLinkLabel: 'App link in the tracker',
    acBrRecogPostbackLabel: 'Install report address',
    acBrRecogPostbackHint: 'Paste it into the tracker — it reports installs to us there.',

    acBrProviderTitle: 'Where to deliver access',
    acBrProviderSelectLabel: 'Which service do you have connected',
    acBrProviderAdaptyIosKey: 'Adapty secret key, iOS',
    acBrProviderAdaptyAndroidKey: 'Adapty secret key, Android',
    acBrProviderAdaptyLevel: 'Access level name in Adapty',
    acBrProviderAdaptyLevelHint: 'The level name in your Adapty — we put the payment there.',
    acBrProviderRcKey: 'RevenueCat secret key',
    acBrProviderRcEnt: 'Entitlement name in RevenueCat',
    acBrProviderSandboxToggle: 'Sandbox keys — optional',
    acBrProviderSandboxChip: 'for the test app',
    acBrProviderTestIos: 'Test key, iOS',
    acBrProviderTestAndroid: 'Test key, Android',
    acBrProviderTestRc: 'RevenueCat test key',
    acBrProviderLoadFailed:
      'Could not read the keys — saving is off so the stored values are not overwritten.',

    acBrDeeplinkTitle: 'If the app is installed, open it right away',
    // Абзацы панели «Зачем это» — отдельными ключами: разметка (переносы,
    // жирное, код-чипы) собирается в компоненте, а не прячется в строке.
    acBrDeeplinkIosLabel: 'iOS — Team ID and Bundle ID in one line',
    acBrDeeplinkAndroidLabel: 'Android — package name',
    acBrDeeplinkShaLabel: 'Android — signing key fingerprint (SHA-256)',
    acBrDeeplinkReturnLabel: 'Return-to-app link',
    acBrDeeplinkOwnSchemeLink: 'Does the app already have its own scheme?',
    acBrDeeplinkOwnSchemeLabel: 'The app’s own scheme',
    acBrDeeplinkOwnSchemeRestore: 'Restore ours',
    acBrDeeplinkReturnCodePlaceholder: '<code>',

    acBrLevelsTitle: 'What to open in the app after payment',
    acBrLevelsSummaryPairs: (n: number) =>
      `${n} ${plural('en', n, { one: 'pair', other: 'pairs' })}`,
    acBrLevelsPairRemove: 'Remove',
    acBrLevelsPairAdd: 'Add a price',

    acBrCheckTitle: 'Check the link before the first payment',
    acBrCheckSummaryOn: 'on',
    acBrCheckEnable: 'Turn on',
    acBrCheckDisable: 'Turn off test mode',
    // WEB-2261: путь, на котором сервер приводит запись к `testMode: false`.
    acBrCheckServerOff:
      'With this connection method the server keeps test mode off, so it cannot be turned on here.',
    acBrCheckShowAnswer: 'Show the answer the app will receive',
    // WEB-3757 юнит 3 — ветки «Основного» на ките, тексты эталона settings-app.jsx (ce823f8f)
    acBrProviderYourPath: 'your path',
    acBrLevelsSummaryOf: (n: number, m: number) =>
      `${n} of ${m} ${plural('en', m, { one: 'price', other: 'prices' })}`,
    acBrRecogAutoChip: 'on',
    acBrRecogMmpLinkHelpAf: 'AppsFlyer → OneLink: the whole template link.',
    acBrRecogMmpLinkHelpAdj: 'The full Adjust link including adj_t.',
    acBrProviderEraseTitle: (service: string, platform: string | null, test: boolean) =>
      `Erase the ${service}${platform ? ' ' + platform : ''} ${test ? 'test key' : 'key'}?`,
    acBrProviderEraseText: (service: string, platform: string | null, test: boolean) =>
      `Access stops being delivered to ${test ? 'the ' + service + ' test app' : service}${platform ? ' for ' + platform : ''} until you set a new one.`,
    acBrProviderKeyErased: 'Key erased',
    acBrDeeplinkShaHelp: 'The app signing key in Google Play, not the upload key.',
    acBrDeeplinkReturnHelp:
      'Give it to the developer — they register the scheme in the iOS and Android builds. Through it the app gets a one-time code and recognizes the buyer.',
    acBrDeeplinkOwnSchemeHelp:
      'Just the name, without “://”. Must match in the iOS and Android builds.',
    acBrLevelsPriceLabel: 'Price',
    acBrLevelsAddRest: (n: number) => `Add rows for the other prices (${n})`,
    acBrLevelsNoPairTitle: 'No pair is set.',
    acBrLevelsNoPairText:
      'The app gets a price ID like price_1Tyw… instead of the word it checks — a paying buyer is left without access.',
    acBrLevelsNoNameTitle: 'Not every price has a name.',
    acBrLevelsNoRows: (n: number, names: string[], more: number) =>
      `${n} ${plural('en', n, { one: 'price has', other: 'prices have' })} no row: ${names.join(', ')}${more > 0 ? ` and ${more} more` : ''}.`,
    acBrLevelsNoName: (n: number) =>
      n === 1
        ? 'In one row the price is chosen but the name is empty'
        : `In ${n} rows the price is chosen but the name is empty`,
    acBrLevelsNoNameText:
      'For these prices the app gets an ID instead of a word — the buyer pays and gets no access.',
    acBrCheckAnswerTitleOn: 'Response to the app — test mode now',
    acBrCheckAnswerTitleOff: 'Response to the app — normal mode',

    // WEB-3757 юнит 4 — «Для разработчика» на ките, тексты эталона settings-app.jsx (ce823f8f)
    acDevEntitlementRespLabel: 'Response',
    acDevSdkLabel: 'Library (SDK)',
    acDevSecretRotateTitle: 'Rotate the secret?',
    acDevSecretRotateBody: `We show the new secret once. The old one is accepted for ${GRACE_HOURS_EN} more — enough to update the server without downtime.`,
    acDevSecretRotateOk: 'Yes, rotate',
    acDevSecretShownNewTitle: 'Signing secret issued',
    acDevSecretShownReTitle: 'Secret reissued',
    acDevSecretShownText: 'Shown once. Save it on your server — we will not show it again.',
    acDevSecretShownGrace: `The old secret keeps working for ${GRACE_HOURS_EN} so your server can update.`,
    acDevKeysEmpty: 'No keys yet. Create the first one — without it your server cannot ask us.',
    acDevKeysLoadFailed: 'Could not load the keys',
    apiKeyNameLabel: 'Name',
    // Эталон называет запретом тестового ключа только деньги; сервер (api-key.guard.ts,
    // TEST_KEY_BLOCKED_SCOPES) запрещает ему и управление подпиской — текст по серверу.
    apiKeyModeHelp:
      'Live moves real money and subscriptions: charges, refunds, cancelling, pausing and resuming a subscription. Test does the same except those — refusal test_key_not_allowed. We show the key once.',
    apiKeyCreateOk: 'Create',
    apiKeyShownOnce: 'The key value is shown only once',
    acDevPortalLabel: 'Portal paywall',

    // WEB-3757 юнит 5 — «События на ваш сервер» и постбэк на ките, тексты эталона
    // settings-app.jsx; числа (повторы, срок хранения) — из констант кода.
    webhooksGroupHelp: (days: number, attempts: number) =>
      `Webhooks: we send events to your address, signed with the receiver secret. Retried ${attempts} ${plural('en', attempts, { one: 'time', other: 'times' })} within an hour, the log is kept for ${days} ${plural('en', days, { one: 'day', other: 'days' })}.`,
    webhooksEmptyNote:
      'No receivers yet. Add an address if your server needs to know about purchases and access.',
    webhooksPausedTitle: 'Delivery paused: the plan was downgraded',
    webhooksPausedTitleOn: (date: string) => `Delivery paused: the plan was downgraded on ${date}`,
    // Эталон обещает письмо «один раз»; сервер шлёт его не чаще раза в сутки
    // (webhook-emit.service.ts, BRIDGE_PAUSED_MAIL_TTL_MS) — текст по серверу.
    webhooksPausedText:
      'Receivers and their settings are kept — delivery resumes by itself once you are back on Pro. Events from this time are not stored and will not be resent. The project owner gets an email about it at most once a day, not one per event.',
    webhooksBackToPro: 'Back to Pro',
    webhookDotOn: 'On',
    // WEB-4090: точка по исходу последней доставки (эталон settings-app.jsx:328-329).
    webhookDotErr: (code: string) => `Last delivery — error ${code}`,
    webhookDotDelivering: 'Delivering',
    webhookDotOff: 'Off',
    webhookDotPaused: 'Paused: the plan was downgraded',
    webhookTestOffHint: 'The receiver is off',
    webhookTestProHint: 'Available on Pro',
    delivChipPeriod: 'Period',
    colAttempts: 'Attempts',
    delivRetryRow: 'Send again',
    delivRetryRowSoon: 'Resending an event is coming later',
    webhookEventsTitle: 'Events',
    webhookAnalyticsTitle: 'Funnel analytics events',
    webhookUserPropsTitle: 'Allowed buyer properties',
    webhookUserPropsHelp: 'Comma-separated. Empty — we send all properties.',
    webhookUrlHttpsErr: 'The address must start with https://',
    postbackTagsHelp: 'We fill in the tags: {click_id}, {payout}, {status}, {txid}.',

    // WEB-3757 юнит 6 — «Логи SDK» на ките, тексты эталона settings-app.jsx (раздел Logs).
    // Подсказка у времени — по коду: время записи серверное (createdAt), печатаем в UTC.
    acLogSearchPlaceholder: 'Event or guid',
    acLogChipLevel: 'Level',
    acLogChipPeriod: 'Period',
    acLogLevelAll: 'All',
    acLogLevelDebug: 'Debug',
    acLogLevelInfo: 'Info',
    acLogLevelWarn: 'Warning',
    acLogLevelError: 'Error',
    acLogPeriodAll: 'All time',
    acLogPeriod7: '7 days',
    acLogPeriod30: '30 days',
    acLogReset: 'Reset',
    acLogModeRecords: 'Records',
    acLogColTime: 'Time',
    acLogTimeHelp:
      'When the entry reached our server, in universal time (UTC). The device’s own clock is in the expanded entry.',
    acLogRowMeta: (clock: string, platform: string) =>
      `Device clock: ${clock} (may drift from our time) · ${platform}`,
    acLogsNone: 'No records yet — they appear after the app first runs.',
    acLogsShowMore: 'Show more',
    acLogsOf: (shown: number, total: number) => `${shown} of ${total}`,

    // WEB-1198, исполнитель C — подвкладка «Для разработчика», задание 24 §6
    // (блоки 9, 10 и переходный блок 12 карты вёрстки).
    acDevWhyCollapse: 'Collapse',
    acDevStatusConnected: 'The app is calling us.',
    acDevStatusNever: 'The app has not connected yet.',
    acDevStatusLastInstall: (moment: string) => `Last install: ${moment}`,
    acDevStatusFailed: 'Could not load the connection state.',
    acDevRetry: 'Retry',
    acDevUpsellTitle: 'App connection is available on Pro.',
    acDevUpsellText:
      'Project keys, the inbound signing secret and event receivers are part of the machine bridge “website → app”, and it works on paid plans.',
    acDevUpsellButton: 'Upgrade to Pro',
    acDevStartTitle: 'Getting started',
    acDevPlatformIos: 'iOS',
    acDevPlatformAndroid: 'Android',
    acDevPlatformServer: 'Your server',
    acDevApiLabel: 'API address',
    acDevProjectIdLabel: 'Project ID',
    acDevSecretLabel: 'Signing secret for inbound requests',
    acDevSecretNotConfigured: 'the secret is not set',
    acDevSecretIssue: 'Issue',
    acDevSecretRotate: 'Rotate',
    acDevSecretConfigured: 'connected',
    acDevSecretRotatedAt: (moment: string) => `last rotation ${moment}`,
    acDevSecretWhyGrace: `Your server signs its requests to us with this secret — it is not the event receiver secret. After rotation the old one works for ${GRACE_HOURS_EN} more.`,
    acDevEntitlementLabel: 'Entitlement request address',
    acDevEntitlementHint:
      'One GET, no key. Access exists if grants has an entry with status: "active".',
    acDevInstallHintIos: (version: string) =>
      `iOS, Swift Package Manager. Version ${version} — add it to the project dependencies.`,
    acDevInstallHintAndroid: (version: string) =>
      `Android, Gradle (JitPack). Version ${version} — add it to the project dependencies.`,
    acDevInstallOpen: 'Open →',
    // Примеры кода живут в словаре, а не в компоненте: комментарии внутри
    // примера читает человек, значит и они существуют на обоих языках
    // (правило «два языка»). Сам код — не текст интерфейса и не переводится.
    acDevSnippetIos: (projectId: string, apiBase: string) =>
      `${SDK_IOS_IMPORT_LINE}\n\n// once at startup\n${sdkIosConfigureCall(projectId, apiBase)}\n\n// check whether there is access\nWeb2App.entitlement { grant in\n    let isPaid = grant?.isActive ?? false\n}`,
    acDevSnippetAndroid: (projectId: string, apiBase: string) =>
      `// once at startup\n${sdkAndroidConfigureCall(projectId, apiBase)}\n\n// recognition: we read the referrer ourselves, no arguments needed\nWeb2AppSdk.identify()`,
    acDevSnippetServer: (apiBase: string) =>
      `curl ${apiBase}/s2s/v1/entitlement?guid=${SDK_SERVER_SNIPPET_GUID} \\\n  -H "Authorization: Bearer sk_live_8fK2…"\n\n# 200 OK\n# { "grants": [{ "level": "premium", "status": "active" }] }`,
    acDevSnippetLabelIos: 'Connecting the library, iOS',
    acDevSnippetLabelAndroid: 'Connecting the library, Android',
    acDevSnippetLabelServer: 'A request from your server',
    acDevCopyCode: 'Copy the code',
    acDevCodeCopied: 'The code has been copied',
    acDevFullDocs: 'Full documentation →',
    acDevKeysTitle: 'Access keys',
    acDevKeysTech: 'Bearer sk_…',
    acDevHooksTitle: 'Events to your server',
    acDevPostbackTitle: 'Sales reports to the tracker',
    acDevPostbackTech: 'postback S2S',
    acDevPostbackLabel: 'Postback address',
    acDevDocsTitle: 'Reference',
    acDevDocsRowTitle: 'Developer documentation',
    acDevDocsRowHint:
      'Requests, response fields, refusal codes, examples in Swift, Kotlin and curl.',
    acDevDocsOpen: 'Open →',
    acDevMcpRowTitle: 'MCP server for an AI assistant',
    acDevMcpRowHint: 'Connect it in Cursor or Claude Code.',
    acDevPortalTitle: 'Subscription management portal',
    acDevPortalMovingChip: 'moving to “Subscriptions”',
    acDevPortalNote:
      'The block stays here until the “Subscriptions” section takes it over: the customer comes to this paywall by the link from the letter and manages the subscription there.',
    // WEB-3413: ссылка «Управлять подпиской» в письме после оплаты — только при назначенном портале.
    acDevPortalNoManageLink:
      'Without a portal, the email after payment is sent without a subscription management link.',
    // WEB-2458 п.3: на бесплатном тарифе портал виден, но не правится.
    acDevPortalProOnly: 'This setting is available on Pro',

    // ── WEB-2027, исполнитель A: каркас раздела «Платежи» ──────────────────
    // Справка сверху, две подвкладки, сноска, витрина будущих систем и роль
    // «владелец без прав на деньги». Тексты дословно из макета
    // `design/project-settings-mockup/src/payments/**`.
    // WEB-2126: способ приёма оплаты — ВЗАИМОИСКЛЮЧАЮЩИЙ выбор, а не тумблер
    // рядом с живым Stripe (правка владельца 17.09).
    payMoneyModeTitle: 'How you take payments',
    payMoneyModeOursTitle: 'Through us',
    payMoneyModeOursDesc:
      'You connect a payment provider and sell on our paywall. Products, prices and customers are in “Subscriptions”.',
    payMoneyModeOwnTitle: 'On your own',
    payMoneyModeOwnDesc:
      'You take the money on your side and build quizzes and collect data with us. We will not show the money sections of the cabinet.',
    payMoneyModeOwnNote:
      'Hidden is what will never fill up for you: the money steps of the funnel, the money columns of the statistics, “Cohorts”, “Subscriptions”, the payment figures above the leads and the email triggers about payment. The choice is visible in “Audit → Project team”.',
    payMoneyOwnKeptAnd: ' and ',
    payMoneyOwnKeptOne: (providers: string) => `${providers} stays connected.`,
    payMoneyOwnKeptMany: (providers: string) => `${providers} stay connected.`,
    payMoneyOwnKeptRest:
      ' Subscriptions taken out earlier keep being charged — we cut nothing off. ',
    payMoneyOwnDisconnectOne: 'Disconnect the payment provider',
    payMoneyOwnDisconnectMany: 'Disconnect the payment providers',
    payMoneyModeOwnToast:
      'The money sections are hidden. Taking payments is untouched',
    payMoneyModeOursToast: 'The money sections are back',
    payMoneyModeSaveFailed: 'Could not save how you take payments',
    payWarnPayUsNoAppTitle: 'A buyer has to be taken somewhere after paying.',
    payWarnPayUsNoAppText:
      'You are not connecting an app, so you grant access yourself: the purchase event will arrive at your server. Where to send the person after payment is set in the paywall.',
    payOtherSoon: 'soon',
    payOtherNotConnected: 'not connected',
    payNoBillingRowText:
      'Payments are connected. The details and the controls are available to the owner',
    payNoBillingRowNoneText:
      'Payments are not connected. The details and the controls are available to the owner',
    payNoBillingRowFailText:
      'We could not check the connection. The details and the controls are available to the owner',
    payNoBillingGroupText:
      'Payment data is not available to you — access to it is granted by the owner of the organization',

    // ── Платежи → «Правила и деньги» (блоки Б8-Б13) ─────────────────────────
    // Часть подписей группы берётся из уже живущих ключей: восемь ярлыков
    // исхода (`chargeStatus*`, `chargeOutcomeUnknown`, `chargeChargebackLabel`),
    // подписи колонок, «Вернуть», «Пробный период», «Дней льготного периода» и
    // тексты отказов потолков (`moneyRangeMessage`, `countRangeMessage`,
    // `moneyFractionMessage`, `moneyZeroDecimalFraction`) совпадают с макетом
    // буква в букву. Заводим только то, чего в словаре нет.
    payRulesTitle: 'Subscription rules',
    // WEB-3756 доводка 1: `payRulesChip` снят парой — подзаголовка группы в эталоне нет.
    payBranchOff: 'off',
    // Пара к `payBranchOff`: свёрнутая ветка обязана говорить факт, а не одно
    // и то же всем (регресс слияния 14.09, «Деньги покупателей»).
    payBranchOn: 'on',
    payGraceIntro:
      'How many days a customer keeps access when the next charge fails — the card was declined, there were not enough funds and so on.',
    // Срок СЛОВОМ, а не числом: так в макете («…по умолчанию — три дня»).
    // Зеркало `DEFAULT_GRACE_PERIOD_DAYS` (3) — число рядом печатается той же
    // подписью `graceDays`, поэтому две записи одного срока не разъедутся молча
    // на глазах у человека: они стоят в одной ветке, одна под другой.
    payGraceDefaultWord: 'three days',
    payGraceRangeHint: (min: number, max: number, defaultDays: string) =>
      `A whole number from ${min} to ${max}. An empty field brings back the default — ${defaultDays}.`,

    payMoneyTitle: 'Customer money',
    payMoneyChipReal: 'real charges',
    payMoneyChipNote: 'needed only if you have your own server',
    // Без пиктограммы: эмодзи в интерфейсе не бывает (решение владельца
    // 14.09.2026, перебивает макет — там на этом месте стоял «🔒»). Замок
    // группы — текстом записки `Lock` (WEB-3756 юнит 9), ярлыка «закрыто» нет.
    payMoneyLockedTitle:
      'It opens once you connect a payment system. This is where charges on saved cards with your server key, refunds and the charge log will live.',
    payMoneyLockedText:
      'Needed only if you have your own server. If you sell through our checkout screens, you will not need this part at all.',
    payMoneyTierTitle: 'Customer money is available on Pro.',
    payMoneyTierText:
      'Charges, refunds and subscription management through your own server are part of the machine bridge “website → app”, and it works on paid plans.',

    payChargesBranch: 'Charges via API',
    payRefundsBranch: 'Refunds via API',
    paySubsBranch: 'Subscription management via API',
    payJournalBranch: 'Charge log',

    payCapBuyerDailyLabel: 'Charges per customer per day',
    payCapProjectDailyLabel: 'Operations per project per day',
    payCapProjectAmountLabel: 'Amount per project per day',
    payCapPlaceholder: (value: string) => `default ${value}`,

    // ── WEB-3756 платежи 29.10 (правка эталона спек-репо 46e4be66, settings-k.jsx Rules; владелец:
    // «по текстам тут просто жесь»): одно короткое предупреждение на право сервера, подробности —
    // в «?» у названия строки и у полей ограничений; числа «?» — постоянные ChargesPanel.tsx.
    payChargesNoticeTitle: 'Your server will be able to charge customers’ cards without them',
    payChargesNoticeText:
      'Charge only what the customer agreed to. Otherwise they dispute it with the bank and the payment system cuts your payouts.',
    payChargesHelp:
      'Only customers with a subscription. If the sk_… key leaks, turn it off: the right is removed from all keys at once. Does not affect the dashboard.',
    payCapOpHelp: (max: string) => `In your price currency, not cents. Up to ${max}`,
    payCapBuyerDailyHelp: (min: string, max: string, hard: string) =>
      `${min}–${max}. No more than ${hard} per buyer per day in any case`,
    payCapProjectDailyHelp: (min: string, max: string) => `${min}–${max}`,
    payCapProjectAmountHelp: 'Shared across all project currencies',
    payCapsSave: 'Save',
    payRefundsNoticeTitle: 'Your server will be able to refund money and revoke access',
    payRefundsNoticeText: 'Turn it on only if refunds go through your server.',
    payRefundsHelp:
      'Separate from charges. Refunds in the dashboard work without it. If the key leaks, turn it off: the right is removed from all keys at once.',
    paySubsNoticeTitle: 'Your server will be able to stop, pause and resume subscriptions',
    paySubsHelp:
      'If the key leaks, turn it off: the right is removed from all keys at once. Does not affect the dashboard.',

    payJournalHintBefore:
      'Charges your server made on customers’ saved cards (the server API). ',
    payJournalHintStrong: 'Refunds are full-amount only.',
    payJournalSearchPlaceholder: 'Customer or email',
    payJournalStatusAny: 'Any outcome',
    payJournalPeriod30: 'Last 30 days',
    payJournalExport: 'Export',
    payJournalEmpty:
      'No charges yet. Charges made by your server through the server API will show up here',
    payJournalLoading: 'Loading the charges…',
    // Подсказка ярлыка «Исход неизвестен» СВОЯ, а не переиспользованная
    // подсказка прежней секции списаний: у макета там запятая («оборвалась,
    // деньги»), у той секции — тире. Секция снята WEB-2544, её ключ — вместе
    // с ней.
    payJournalOutcomeUnknownTip:
      'The connection to the provider dropped, the money may have been charged. Reconciliation will settle the outcome.',
    payJournalNoteUnknownName: '“Outcome unknown”',
    payJournalNoteUnknownA:
      ' — the connection to the provider dropped, the money may have been charged. Reconciliation will settle the outcome. ',
    payJournalNoteUnknownB: 'Within 24 hours',
    payJournalNoteUnknownC: ' you have to repeat ',
    payJournalNoteUnknownD: 'the very same request',
    payJournalNoteUnknownE:
      ': it goes to the payment system with the key of the first attempt, and there will be no second charge. Later the server refuses on its own and tells you: check the payment in the payment system and, if there was no charge, repeat it with a new key.',
    payJournalNoteChargebackName: '“Taken back by the bank”',
    payJournalNoteChargebackText:
      ' — the customer disputed the charge with their bank and won: the money was taken from you. This is not a voluntary refund, hence a label of its own.',
    payJournalExportTruncated: (rows: string) =>
      `The export is truncated: only the first ${rows} rows made it into the file.`,

    // ── Журнал списаний: отбор, сортировка, причина отказа (WEB-2084/WEB-2071)
    payJournalPeriod7: 'Last 7 days',
    payJournalPeriod90: 'Last 90 days',
    payJournalPeriodAll: 'All time',
    payJournalAmountMin: 'Amount from',
    payJournalAmountMax: 'Amount to',
    payJournalAmountHint:
      'Amount in the smallest currency unit, the way the payment system stores it (1999 = 19.99).',
    payJournalAmountBad:
      'Enter a whole number in the smallest currency unit (for example 1999 for 19.99) — the filter is not applied.',
    payJournalSortNewest: 'Newest first',
    payJournalSortOldest: 'Oldest first',
    payJournalSortAmountDesc: 'Largest amount first',
    payJournalSortAmountAsc: 'Smallest amount first',
    colFailureReason: 'Reason',
    colInitiator: 'Initiated by',
    payJournalRunTip: (runId: string) => `Part of batch ${runId}`,
    // Инициаторы списания — подписи реестра
    // `backend/src/stripe/enums/charge-actor.enum.ts`. Пара сторожей держит
    // множества в обе стороны: значение без подписи и подпись без значения —
    // одинаково красные.
    chargeActorOwner: 'Project owner',
    chargeActorAdmin: 'Organization admin',
    chargeActorServerKey: 'Server key',
    chargeActorCron: 'Scheduled task',
    chargeActorUnknown: 'Not recorded',
    // Расшифровки кодов отказа платёжной системы. Незнакомый код печатается
    // как есть — выдуманная фраза на денежном экране хуже машинного кода.
    declineInsufficientFunds: 'Not enough money on the card',
    declineDoNotHonor: 'The bank declined the charge without a reason',
    declineExpiredCard: 'The card has expired',
    declineLostCard: 'The card is reported lost',
    declineStolenCard: 'The card is reported stolen',
    declineIncorrectCvc: 'Wrong card security code',
    declineIncorrectNumber: 'Wrong card number',
    declineProcessingError: 'A payment system error — worth retrying',
    declineCardDeclined: 'The bank declined the charge',
    declineGenericDecline: 'The bank declined the charge',
    declineTryAgainLater: 'The bank asked to retry later',
    declineAuthenticationRequired:
      'The bank requires the customer to confirm the payment',
    declineCardNotSupported: 'The card does not support this kind of charge',
    declineCurrencyNotSupported: 'The card does not support this currency',
    declineWithdrawalCountExceeded: 'The card’s charge limit is exhausted',
    declinePickupCard: 'The card is blocked by the bank',
    declineRepeatNever: 'Do not repeat: the card will not work again',
    declineRepeatLater: 'Can be repeated later',
    // WEB-3756 юнит 10: журнал списаний на ките — меню «⋯» строки с одним
    // действием «Полный возврат» (владелец 25.10 п. 61) и подпись листалки `Pager`.
    // Таблицы больше нет — сняты ставшие мёртвыми `colCustomer`, `colAmount`,
    // `colPayment` и `chargeRefundButton` (обе половины).
    payJournalRowActions: (id: string) => `Actions for charge ${id}`,
    payJournalRefundFull: 'Full refund',
    payJournalPager: (from: number, to: number, total: number) =>
      `${from}–${to} of ${total}`,

    // ── Платежи: пять окон (§7 задания, исполнитель D) ──────────────────────
    // Числа в этих строках выделены жирным, поэтому фраза разрезана на куски
    // вокруг числа, а не собрана подстановкой: подставить `<b>` внутрь строки
    // словаря нельзя, а терять выделение — расходиться с макетом.
    payUnbindAllTitle: 'Disconnect Stripe?',
    payUnbindStillBillingBefore: 'Of them, still charging money: ',
    // WEB-3756 юнит 8: абзацы окна полной отвязки (`payUnbindActiveCountBefore`,
    // `payUnbindAllBothSets*`, `payUnbindAllOneTime*`, `payUnbindAllPricesHint`) сняты —
    // текст окна по макету (владелец 25.10 п. 71), новые ключи — блоком юнита 8.

    payUnbindOneTitle: (keySet: string) => `Detach the ${keySet}?`,
    payUnbindOneOnlySetBefore: 'Disconnecting removes ',
    payUnbindOneOnlySetStrong: (keySet: string) => `ONLY the “${keySet}” set`,
    payUnbindOneOnlySetRest:
      '. The other set and its event endpoint stay untouched.',
    payUnbindOneActiveCountBefore: 'Active subscriptions of this environment: ',
    payUnbindOneBuyersStrong: 'What happens to the customers.',
    payUnbindOneBuyersRest:
      ' Subscriptions created with this key set will be revoked. Access is revoked only for customers of this environment — customers of the other environment keep theirs.',
    payUnbindOneOneTimeStrong: 'One-time purchases stay with the customers:',
    payUnbindOneOneTimeRest:
      ' the money for them is not refunded, and we do not take the access away.',
    payUnbindOneSoleSetStrong: 'If this is your only connected set',
    payUnbindOneSoleSetRest:
      ' — disconnecting it equals a full disconnect: one-time purchases go dark too, and the second webhook leaves with them.',
    payUnbindOneDone: (keySet: string) => `${keySet} detached`,

    payToLiveBefore: 'Buyers will start being charged ',
    payToLiveStrong: 'real money',
    payToLiveRest: ' — test payments (the sandbox) will no longer be accepted.',

    payRefundBodyBefore: 'The customer ',
    payRefundBodyMid: ' will be refunded ',
    payRefundBodyTail: '. Partial refunds are not supported.',

    payImportedCountsBefore: 'Subscriptions transferred: ',
    payImportedCountsOf: ' of ',
    payImportedCountsSkipped: '. Skipped: ',

    // ── WEB-2027, исполнитель B: строка Stripe ──────────────────────────────
    // Предупреждения над строкой, среда оплаты, адрес событий, управление
    // ключами и форма первого подключения. Тексты дословно из макета
    // `design/project-settings-mockup/src/payments/connections/**`.
    payWarnAcceptOffTitle:
      'Payment acceptance is off — buyers see an error on the paywall.',
    payWarnSandboxTitle:
      'The sandbox is active right now — there are no real charges.',
    payConnFailTitle: 'We could not check the connection.',
    payConnFailText:
      'The server did not answer — that does not mean payments are off',
    payStripePowerOn: 'Turn Stripe off',
    payLoadingHint: 'Waiting for the server.',
    payWebhookLabel: 'Event endpoint',
    payWebhookNote:
      'We create the event endpoint in your account ourselves on connect and subscribe it to twenty-one types. Nothing has to be set up in the Stripe dashboard, and the list is not editable.',
    payEvCheckoutCompleted: 'Payment succeeded',
    payEvCheckoutExpired: 'Payment session expired',
    payEvAsyncSucceeded: 'Delayed payment succeeded',
    payEvAsyncFailed: 'Delayed payment failed',
    payEvSubCreated: 'Subscription started',
    payEvSubUpdated: 'Subscription changed',
    payEvSubDeleted: 'Subscription canceled',
    payEvInvoicePaid: 'Invoice paid',
    payEvInvoiceFailed: 'Invoice not paid',
    payEvRefundCreated: 'Refund created',
    payEvRefundUpdated: 'Refund updated',
    payEvDisputeCreated: 'Dispute opened',
    payEvDisputeClosed: 'Dispute closed',
    payEvIntentSucceeded: 'Charge succeeded',
    payEvIntentFailed: 'Charge failed',
    payEvProductCreated: 'Product created',
    payEvProductUpdated: 'Product changed',
    payEvProductDeleted: 'Product deleted',
    payEvPriceCreated: 'Price created',
    payEvPriceUpdated: 'Price changed',
    payEvPriceDeleted: 'Price deleted',
    payKeysLive: 'Live keys',
    payKeysTest: 'Test keys (sandbox)',
    payKeysNote:
      'Reconnecting opens an empty form — the old secrets are not prefilled. “Cancel” keeps the current keys.',
    // WEB-3756 доводка 1: одна строка пояснения эталона — intro + образцы pk_test_ / sk_test_.
    payConnectIntro: 'Paste the keys from your Stripe dashboard.',
    payConnectPkWarnLive:
      'These are live keys — payments will charge real money.',
    payConnectMismatchPkLive:
      'The keys come from different environments: the publishable one is live, the secret one is test',
    payConnectMismatchPkTest:
      'The keys come from different environments: the publishable one is test, the secret one is live',
    payConnectSkHint: 'Stored encrypted, never shown again.',
    payConnectTestHintBefore: 'Want to start with test keys — enter a ',
    payConnectTestHintMid: ' and ',
    payConnectTestHintAfter: ' pair here.',
    payConnectPending: 'Checking the keys…',
    // Фраза макета разрезана на два куска, потому что первое предложение в ней
    // ЖИРНОЕ, а `<b>` внутрь строки словаря не кладут. Запасной текст для
    // отказа без кода собирается из этих же двух кусков — одно предложение
    // живёт в одном месте, второй копии нет.
    payConnectFailedStrong: 'Could not connect.',
    payConnectFailedRest: ' Check the secret key — Stripe rejected it.',

    /*
     * WEB-2101 — «Отправить тестовое событие» у получателя вебхуков. Имя
     * кнопки названо ДОСЛОВНО в документации для разработчиков на всех четырёх
     * поверхностях (статья «Вебхуки» сайта docs.subster.ai на обоих языках,
     * обе половины конспекта `backend/src/mcp/content/docs-content*`): интегратор
     * просит владельца проекта нажать именно её, и расхождение имени тут же
     * стоит человеку времени на поиск несуществующей кнопки.
     */
    webhookTestSend: 'Send a test event',
    webhookTestSending: 'Sending a test event…',
    webhookTestWaiting:
      'Test event sent, waiting for the recipient to respond…',
    webhookTestOk: (response: string) =>
      `The recipient responded ${response} — the delivery went through`,
    webhookTestBad: (response: string) =>
      `The recipient responded ${response} — the delivery failed`,
    webhookTestDisabled:
      'The recipient is switched off — the test event was not sent. Switch it on and try again.',
    webhookTestSsrfBlocked:
      'Internal network address — the test event was not sent: we do not send events to addresses inside a private network. Use a public recipient address.',
    webhookTestNoResponse: (errorClass: string) =>
      `No response from the recipient — the delivery failed before it reached them (${errorClass}). See the delivery log.`,
    webhookTestNoAnswerYet:
      'There is no answer in the log yet: a manual send is retried up to 5 times. Open the delivery log a little later.',
    webhookTestFailed: 'Could not send the test event',

    // WEB-2069 (решение владельца М17) — потолок льготного периода. Доступ
    // после сбоя оплаты живёт до момента следующей попытки списания, но не
    // дольше этого потолка: карта может быть закрыта окончательно, попыток не
    // будет вовсе, и без верхней границы доступ повис бы бесплатным навсегда.
    payGraceMaxLabel: 'Grace period cap, days',
    payGraceMaxIntro:
      'If the payment system tells us when it will retry the charge, access lasts until that moment — but never longer than this cap.',
    // Срок СЛОВОМ, как у соседа: подпись читают фразой, а в ней цифра спотыкает.
    payGraceMaxDefaultWord: 'three weeks',
    graceMaxSaved: 'Grace period cap saved',
    graceMaxSaveFailed: 'Could not save the grace period cap',
    // ── WEB-2121: две подвкладки «Аналитика и пиксели» + строка Amplitude ────
    // Новый блок. Правится ТОЛЬКО вставками в конец: тот же файл в этой пачке
    // трогают ещё три юнита.
    analyticsTabAnalytics: 'Analytics',
    analyticsTabAdsIntro:
      'Pixels and server-side conversions: the network learns which ad led to the payment.',
    analyticsSoonTag: 'Planned',
    /** Единственное витринное имя с союзом — остальные строки марки, их не переводят. */
    // WEB-4116: имя строки «В планах» — как у эталона Soon.
    analyticsSoonGaLabel: 'Google Analytics',
    // WEB-3756 юнит 11: «Аналитика и пиксели» на ките — список слева (группы,
    // пояснение пункта), тумблер строки сети (эталон settings-k.jsx Ads, Net).
    // Сняты вместе с подвкладками и раскрывашкой строки сети (не читает больше
    // никто, проверено обходом `frontend/src`): `analyticsTabAds`,
    // `analyticsTabSoonTitle`, `pixelConfigure`, `pixelCollapse`, `pixelFillFields`.
    adsListAds: 'Advertising',
    adsListSoon: 'Coming soon',
    adsNoteOn: 'connected',
    adsNoteOff: 'not connected',
    pixelSendingOn: 'Sending on',
    // WEB-3757 (эталон settings-k.jsx Ads, сверка 30.10 поломка 4): проверка
    // подключения сети не ответила — одна плашка вместо строки и формы.
    pixelLoadFailTitle: 'Could not load connections',
    pixelSendingOff: 'Off',
    // WEB-3756 доводка 1 (решение владельца 27.10 № 9): тумблер неподключённой
    // сети погашен — причина в подсказке обёртки.
    capiSwitchConnectFirst:
      'Connect the network first: fill in the fields below',

    amplitudeNotConnected: 'Not connected',
    amplitudeOffKeySaved: 'Off · key saved',
    amplitudeLogButton: 'Dispatch log',
    amplitudeToggleEnableHint: 'Turn on',
    amplitudeToggleDisableHint: 'Turn off. The key will be kept',
    amplitudeNoRightsHint:
      'The Amplitude connection is managed by the project owner and admin',
    amplitudeStatusUnknown: 'We could not check the connection',
    amplitudeRetry: 'Retry',
    amplitudeReadOnlyHint:
      'Your role can view this setting but not change it. The key is never shown.',

    amplitudeKeyLabel: 'Project key',
    amplitudeKeyHint:
      'Settings → Projects → your project → General. You need the API Key, not the Secret Key.',
    amplitudeKeySaved: 'Key saved',
    amplitudeKeySavedTail: (tail: string) => `Key saved · …${tail}`,
    amplitudeKeyReplace: 'Replace',
    amplitudeRegionLabel: 'Region',
    // WEB-4116 (сверка вида 31.10, поломка 9): как у эталона AmpForm.
    amplitudeRegionUs: 'US',
    amplitudeRegionEu: 'EU',
    amplitudeRegionHint:
      'EU if your Amplitude address has .eu. in it (app.eu.amplitude.com). A wrong region silently loses events.',
    amplitudeEventsTitle: 'Events',
    amplitudeEventsCount: (chosen: number, total: number) =>
      `${chosen} of ${total}`,
    amplitudeEventsSelectAll: 'Select all',
    amplitudeEventsClearAll: 'Clear all',
    amplitudeEventOpen: 'Opened the funnel',
    amplitudeEventStart: 'Started the quiz',
    amplitudeEventStep: 'Quiz screen shown',
    amplitudeEventAnswer: 'Answered a question',
    amplitudeEventEmail: 'Left an email',
    amplitudeEventFinish: 'Reached the end of the quiz',
    amplitudeEventPaywall: 'Saw the payment screen',
    amplitudeEventPay: 'Paid',
    // WEB-2413 Д1: перечень собирается из подписей реестра
    // `AMPLITUDE_ALWAYS_EVENTS` (capiPixelCanon.ts), а не числом в тексте.
    amplitudeEventsAlways: (events: readonly string[]) =>
      `Always sent: ${
        events.length > 1
          ? `${events.slice(0, -1).join(', ')} and ${events[events.length - 1]}`
          : events.join('')
      }.`,
    amplitudeAlwaysPriceClick: 'plan click',
    amplitudeAlwaysCheckoutStart: 'checkout start',
    amplitudeAlwaysCheckoutFailed: 'failed payment',
    amplitudeAlwaysPaymentUnavailable: '"payment unavailable"',
    amplitudeAlwaysPaywallHandoff: 'handoff to your own checkout',
    amplitudeLogTitle: 'Dispatch log',
    // WEB-2413 п.16: причины строк журнала Amplitude — по статусу сервера
    // (`AMPLITUDE_DELIVERY_LOG_REASON`, amplitudeDeliveryLog.ts).
    amplitudeLogReasonAccepted: 'accepted by Amplitude',
    amplitudeLogReasonPartial: (ingested: string, sent: string) =>
      `Amplitude ingested ${ingested} of ${sent}: the rest was dropped — most likely the event type is blocked or strict schema is on in Amplitude`,
    amplitudeLogReasonRateLimited: 'Amplitude throttled the sending rate',
    amplitudeLogReasonRejected: 'Amplitude rejected the event',
    amplitudeLogReasonTransport:
      'Amplitude did not answer or answered with a failure',
    amplitudeLogReasonStale:
      'the event was lost: delivery was broken for more than a day',
    amplitudeLogReasonConsent: 'the visitor did not consent to analytics',
    amplitudeLogReasonNoConnection:
      'Amplitude is not connected in this project',
    amplitudeLogReasonDisabled: 'sending was switched off',
    amplitudeLogReasonProviderPaused:
      'sending to Amplitude is paused by the service — the event goes out once it resumes',
    amplitudeLogReasonEventDisabled:
      'this event is switched off in the connection settings',
    amplitudeLogReasonUnknownVisitor:
      'the visitor is not identified — such events are not forwarded',
    amplitudeLogReasonNotLive: 'service traffic (a speed check of the funnel)',
    amplitudeLogReasonTestMode: 'a test payment — it never goes to analytics',
    amplitudeLogReasonForeignPaywall:
      'a purchase from your own paywall — it is not sent to analytics',
    amplitudeLogResponse: (httpCode: string, code: string | null) =>
      code
        ? ` · response ${httpCode}, code ${code}`
        : ` · response ${httpCode}`,
    amplitudeLogTestMark: ' · test event',
    amplitudeLogAttempts: (attempts: number) => `attempts: ${attempts}`,
    // WEB-2119: the banner no longer prints the server's machine string and no
    // longer gives the same advice for every outcome. One phrase per outcome,
    // and «check the key and the region» only where that is true.
    amplitudeFailureRejectedPermanent:
      'Amplitude refused the events outright: the project API key does not fit the address we send to. Check the key and the region in the project settings — a key from a project in the other region is refused the same way as a wrong one.',
    amplitudeFailureTransportFailed:
      'We could not reach Amplitude at all — the request did not go through or its server answered with an error. There is nothing to fix on your side: we retry by ourselves for 24 hours.',
    amplitudeFailureRateLimited:
      'Amplitude is throttling us: it counts its limit per device, and one visitor produced events faster than it accepts them. The events are not lost — we are slowing down and retrying.',
    amplitudeFailurePartiallyAccepted:
      'Amplitude answered with a success but dropped the events after accepting the request. That happens when the event type is blocked in your project or strict schema mode is on.',
    // WEB-2412 п.8: отказ по полям НАШЕГО конверта — не «ключ не подходит».
    amplitudeFailureRejectedInvalidEvent:
      'Amplitude refused the request itself — not because of your project settings, but because of a defect in the data we send. This is on us; we already know and are looking into it. There is nothing to check on your side.',
    amplitudeFailureUnknown:
      'Amplitude is not accepting the events, and we could not tell its answer apart. Check the key and the region in the project settings.',
    amplitudeFailureCode: (code: string) => `Response code: ${code}.`,
    // WEB-3782: подписи плашки каркаса о поломке доставки сняты вместе с ней;
    // «Открыть настройки» читает строка блока «Требует внимания».
    deliveryAlertOpen: 'Open settings',
    amplitudeEventsNote:
      'Free-text answers and emails never leave us — only the fact of the event.',
    amplitudeServiceTitle: 'Service events',
    amplitudeServiceHint:
      'Smart-link clicks, app installs, traffic coming back.',
    amplitudeRevenueTitle: 'Payments as revenue',
    amplitudeRevenueHint: 'Payments land in Amplitude revenue reports.',
    amplitudeRevenueWarn:
      'If payments already reach this Amplitude from elsewhere, revenue will double.',
    amplitudeHonestStart: 'Data starts from the moment you connect.',
    amplitudeHonestSchema:
      'Strict schema mode on? Add our event names to your plan, or Amplitude drops them.',
    // WEB-2292 п.2: the line used to say renewals never arrive. Since WEB-2251
    // they do, from three doors — the checkout, the paid subscription invoice
    // and a server-side charge — and the client tells them apart by the
    // revenueType property. Saying otherwise was a straight lie in the form.
    amplitudeHonestRenewals:
      'Renewals arrive too — split revenue by the revenueType property.',
    amplitudeConnectButton: 'Connect',
    amplitudeCheckButton: 'Test',
    amplitudeWhyDisabled: 'Fill in the key and choose a region.',
    amplitudeCheckNeedsSaveChanges:
      'Save your changes first — the check runs on the saved connection settings, not on what is in the form.',
    amplitudeCheckRunning: 'Sending a test event…',
    amplitudeCheckOkTitle: 'The test event went to your project',
    amplitudeCheckOkText:
      'Look for the «[Funnel] Connection Check» event in your Amplitude within a minute. If it is not there, check whether this event type is blocked and whether strict schema mode is on.',
    amplitudeCheckPartialTitle: 'Amplitude accepted fewer events than we sent',
    amplitudeCheckPartialText:
      'The answer is a success, but part of the events was dropped. That usually means the event type is blocked or strict schema mode is on.',
    amplitudeCheckRejectedTitle: 'Amplitude refused the test event',
    amplitudeCheckRejectedText:
      'Check the key and the region: a key from another project or another region is the usual cause. We will not pretend everything is fine.',
    amplitudeCheckRateLimitedTitle: 'Amplitude asked us to slow down',
    amplitudeCheckRateLimitedText:
      'The connection itself works: Amplitude turned the test event down by its rate limit. Try again in a minute.',
    amplitudeCheckTransportTitle: 'We could not reach Amplitude',
    amplitudeCheckTransportText:
      'The request never reached Amplitude — this is about the connection, not about your key.',
    amplitudeCheckFailed: 'Could not send the test event',
    amplitudeConnectedToast: 'Amplitude connected',
    amplitudeSavedToast: 'Saved',
    amplitudeSaveFailed: 'Could not save the Amplitude connection',
    amplitudeDisableConfirmTitle: 'Turn Amplitude off?',
    amplitudeDisableConfirmOk: 'Turn off',
    amplitudeDisableConfirmBody:
      'Sending will stop. The key will be kept — you can turn it back on with the same switch, with nothing to retype.',
    amplitudeDisabledToast: 'Sending to Amplitude stopped',
    amplitudeEnabledToast: 'Sending to Amplitude resumed',
    amplitudeToggleFailed: 'Could not switch sending to Amplitude',
    // WEB-2580: отвязать насовсем — ссылка в форме «Настроить», ключ стирается.
    amplitudeRemoveKey: 'Detach and delete the key',
    amplitudeRemoveConfirmTitle: 'Detach Amplitude?',
    amplitudeRemoveConfirmBody:
      'The key and settings will be deleted and sending will stop. To connect again, you will need to enter the key anew.',
    amplitudeRemoveConfirmOk: 'Detach',
    amplitudeRemovedToast: 'Amplitude detached, key deleted',
    amplitudeRemoveFailed: 'Could not detach Amplitude',
    // WEB-3756 юнит 14: Amplitude на ките (эталон settings-k.jsx, Ads → Amplitude).
    // Подпись тумблера отправки в шапке группы — как у рекламных сетей эталона;
    // у переключателей служебных событий и выручки видимой подписи нет (WEB-4116:
    // amplitudeSwitchOn / amplitudeSwitchOff сняты), имя — название строки.
    // Сняты вместе с кнопкой «Настроить» и выпадашкой региона (п. 62 — раскрыто
    // сразу): amplitudeSetUpButton, amplitudeCollapseButton,
    // amplitudeToggleConnectHint, amplitudeToggleCollapseHint,
    // amplitudeRegionPlaceholder.
    amplitudeSendingOn: 'Sending on',
    amplitudeSendingOff: 'Off',

    // ── Журнал отправок в сеть (WEB-2219) ───────────────────────────────────
    // Одна шторка на провайдера, фильтр внутри. «Решили не слать» — наше
    // сознательное решение, а не поломка: красить его как ошибку нельзя.
    // WEB-3756 юнит 13: заголовок окна по эталону — без имени сети (параметр
    // остаётся: его передаёт capiDeliveryLogTitle).
    deliveryLogTitle: (_network: string) => 'Dispatch log',
    deliveryLogFilterAll: 'All',
    deliveryLogFilterDelivered: 'Delivered',
    deliveryLogFilterSkipped: 'Decided not to send',
    deliveryLogFilterFailed: 'Error',
    deliveryLogPeriodDay: 'Last 24 hours',
    deliveryLogPeriodWeek: 'Last 7 days',
    deliveryLogPeriodMonth: 'Last 30 days',
    deliveryLogRetention: 'The log is kept for 30 days.',
    // WEB-2305 / WEB-2413 п.1, WEB-2631: сроки — копии констант сервера
    // (api/capiDispatchAttempts.ts); отмена живёт столько же, сколько её покупка.
    capiDeliveryLogRetention: (days: number, purchaseDays: number) =>
      `The log shows deliveries for the last ${days} days. Sent purchases are kept for ${purchaseDays} days — they are needed to cancel a conversion on refund and for “first purchase only”; a cancellation is kept as long as its purchase, so that it is not sent to the network twice. Everything else is kept for ${days} days.`,
    deliveryLogOutcomeDelivered: 'Delivered',
    deliveryLogOutcomeSkipped: 'Decided not to send',
    deliveryLogOutcomeFailed: 'Error',
    // WEB-3756 сверка пикселей 1, М12: ключ склейки и попытки — отдельными строками.
    deliveryLogMatchKey: (key: string) => `match key ${key}`,
    deliveryLogAttemptCount: (n: number) => `Attempts: ${n}`,
    deliveryLogEmpty: 'There were no such deliveries in this period',
    deliveryLogMore: 'Show more',
    deliveryLogLoadFailed: 'Could not load the delivery log',
    deliveryLogEventUnnamed: 'Event without a name',
    deliveryLogAccepted: (httpCode: string) => `${httpCode} \u00b7 accepted`,
    deliveryLogRejected: (httpCode: string, code: string | null) =>
      code
        ? `${httpCode} \u00b7 the network refused (code ${code})`
        : `${httpCode} \u00b7 the network refused`,
    deliveryLogNoAnswer: 'the network did not answer',
    // WEB-2413 п.5: сеть ответила кодом 2xx, но приём не подтвердила.
    deliveryLogUnconfirmed: (httpCode: string) =>
      `${httpCode} \u00b7 the network did not confirm receipt, we will retry`,
    deliveryLogReasonTestMode: 'a test purchase — it never goes to ad networks',
    deliveryLogReasonErased: 'the person asked us to erase their data',
    deliveryLogReasonConsent: 'the visitor refused tracking',
    deliveryLogReasonConsentServerInferred:
      'consent was assumed by the sender, not given by the visitor',
    deliveryLogReasonConsentForeignProject:
      'consent was given in another project',
    deliveryLogReasonNoConsentBasis:
      'there is no record of this visitor consenting',
    deliveryLogReasonNetworkDeferred: 'this network is not supported yet',
    deliveryLogReasonNoConnection:
      'the network is not connected in this project',
    deliveryLogReasonFirstPurchaseOnly:
      '“only the first payment of a person” is chosen in the settings — a repeat payment is not sent',
    deliveryLogReasonNoIdentifier:
      'there is nothing to recognise the visitor by — the network would not accept the event',
    // WEB-2241 — решения по отмене конверсии (возврат, проигранный спор).
    deliveryLogReasonRefundsDisabled:
      '“send refunds to the ad network” is switched off in the settings',
    deliveryLogReasonRefundNotFull:
      'the refund is partial — only a full refund and a lost dispute are sent',
    deliveryLogReasonRefundScopeUnknown:
      'we could not tell whether the refund is full — the payment could not be read',
    deliveryLogReasonPurchaseNotDispatched:
      'the purchase itself was never sent to this network — there is nothing to cancel',
    // WEB-2249 — покупка из предпросмотра или тестовой версии воронки.
    deliveryLogReasonNotLive:
      'the payment came from a funnel preview or a test version — such a purchase is not sent to the ad network',
    // WEB-2407 пп.9, 1, 4 (пачка 49, U4 А3).
    deliveryLogReasonAlreadySent:
      'this event has already been delivered to this network — it is not sent a second time',
    // WEB-2592 (пачка 49): одна отмена на покупку и сеть.
    deliveryLogReasonRefundAlreadySent:
      'a cancellation of this purchase has already been delivered to this network (after another refund or dispute) — a second cancellation is not sent',
    deliveryLogFailureAwaitingToken: (retryHours: number, maxDays: number) =>
      `the access token has expired or was revoked — waiting for a new one; we retry every ${retryHours} h for up to ${maxDays} days, and after you replace the token the event is delivered by itself`,
    deliveryLogFailureAbandoned:
      'delivery stopped: the event was not delivered and will not be sent again automatically',
    deliveryLogReasonUnknown: (status: string) => `not sent (${status})`,
    // WEB-2304: строка про повтор у ошибки. Числа — аргументами, не текстом:
    // «из M» берётся из ответа сервера либо из копии константы очереди.
    deliveryLogRetryIn: (minutes: number, attempt: number, max: number) =>
      `retry in ${minutes} ${plural('en', minutes, { one: 'minute', other: 'minutes' })} (attempt ${attempt} of ${max})`,
    deliveryLogRetryPendingSince: (
      time: string,
      attempt: number,
      max: number,
    ) => `retry pending since ${time} (attempt ${attempt} of ${max})`,
    deliveryLogRetryPending: (attempt: number, max: number) =>
      `retry pending (attempt ${attempt} of ${max})`,
    // WEB-2583: от 90 минут ожидание называется в часах (вверх до целого часа).
    deliveryLogRetryInHours: (hours: number, attempt: number, max: number) =>
      `retry in ${hours} ${plural('en', hours, { one: 'hour', other: 'hours' })} (attempt ${attempt} of ${max})`,
    // WEB-2583: «ждёт нового токена» — попытка у этого исхода не расходуется,
    // поэтому без «attempt K of M»: только когда следующая отправка.
    deliveryLogTokenWaitInMinutes: (minutes: number) =>
      `waiting for a new token, next send in ${minutes} ${plural('en', minutes, { one: 'minute', other: 'minutes' })}`,
    deliveryLogTokenWaitInHours: (hours: number) =>
      `waiting for a new token, next send in ${hours} ${plural('en', hours, { one: 'hour', other: 'hours' })}`,
    deliveryLogTokenWaitPendingSince: (time: string) =>
      `waiting for a new token, next send pending since ${time}`,
    deliveryLogTokenWaitPending: 'waiting for a new token, next send pending',
    // WEB-2626: сколько раз событие откладывалось до замены токена (счётчик
    // отдаёт сервер, `tokenWaits`); дописывается к строке ожидания в скобках.
    deliveryLogTokenWaitCount: (waits: number) =>
      `(postponed ${plural('en', waits, { one: 'once', other: `${waits} times` })})`,
    deliveryLogFinal: 'finally not delivered',
    // WEB-2304 хвосты (F102): попытки одного события свёрнуты в одну строку.
    deliveryLogDeliveredOnAttempt: (attempt: number, max: number) =>
      `delivered on attempt ${attempt} of ${max}`,
    deliveryLogAttemptLine: (
      time: string,
      attempt: number,
      max: number,
      detail: string,
    ) => `${time} \u00b7 attempt ${attempt} of ${max} \u00b7 ${detail}`,
    // WEB-2466: вкладка «Команда» у гостя организации (позван только в
    // проекты) — состав организации сервер ему не отдаёт.
    orgTeamGuestNote:
      "The organization's owner and admins see its members. You were invited to projects of this organization — their team is below.",
  },
  {
    pageTitle: 'Личные настройки',
    tabAccount: 'Аккаунт',
    tabOrganization: 'Организация',

    profileUsernameLabel: 'Имя',
    profileUsernameEmpty: 'Имя не может быть пустым',
    profileAvatarLabel: 'Аватар',
    profileAvatarUrlInvalid: 'Невалидный URL — начните с https://',
    profileAvatarUrlPlaceholder: 'https://',
    profileAvatarUpload: 'Загрузить',
    profileAvatarUploaded: 'Аватар загружен',
    profileAvatarUploadFailed: 'Не удалось загрузить изображение',
    profileSaved: 'Сохранено',
    profileAvatarSaved: 'Аватар сохранён',
    profileSaveFailed: 'Не удалось сохранить профиль',

    pwdRuleLen: 'Минимум 8 символов',
    pwdRuleUpper: 'Заглавная латинская буква (A–Z)',
    pwdRuleLower: 'Строчная латинская буква (a–z)',
    pwdRuleDigit: 'Цифра',
    pwdRuleSpecial: 'Спецсимвол (например ! @ # $ %)',

    pwdChangeTitle: 'Смена пароля',
    pwdAddTitle: 'Добавить пароль',
    pwdTooManyAttempts: 'Слишком много попыток. Попробуйте позже.',
    pwdCurrentWrong: 'Неверный текущий пароль.',
    pwdCodeInvalid: 'Неверный или просроченный код.',
    pwdGenericError: 'Произошла ошибка. Попробуйте ещё раз.',
    pwdGoogleOnlyHint:
      'Вы входите через Google — пароля нет. Добавьте пароль, чтобы входить по email и выполнять опасные действия.',
    pwdCodeSentTo: (email: string) => `Код отправлен на ${email}`,
    pwdCurrentLabel: 'Текущий пароль',
    pwdEmailCodeLabel: 'Код из письма',
    // WEB-3758 заход 28: кнопка отправки кода в окне пароля (эталон me-k.jsx стр. 47)
    pwdSendCode: 'Отправить код на email',
    pwdSixDigits: '6 цифр',
    pwdNewLabel: 'Новый пароль',
    pwdPasswordLabel: 'Пароль',
    pwdRepeatLabel: 'Повторите пароль',
    pwdMismatch: 'Пароли не совпадают',
    pwdChangeSubmit: 'Сменить пароль',
    pwdChangedToast: 'Пароль изменён',
    pwdAddedToast: 'Пароль добавлен',
    pwdChangeViaEmail: 'Сменить через email',

    twoFactorTitle: 'Двухфакторная аутентификация',
    twoFactorDisable: 'Отключить 2FA',
    twoFactorEnable: 'Включить 2FA',
    twoFactorSetupTitle: 'Настройка 2FA',
    twoFactorDisabledToast: 'Двухфакторная аутентификация отключена',
    twoFactorDisableFailed: 'Не удалось отключить двухфакторную аутентификацию',
    twoFactorRegenFailed: 'Не удалось обновить резервные коды',
    twoFactorDisableConfirm: 'Отключить',
    twoFactorDisableHint: 'Для отключения введите пароль и текущий код:',
    twoFactorRegenTitle: 'Обновление резервных кодов',
    twoFactorRegenOk: 'Обновить',
    twoFactorRegenHint:
      'Введите текущий код 2FA. Это аннулирует ваши предыдущие резервные коды.',
    twoFactorDisableHintRecovery:
      'Для отключения введите один из резервных кодов:',
    twoFactorRegenHintRecovery:
      'Введите один из резервных кодов. Это аннулирует ваши предыдущие резервные коды.',
    twoFactorUseRecovery: 'У меня резервный код',
    twoFactorUseCode: 'Ввести код подтверждения',
    twoFactorRecoveryPlaceholder: 'xxxxx-xxxxx',
    twoFactorSendCode: 'Прислать код на почту',
    twoFactorCodeSentTo: (email: string) => `Код отправлен на ${email}`,
    twoFactorSendCodeFailed: 'Не удалось отправить код',
    twoFactorCodeRequired: 'Введите код',
    twoFactorRecoveryRequired: 'Введите резервный код',
    twoFactorStepMethod: 'Метод',
    twoFactorStepQr: 'QR-код',
    twoFactorStepConfirm: 'Подтверждение',
    // WEB-2438 Ж9: по-русски, как остальные строки — «резервный код».
    twoFactorStepRecovery: 'Резервные коды',
    twoFactorSetupFailed: 'Не удалось начать настройку 2FA',
    twoFactorWrongCode: 'Неверный код',
    twoFactorMethodTotp: 'Приложение-аутентификатор (TOTP) — рекомендуем',
    twoFactorMethodEmail: 'Код на email',
    twoFactorConfirmPasswordHint:
      'Подтвердите пароль, чтобы включить второй фактор.',
    twoFactorGoogleHint:
      'Вы входите через Google — подтвердите, что это ваш аккаунт.',
    twoFactorGoogleConfirmed: 'Google-аккаунт подтверждён',
    twoFactorGoogleFailed: 'Не удалось подтвердить через Google',
    twoFactorGoogleUnavailable:
      'Не удалось загрузить вход через Google — обратитесь в поддержку, чтобы включить двухфакторную защиту.',
    twoFactorManualKey: 'Или введите ключ вручную:',
    twoFactorEmailCodesTo: (email: string) =>
      `Коды будут приходить на ${email}.`,
    twoFactorFinish: 'Готово — коды сохранил',

    // ── Резервные коды (WEB-3758 заход 28, возврат: кнопки кодов — решение владельца 27.10) ──
    recoveryCopied: 'Резервные коды скопированы',
    recoveryCopyFailed: 'Не удалось скопировать резервные коды',
    recoveryShownOnceTitle: 'Показываются один раз',
    recoveryShownOnceText:
      'Сохраните коды в надёжном месте — каждый работает один раз.',
    recoveryCopyAll: 'Скопировать все',
    recoveryDownload: 'Скачать .txt',

    dangerZoneTitle: 'Опасная зона',
    accountDeleteHeading: 'Удалить аккаунт',
    accountDeleteText:
      'Аккаунт, организации без других владельцев и их проекты будут удалены безвозвратно.',
    accountDeleteNoPasswordTip: 'Сначала добавьте пароль (вкладка выше)',
    accountDeleteConfirmTitle: 'Удалить аккаунт?',
    accountDeleteConfirmBody:
      'Аккаунт и организации без других владельцев (со всеми проектами) будут удалены. Действие необратимо.',
    accountDeletedToast: 'Аккаунт удалён',

    projectSettingsTitle: 'Настройки проекта',
    tabGeneral: 'Основные',
    tabMembers: 'Участники',
    tabPayments: 'Платежи',
    tabIntegrations: 'Аналитика и пиксели',
    tabContext: 'Контекст',
    tabAppConnection: 'Подключение приложения',
    tabKeysWebhooks: 'Ключи и вебхуки',
    generalReadOnly: 'Ваша роль позволяет только просматривать настройки',
    bridgeConfigLoadError: 'Не удалось загрузить адреса магазинов. Поля заблокированы',
    projectNameLabel: 'Название проекта',
    projectNameRequired: 'Укажите название проекта',
    projectNamePlaceholder: 'Мой проект',
    storeUrlInvalid: 'Невалидная ссылка',
    storeFallbackHint:
      'Стор-фолбэк: сюда уходят посетители, если воронка выключена или сломана',
    projectSaved: 'Проект сохранён',
    storeUrlSaveFailed: 'Не удалось сохранить адреса магазинов.',
    nameSavedStoreFailed: (details: string) => `Название сохранено. ${details}`,
    settingsSaveFailed: 'Не удалось сохранить настройки',
    deleteProject: 'Удалить проект',
    deleteCascadeList: (quizzes: number, paywalls: number, invites: number) =>
      `${quizzes} ${plural('ru', quizzes, { one: 'квиз', few: 'квиза', many: 'квизов' })}, ` +
      `${paywalls} ${plural('ru', paywalls, { one: 'пейволл', few: 'пейволла', many: 'пейволлов' })}, ` +
      `${invites} ${plural('ru', invites, { one: 'инвайт', few: 'инвайта', many: 'инвайтов' })}`,
    deleteProjectCascade: (list: string) => `Будут удалены: ${list}. `,
    deleteProjectGeneric:
      'Удаление проекта необратимо — все воронки, квизы и пейволлы будут удалены. ',
    deleteProjectDomainsNote:
      'Оплаченные домены сохранятся на уровне организации.',
    // WEB-2438 Ж8: и владелец организации.
    deleteProjectTip:
      'Удалить проект может владелец проекта, владелец или администратор организации',
    membersCardTitle: 'Участники проекта',
    inviteButton: 'Пригласить',
    activeInvitesTitle: 'Активные приглашения',
    membersReadOnly:
      'Участник и ретеншн-менеджер не управляют командой — список только для чтения',
    inviteLinkCopied: 'Ссылка-приглашение скопирована',
    projectSettingsTabsLabel: 'Разделы настроек',
    // WEB-3756 юнит 6 — см. английскую половину выше.
    payListSystems: 'Платёжные системы',
    payListSoon: 'Скоро',
    payListRules: 'Правила',
    payStripeNoteNone: 'не подключён',
    payStripeNoteSandbox: 'песочница',
    payStripeNoteLive: 'боевой режим',
    payEnvRow: 'Среда',
    payConnectLead:
      'Вставьте ключи из кабинета Stripe. Хотите начать с тестовых — введите здесь пару pk_test_ и sk_test_.',
    payWebhookEventsBtn: (count: number) => `События · ${count}`,
    payEventsTitle: 'События Stripe',
    // WEB-3756 юнит 9 — см. английскую половину выше.
    payJournalOpen: 'Открыть журнал',
    // WEB-3756 юнит 8 — см. английскую половину выше.
    payUnbindAllQuestion: (count: number) =>
      `Что сделать с ${count} ${plural('ru', count, { one: 'активной подпиской', few: 'активными подписками', many: 'активными подписками' })}?`,
    payUnbindAllKeysLine:
      'Отключатся боевые и тестовые ключи, разовые покупки погаснут.',
    payUnbindChoiceLabel: 'Что сделать с подписками',
    // WEB-3756 доводка 1 — см. английскую половину выше.
    payUnbindBranchNowTitle: 'Отменить сейчас',
    payUnbindBranchNowText: 'Доступ закроется сразу.',
    payUnbindBranchEndTitle: 'Отменить в конце периода',
    payUnbindBranchEndText: 'Доступ — до конца оплаченного.',
    payUnbindBranchKeepTitle: 'Оставить в Stripe',
    payUnbindBranchKeepText: 'Списания идут, доступа нет.',
    payUnbindKeepWarnTitle: 'Отменить подписки придётся в Stripe самим',
    payGraceBothSaved: 'Сохранено',
    // WEB-2027: `paymentsInfoTitle` / `paymentsInfoText` сняты — см. пару выше.
    // WEB-2121: пара снята — см. английскую половину выше.
    subtabDev: 'Для разработчика',
    subtabSdkLogs: 'Логи SDK',
    // WEB-1198, блок 1 — тексты дословно из задания 24 §5.0.
    subtabMain: 'Основное',
    acFirstVisitTitle:
      'Кто в вашем приложении решает, что у человека есть подписка?',
    acPathLibTitle: 'Наша библиотека в приложении',
    acPathLibTech: 'SDK',
    acPathLibDesc: 'Разработчик встраивает наш код (SDK)',
    acPathServerTitle: 'Собственный сервер',
    acPathServerDesc: 'Ваш бэкенд спрашивает нас по ключу',
    acPathProviderTitle: 'Подписки живут в стороннем сервисе',
    acPathProviderDesc: 'Adapty, RevenueCat и подобные',
    // WEB-2126: четвёртый путь. «Приложения нет» — неправда: приложение у
    // клиента вполне может быть, он просто не связывает его с квизами.
    acPathNoneTitle: 'Приложение не подключаю',
    acPathNoneDesc: 'Квизы и приложение живут отдельно',
    acPathLineLead: 'Ваш путь:',
    acPathLineChange: 'Сменить',
    acDevStatusNoBridgeTitle: 'Чем забрать данные к себе',
    acDevStatusNoBridgeText:
      'Ключ для запросов с вашего сервера, события на ваш адрес и отчёты в рекламный трекер.',

    // WEB-1198, исполнитель A: ствол подвкладки «Основное» (блоки 2, 3, 4, 5, 8).
    // Тексты дословно из задания 24 §5.1–5.4 и §5.6.
    acMainStatusDemoText:
      'Приложение получает ответ «подписка есть» на любой запрос — настоящие покупки не читаются.',
    acMainStatusDemoOff: 'Выключить',
    acMainStatusDemoOffPending: 'Выключится после сохранения настроек.',
    acMainStatusNever: 'Приложение ещё не обращалось к нам.',
    acMainStatusOkText: (when: string) =>
      `Приложение сообщило об установке: ${when}`,
    acMainStatusWarnTitle: 'Чего не хватает для полной связки:',
    acMainMissingIos: 'Ссылка на App Store',
    acMainMissingAndroid: 'Ссылка на Google Play',
    acMainMissingSecret: 'Секрет подписи не выпущен',
    acMainPathNameAll: 'Не выбран',
    acMainFailText:
      'Сохранение выключено, чтобы не затереть уже сохранённые значения.',
    acMainCopy: 'Копировать',
    acMainCopied: 'Скопировано',
    acMainConnectTitle: 'Подключение',
    acMainProjectIdLabel: 'ID проекта',
    acMainApiBaseLabel: 'Адрес нашего API',
    acMainSecretLabel: 'Секрет подписи',
    acMainSecretIssuedValue: 'выпущен · значение показывали один раз',
    acMainSecretNotIssuedValue: 'не выпущен',
    acMainSecretIssue: 'Выпустить',
    acMainSecretRotate: 'Перевыпустить',
    acMainSecretNotNeeded:
      'Секрет подписи при вашем способе не нужен — профиль вендора приходит к нам без подписи.',
    acMainSecretIssuedUnused:
      'Секрет подписи выпущен, но при вашем способе не используется.',
    acMainSecretIssueAnyway: 'Всё равно выпустить',
    acMainSecretShow: 'Показать',
    acMainSecretForeignHint:
      'Не нужно при вашем способе: профиль вендора приходит к нам без подписи. Понадобится, только если захотите искать покупателя по почте со своей стороны.',
    acMainShowCode: 'Показать код',
    acMainDlgClose: 'Закрыть',
    acMainDlgCodeTitle: 'Код для приложения',
    acMainDlgCodeHint: 'Одна строка при запуске приложения:',
    acMainDlgSecretNewTitle: 'Секрет подписи выпущен',
    acMainDlgSecretAgainTitle: 'Секрет перевыпущен',
    acMainDlgSecretHint:
      'Покажем один раз. Сохраните его на своём сервере — второй раз мы его не покажем.',
    acMainDlgSecretGrace: `Старый секрет ещё поработает ${GRACE_HOURS_RU}, чтобы приложение успело обновиться.`,
    acMainDlgSecretOk: 'Я сохранил секрет — закрыть',
    acMainStoresTitle: 'Куда ведёт кнопка после оплаты',
    acMainIosLabel: 'App Store',
    acMainAndroidLabel: 'Google Play',
    acMainIosPlaceholder: 'https://apps.apple.com/app/id…',
    acMainAndroidPlaceholder: 'https://play.google.com/store/apps/details?id=…',
    acMainWebPayTitle: 'Оплата из приложения',
    acMainWebPayLabel: 'Адрес вашей страницы оплаты',
    acMainWebPayFieldHint:
      'Опубликованная страница с ценами. Пусто — ссылку не отдаём, ведёте к оплате сами.',
    acMainWebPayPlaceholder: 'https://pay.example.com/offer',
    acMainSave: 'Сохранить настройки',
    acMainLeaveTitle: 'Настройки изменены, но не сохранены',
    acMainLeaveBody:
      'Вы изменили настройки подключения и не сохранили их. Сохранить изменения перед уходом?',
    acMainLeaveSave: 'Сохранить',
    acMainLeaveDiscard: 'Не сохранять',
    acMainLeaveStay: 'Остаться',
    acMainSaveFailed: 'Не удалось сохранить настройки',
    // WEB-3757 юнит 2 — «Основное» на ките, тексты эталона settings-app.jsx
    // (спек-репо ce823f8f, стр. 121–177 и окна стр. 392–425); замок тарифа — без
    // названия тарифа (решение владельца 27.10 № 8, как у «Контекста» и A/B).
    acMainBarTestTitle: 'Включён режим проверки',
    acMainBarOkTitle: 'Связка работает',
    acMainLockText: 'Подключение приложения недоступно на вашем тарифе',
    acMainLockPay: 'Оплатить тариф',
    // WEB-1168: условие сборщика ссылки установки — нет пакета Android и пригодного трекера.
    acMainInstallLinkUnbuildable:
      'Ссылка установки не собирается: не задано имя пакета Android и не выбран трекер — покупатель получит только письмо',
    // WEB-2458 п.4: у кого нет права на деньги — текст вместо кнопки тарифа.
    acTierAskOwner:
      'Тариф меняет владелец или администратор организации — перешлите им эту страницу',
    acMainLoadFailTitle: 'Не удалось загрузить настройки',
    acMainLeaveLoadFail:
      'Не удалось загрузить настройки. Сохранение выключено, чтобы не затереть уже сохранённые значения.',
    acMainSecretHelp:
      'Им ваш сервер подписывает запросы к нам. Без подписи мы не отдадим покупателя по почте — защита от чужого доступа. В коде приложения секрет не оставляют: оттуда его достанут.',
    acMainGuideBtn: 'Инструкция для разработчика',
    acMainGuideLead:
      'Готовый текст. Скопируйте и передайте разработчику — почтой, в мессенджере, задачей в трекере, как вам удобнее.',
    acMainGuideText: (g: AcMainGuide) =>
      'Привет! Настраиваю оплату в браузере с выдачей доступа в приложении (web2app).\n' +
      'От тебя один шаг — подключить библиотеку и один раз проверить ответ.\n\n' +
      `ID проекта: ${g.projectId}\nАдрес API:  ${g.apiBase}\n\n` +
      `Одна строка при запуске приложения:\n  ${g.configure}\n\n` +
      `Проверка доступа — один GET, без ключа:\n  ${g.entitlementUrl}\n` +
      'Доступ есть, если среди grants найдётся хотя бы один со status: "active".\n' +
      'GUID подставляет сама библиотека.\n\n' +
      `Библиотеки: iOS ${g.ios} · Android ${g.android}\nДокументация: ${g.docsUrl}\n\n` +
      'Секрет подписи пришлю отдельно — он нужен, только если запросы пойдут с нашего сервера.',
    acMainGuideNoSecretTitle: 'Секрета подписи в тексте нет намеренно.',
    acMainGuideNoSecretText:
      'Он открывает доступ к данным покупателей, поэтому в переписке ему не место — передайте его отдельно и не тем же каналом.',
    acMainGuideCopy: 'Скопировать текст',
    acMainGuideCopied: 'Текст скопирован',
    acMainCodeVersions: (ios: string, android: string) =>
      `Версии библиотеки: iOS ${ios} · Android ${android}`,

    // Q107 (решение владельца 06.09, WEB-1924): роли по-русски; коды ролей в
    // API прежние, меняются только подписи.
    roleProjectOwner: 'Владелец',
    roleProjectAdmin: 'Администратор',
    roleProjectMember: 'Участник',
    roleRm: 'Ретеншн-менеджер',
    roleOrgOwner: 'Владелец',
    roleOrgAdmin: 'Администратор',
    roleOrgMember: 'Участник проекта',

    memberRoleChanged: (_name: string, _role: string) => 'Роль изменена',
    memberRoleChangeFailed: 'Не удалось обновить роль',
    memberRemoved: 'Участник удалён',
    memberRemoveFailed: 'Не удалось удалить участника',
    ownerNowIs: (_name: string) => 'Владение передано',
    transferFailed: 'Не удалось передать владение',
    leftProject: 'Вы покинули проект',
    leaveFailed: 'Не удалось покинуть проект',
    colMember: 'Имя',
    tagYou: 'вы',
    colRole: 'Роль',
    // WEB-1918: пометка рядом с ролью и пояснение к ней.
    roleOrgOwnerNote: 'владелец организации',
    roleOrgOwnerHint:
      'Роль в проекте указана прямая, но владельческие действия доступны, потому что человек владеет организацией, которой принадлежит проект.',
    colJoined: 'Добавлен',
    ownerRowTooltip: 'Владельца нельзя изменить на этой строке',
    leaveButton: 'Покинуть',
    makeOwner: 'Сделать владельцем',
    removeMemberConfirmTitle: (name: string) => `Удалить ${name} из проекта?`,
    removeMemberConfirmBody: 'Доступ к проекту будет отозван сразу.',
    transferConfirmTitle: (name: string) =>
      `Передать владение проектом: ${name}?`,
    transferConfirmBody: (_name: string) => 'Вы станете администратором проекта.',
    // WEB-3756 юнит «участники»: нажал владелец организации, не владелец проекта.
    transferConfirmBodyOther: (owner: string) =>
      `${owner} станет администратором проекта. Ваша роль не изменится.`,
    transferConfirmBodyOtherUnknown:
      'Нынешний владелец станет администратором проекта. Ваша роль не изменится.',
    leaveConfirmTitle: 'Покинуть проект?',
    leaveConfirmBody: 'Вернуться можно будет только по новому приглашению.',
    // WEB-3756 юнит 5a
    colMemberEmail: 'Почта',
    transferConfirmGo: 'Передать',
    memberActions: 'Действия',
    membersLoadFailed: 'Не удалось загрузить участников',
    // WEB-3756 юнит 5a, сверка вида круг 1: пункт меню и кнопка окна удаления.
    memberRemove: 'Удалить',

    inviteForever: 'Бессрочно',
    inviteStatusActive: 'Активно',
    inviteExpired: 'Истёк',
    inviteUsedOf: (used: number, max: number) => `${used} из ${max}`,
    inviteNoLimit: 'без лимита',
    inviteRevokedToast: 'Приглашение отозвано',
    inviteRevokeFailed: 'Не удалось отозвать приглашение',
    inviteDeletedToast: 'Приглашение удалено',
    inviteDeleteFailed: 'Не удалось удалить приглашение',
    inviteResentToast: 'Письмо отправлено повторно',
    inviteResendFailed: 'Не удалось отправить письмо. Попробуйте позже.',
    colInviteKind: 'Способ',
    inviteKindLink: 'Ссылка',
    colInviteUsed: 'Использовано',
    colInviteExpires: 'Действует до',
    colInviteStatus: 'Статус',
    inviteStatusRevoked: 'Отозван',
    inviteDeliveryFailedTip:
      'Письмо не дошло до получателя. Отправьте его повторно.',
    inviteStatusNotDelivered: 'Не доставлено',
    inviteResend: 'Отправить повторно',
    inviteCopy: 'Копировать',
    inviteRevoke: 'Отозвать',

    inviteTtl1d: '1 день',
    inviteTtl7d: '7 дней',
    inviteTtl30d: '30 дней',
    inviteLinkCreated: 'Ссылка-приглашение создана',
    inviteLinkCreateFailed: 'Не удалось создать ссылку-приглашение',
    inviteLimitHint: (max: number, count: number) =>
      `До ${max} адресов за раз, введено ${count}`,
    // WEB-3756 юнит 5b: текст макета; параметр лишних остаётся, не используется.
    inviteOverLimitError: (max: number, _excess: number) =>
      `Больше ${max} адресов`,
    inviteInvalidEmails: (list: string) => `Некорректные email-адреса: ${list}`,
    inviteSentCount: (delivered: number) => `Отправлено ${delivered}`,
    inviteMailFailedPart: (list: string) =>
      `письмо не доставлено: ${list} — можно отправить повторно из таблицы приглашений`,
    inviteSkippedPart: (list: string) =>
      `не отправлено: ${list} — подробности в таблице приглашений`,
    inviteSendFailed: 'Не удалось отправить приглашения',
    inviteModalTitle: (project: string) => `Пригласить в проект «${project}»`,
    inviteFieldTtl: 'Срок действия',
    inviteFieldMaxUses: 'Макс. использований',
    inviteCreateLink: 'Создать ссылку',
    inviteFieldRecipients: 'Кому (можно несколько)',
    inviteFieldRoleForAll: 'Роль для всех',
    inviteSend: 'Отправить',

    apiKeyRevoked: 'отозван',
    apiKeyActive: 'активен',
    apiKeyGraceUntil: (until: string) => `дожитие до ${until}`,
    apiKeyGraceTooltip:
      'Ключ ротирован: работает до конца окна дожития, потом перестанет',
    apiKeyRevokeNow: 'Отозвать сейчас',
    apiKeyNameRequired: 'Укажите имя ключа',
    apiKeyIssueFailed: 'Не удалось выпустить ключ',
    apiKeyRotateFailed: 'Не удалось ротировать ключ',
    apiKeyRevokedToast: 'Ключ отозван',
    apiKeyRevokeFailed: 'Не удалось отозвать ключ',
    apiKeyCopied: 'Ключ скопирован',
    colName: 'Имя',
    colPrefix: 'Ключ',
    apiKeyModeTest: 'тест',
    colCreated: 'Создан',
    colLastUsed: 'Последний запрос',
    colActions: 'Действия',
    apiKeyRotate: 'Ротировать',
    reloadOrTryLater: 'Обновите страницу или попробуйте позже.',
    apiKeyCreate: 'Создать ключ',
    apiKeyCreateTitle: 'Создать ключ доступа',
    apiKeyNamePlaceholder: 'Прод-бэкенд',
    apiKeyModeLabel: 'Режим',
    apiKeyModeLive: 'Боевой',
    apiKeyModeTestRadio: 'Тестовый',
    apiKeyRevealTitle: 'Новый ключ',
    apiKeyRevealClose: 'Я сохранил ключ — закрыть',
    apiKeyCopyAria: 'Скопировать ключ',
    apiKeyRevokeConfirmTitle: 'Отозвать ключ сейчас?',
    apiKeyRotateConfirmTitle: 'Ротировать ключ?',
    apiKeyRevokeConfirmOk: 'Да, продолжить',
    apiKeyRotateConfirmOk: 'Да, ротировать',
    apiKeyRevokeConfirmBody:
      'Ключ мгновенно перестанет работать. Действие необратимо. Это то, что нужно сделать, если ключ утёк — не дожидаясь конца окна дожития.',
    apiKeyRotateConfirmBody:
      `Новый ключ покажем один раз. Старый работает ещё ${KEY_GRACE_HOURS_RU}.`,

    webhookSecretIssueFailed: 'Не удалось выпустить секрет',
    webhookSecretCopied: 'Секрет скопирован',
    webhookSecretStatusLoadFailed: 'Не удалось загрузить статус секрета.',
    webhookSecretTitle: 'Подпись входящих событий',
    webhookSecretHint:
      'Секрет для HMAC-подписи входящих server-to-server запросов. Project ID — публичный идентификатор, секрет храните только на вашем сервере.',
    webhookSecretConfigured: 'Секрет подключён',
    webhookSecretRotatedAt: (moment: string) => `Последняя ротация: ${moment}`,
    webhookSecretNotConfigured: 'Секрет не настроен',
    webhookSecretRotate: 'Ротировать секрет',
    webhookSecretIssue: 'Выпустить секрет',
    webhookSecretReadOnlyNote:
      'Выпустить или ротировать секрет может только владелец или администратор проекта.',
    webhookSecretModalClose: 'Я сохранил секрет — закрыть',
    webhookSecretSaveNowTitle: 'Сохраните секрет сейчас',
    webhookSecretCopyAria: 'Скопировать секрет',

    postbackSaved: 'Постбэк сохранён',
    postbackSaveFailed: 'Не удалось сохранить постбэк',
    postbackHintTxid:
      'Добавьте {txid}: при повторе постбэка после таймаута он не меняется, и сеть отбросит дубль.',
    postbackNoTxidWarning:
      'В адресе нет {txid}. Без номера операции партнёр не сможет склеить повтор постбэка после таймаута, возможны дубли. Сохранить адрес всё равно можно.',
    postbackUrlInvalid: 'Укажите корректный http/https URL',

    integrationDataTitle: 'Данные интеграции',
    integrationDataHint:
      'Нужны для S2S-запросов вашего бэкенда и настройки SDK (см. раздел «Аутентификация» в документации). Это публичные значения — секрет для подписи выпускается ниже.',
    integrationApiBaseLabel: 'Базовый URL API (FUNNEL_API_BASE)',
    integrationProjectIdLabel: 'ID проекта (FUNNEL_PROJECT_ID)',

    graceDays: (days: number) =>
      `${days} ${plural('ru', days, { one: 'день', few: 'дня', many: 'дней' })}`,
    graceSaved: 'Льготный период сохранён',
    graceSaveFailed: 'Не удалось сохранить льготный период',
    graceTitle: 'Льготный период после сбоя оплаты',
    graceRbac: 'Изменять эту настройку может владелец проекта',
    graceHint: (defaultDays: string) =>
      `Сколько дней покупатель сохраняет доступ к купленному, если очередное списание по подписке не прошло — карта отклонена, не хватило средств и так далее. По умолчанию ${defaultDays}: столько действует срок у проектов, где поле не заполняли.`,
    graceFieldLabel: 'Дней льготного периода',
    graceFieldExtra: (min: number, max: number, defaultDays: string) =>
      `Целое число от ${min} до ${max}. Оставьте поле пустым, чтобы вернуть значение по умолчанию (${defaultDays}).`,
    graceFieldError: (min: number, max: number) =>
      `Целое число от ${min} до ${max} либо пусто`,
    graceFieldPlaceholder: (defaultDays: number) =>
      `по умолчанию (${defaultDays})`,

    trialOnceOnToast: 'Пробный период выдаётся только один раз',
    trialOnceOffToast: 'Повторный пробный период разрешён',
    trialOnceSaveFailed: 'Не удалось сохранить настройку',
    trialOnceTitle: 'Пробный период',
    trialOnceSwitchLabel: 'Давать пробный период только один раз',
    trialOnceHint:
      'Покупатель, уже бравший пробный период в этом проекте, при повторной подписке оплачивает сразу. Защищает от бесплатного доступа по кругу «оформил — отменил — оформил снова».',

    s2sRefundOnToast: 'Серверные возвраты включены',
    s2sRefundOffToast: 'Серверные возвраты выключены',
    s2sRefundToggleFailed: 'Не удалось изменить настройку возвратов',
    s2sRefundTitle: 'Серверные возвраты',
    s2sOwnerAdminOnly: 'Изменять эту настройку могут Owner и Admin',
    s2sBillingAccessRequired:
      'Платёжные данные вам недоступны — доступ к ним выдаёт владелец организации',
    s2sRefundWarningTitle:
      'Включение разрешает возвращать деньги по серверному ключу',
    s2sRefundWarningBefore:
      'Пока переключатель выключен, запрос возврата отклоняется любым ключом. После включения ваш серверный ключ (',
    s2sRefundWarningAfter:
      ') получает право возвращать покупателям деньги и снимать выданный доступ. Утёкший ключ = обнуление выручки, поэтому выключение переключателя работает как стоп-кран: право снимается со всех ключей проекта сразу. Право на возврат отдельно от права на списание — включение возвратов НЕ разрешает списания, и наоборот.',
    s2sRefundSwitchLabel: 'Разрешить серверные возвраты',
    // WEB-2002 (формулировка владельца, решение Р6 от 12.09.2026): переключатель
    // относится только к серверному API — на кнопки «Вернуть» в кабинете он
    // никогда не влиял, но подпись рядом с ним читалась так, будто влияет.
    s2sRefundCabinetUnaffectedHint:
      'На кнопки «Вернуть» в кабинете не влияет — вы возвращаете всегда.',

    s2sSubscriptionManageOnToast: 'Управление подписками через API включено',
    s2sSubscriptionManageOffToast: 'Управление подписками через API выключено',
    s2sSubscriptionManageToggleFailed:
      'Не удалось изменить настройку управления подписками',
    s2sSubscriptionManageTitle: 'Управление подписками через API',
    s2sSubscriptionManageWarningTitle:
      'Ваши серверные ключи смогут управлять подписками покупателей',
    s2sSubscriptionManageWarningText:
      'Право покрывает остановку продления, постановку на паузу, возобновление и выпуск ссылки на страницу управления подпиской. Утёкший ключ может применить это против покупателя, поэтому выключение переключателя работает как стоп-кран: право снимается со всех ключей проекта сразу.',
    s2sSubscriptionManageSwitchLabel:
      'Разрешить вашему серверу управлять подписками покупателей',

    moneyFractionMessage: 'Не более двух знаков после запятой',
    moneyZeroDecimalFraction:
      'У валюты ваших цен нет дробной части — укажите целую сумму',
    countRangeMessage: (min: string, max: string) =>
      `Целое число от ${min} до ${max} либо пусто`,
    moneyRangeMessage: (min: string, max: string) =>
      `Сумма от ${min} до ${max} либо пусто`,
    s2sAmountRangeHint: (min: string, max: string) =>
      ` Допустимо от ${min} до ${max}.`,
    moneyPrecisionHintZero: ' Дробной части у этой валюты нет.',
    moneyPrecisionHintTwo: ' Не более двух знаков после запятой.',
    s2sMaxAmountExtraCurrency: (
      currencyLabel: string,
      example: string,
      moneyExample: string,
      defaultMoney: string,
    ) =>
      `Сумма в ${currencyLabel} — валюте ваших цен. Например, ${example} = ${moneyExample}. Пусто — встроенный консервативный дефолт (${defaultMoney}).`,
    s2sMaxAmountExtraNeutral:
      'Сумма в главных единицах валюты ваших цен, а не в сотых долях. Пусто — встроенный консервативный дефолт (100 в валюте ваших цен).',
    s2sProjectDailyAmountExtraCurrency: (
      currencyLabel: string,
      defaultMoney: string,
    ) =>
      `Все покупатели вместе, сумма в ${currencyLabel}. Пусто — по умолчанию ${defaultMoney} в сутки.`,
    s2sProjectDailyAmountExtraNeutral:
      'Все покупатели вместе, сумма в главных единицах валюты ваших цен. Пусто — по умолчанию 5 000 в сутки в валюте ваших цен.',
    s2sMixedCurrenciesNote:
      ' У проекта цены в нескольких валютах — потолок общий на все, суммы складываются как есть.',
    s2sChargeOnToast: 'Серверные списания включены',
    s2sChargeOffToast: 'Серверные списания выключены',
    s2sChargeToggleFailed: 'Не удалось изменить настройку списаний',
    s2sLimitsSaved: 'Ограничения списаний сохранены',
    s2sLimitsSaveFailed: 'Не удалось сохранить ограничения',
    s2sChargeTitle: 'Серверные списания с сохранённых карт',
    s2sChargeWarningTitle:
      'Включение разрешает списания с сохранённых карт покупателей',
    s2sChargeWarningBefore:
      'Пока переключатель выключен, запрос списания отклоняется любым ключом. После включения ваш серверный ключ (',
    s2sChargeWarningAfter:
      ') получает право списывать деньги с сохранённых карт ваших покупателей без их участия. Утёкший ключ = чужие списания, поэтому держите ограничения ниже как можно строже, а выключение переключателя работает как стоп-кран: право снимается со всех ключей проекта сразу.',
    s2sChargeSwitchLabel: 'Разрешить серверные списания',
    s2sMaxAmountLabel: 'Потолок одной операции',
    s2sDefaultPlaceholder: 'по умолчанию',
    s2sDailyLimitLabel:
      'Потолок числа успешных списаний на покупателя за сутки',
    s2sDailyLimitExtra: (min: string, max: string) =>
      `Действует на одного покупателя. Пусто — встроенный консервативный дефолт. Допустимо целое от ${min} до ${max}.`,
    s2sProjectDailyLimitLabel: 'Суточный потолок числа списаний на весь проект',
    s2sProjectDailyLimitExtra: (min: string, max: string) =>
      `Все покупатели вместе. Пусто — по умолчанию 100 операций в сутки. Допустимо целое от ${min} до ${max}.`,
    s2sProjectDailyLimitPlaceholder: 'по умолчанию (100)',
    s2sProjectDailyAmountLabel:
      'Суточный потолок суммы списаний на весь проект',
    s2sDefaultPlaceholderWith: (value: string) => `по умолчанию (${value})`,
    s2sSaveLimits: 'Сохранить ограничения',

    chargeChargebackLabel: 'Отнято банком',
    chargeStatusSucceeded: 'Списано',
    chargeStatusRefunded: 'Возвращено',
    chargeStatusPending: 'В обработке',
    chargeStatusRequiresAction: 'Требуется 3DS',
    chargeStatusFailed: 'Отказ',
    chargeStatusRefunding: 'Возвращаем…',
    chargeNoPaymentToRefund: 'Списания не было — возвращать нечего',
    chargeRefundFallback: 'Не удалось выполнить возврат. Повторите позже.',
    chargeRefundConfirmTitle: 'Полный возврат списания?',
    chargeRefundOk: 'Вернуть средства',
    chargeRefundDone: 'Возврат выполнен',
    colWhen: 'Когда',
    chargeOutcomeUnknown: 'Исход неизвестен',
    serverChargesLoadFailed: 'Не удалось загрузить списания',

    providerDisconnectOwnerOnly:
      'Отключить провайдера может только владелец проекта',
    providerDisconnectHint: 'Выключить — отключить провайдера',
    providerConnected: 'Подключено',
    providerNotConnected: 'Не подключено',
    pixelDisconnectHint:
      'Отключить пиксель может владелец или администратор проекта',
    stripeSince: (key: string, date: string) => `${key} · с ${date}`,
    stripeModeSandbox: 'Песочница',
    stripeModeLive: 'Боевой режим',
    stripePaymentsAria: 'Приём оплаты',
    stripePaymentsOn: 'Приём оплаты включён',
    stripePaymentsOff: 'Приём оплаты выключен',
    stripeDisabledAlertTitle:
      'Приём оплаты выключен — покупатели видят ошибку на пейволле',
    stripeDisabledAlertText:
      'Ключи и настройки сохранены. Чтобы снова принимать платежи, включите приём оплаты переключателем в строке Stripe.',
    stripeSandboxAlertTitle: 'Сейчас активна песочница — реальных списаний нет',
    stripeSandboxAlertText:
      'Платежи принимаются тестовыми ключами (карты понарошку). Чтобы принимать боевые платежи, подключите боевые ключи в блоке «Управление ключами» ниже.',

    providerSyncSaved: 'Настройки Adapty/RevenueCat сохранены',
    providerSyncSaveFailed: 'Не удалось сохранить — попробуйте ещё раз',
    providerSyncKeySet: (mask: string) => `задан: ${mask}`,
    providerSyncKeyWillClear: 'будет стёрт',
    providerSyncReplacePlaceholder: 'новый ключ (замена)',
    providerSyncKeep: 'Оставить',
    providerSyncClear: 'Стереть',
    providerSyncTitle: 'Adapty / RevenueCat: выдача доступа после оплаты',
    providerSyncOn: 'включено',
    providerSyncNotConfigured: 'не настроено',
    providerSyncLoadFailed: 'Не удалось загрузить настройки',
    providerSyncLoadFailedText:
      'Сохранение выключено, чтобы не затереть уже сохранённые значения.',
    retry: 'Повторить',
    providerSyncIntroBefore:
      'Покупка на веб-пейволле автоматически выдаёт доступ в вашей подписочной платформе (и отзывает при возврате). Приложение сообщает нам свой profile-id параметром',
    providerSyncIntroAfter:
      'в адресе встраиваемой страницы — подробности в документации для разработчиков.',
    providerSyncAdaptyStatus: 'Трансляция в Adapty: ',
    providerSyncRevenuecatStatus: 'Трансляция в RevenueCat: ',
    providerSyncStatusOn: 'включена (ключи подключены)',
    providerSyncStatusOff: 'выключена (ключи не заданы)',
    providerSyncAdaptyIosKey: 'Adapty — секретный ключ приложения iOS',
    providerSyncAdaptyAndroidKey: 'Adapty — секретный ключ приложения Android',
    providerSyncAdaptyLevel: 'Adapty — access level',
    providerSyncAdaptyLevelWarning:
      'Ключ Adapty задан, а access level пуст — доступ после покупки выдаваться не будет.',
    providerSyncRevenuecatKey: 'RevenueCat — секретный API-ключ',
    providerSyncRevenuecatEntitlement: 'RevenueCat — entitlement',
    providerSyncRevenuecatWarning:
      'Ключ RevenueCat задан, а entitlement пуст — доступ после покупки выдаваться не будет.',
    providerSyncTestMode: 'Тестовый режим (песочница) — необязательно',
    providerSyncSandboxConfigured: 'песочница настроена',
    providerSyncTestIntro:
      'Отдельные ключи тестовых приложений Adapty/RevenueCat нужны, чтобы проверить интеграцию до запуска: тестовая покупка выдаст доступ в вашей песочнице, а не в боевой платформе.',
    providerSyncTestIntro2:
      'Тестовые покупки уезжают в песочницу только при заданных здесь ключах — пока поля пусты, они никуда не транслируются (как и раньше). Боевые покупки всегда идут только по боевым ключам выше.',
    providerSyncAdaptyIosTestKey: 'Adapty — тестовый ключ приложения iOS',
    providerSyncAdaptyAndroidTestKey:
      'Adapty — тестовый ключ приложения Android',
    providerSyncRevenuecatTestKey: 'RevenueCat — тестовый API-ключ',
    providerSyncTestLevelNote:
      'Access level и entitlement берутся те же, что заданы выше — в тестовом приложении заведите уровень доступа с тем же идентификатором.',
    providerSyncEncryptedNote:
      'Ключи хранятся шифрованными и никогда не показываются целиком — только последние 4 символа.',

    whEntitlementGranted: 'Доступ выдан',
    whEntitlementRenewed: 'Доступ продлён',
    whEntitlementRevoked: 'Доступ отозван',
    whPurchaseCompleted: 'Покупка оплачена',
    whRefundProcessed: 'Возврат проведён',
    whSubPaymentFailed: 'Списание по подписке не прошло',
    whSubPaymentRecovered: 'Списание по подписке прошло после провала',
    whChargebackOpened: 'Покупатель оспорил платёж в банке',
    whChargebackResolved: 'Спор по платежу разрешён',
    whSubPaused: 'Подписка на паузе (сбор оплаты приостановлен)',
    whSubResumed: 'Подписка возобновлена',
    whSubPlanChanged: 'Покупатель сменил тариф',
    whQuizCompleted: 'Посетитель завершил квиз',
    whUserPropertyUpdated: 'Свойство покупателя создано или обновлено',
    whUserPropertiesCompleted: 'Все свойства покупателя по завершении квиза',
    whFunnelAnalyticsEvent: 'Аналитика воронки (просмотры, шаги, ответы)',
    whChargeCompleted: 'Списание с сохранённой карты прошло',
    whChargeFailed: 'Списание с сохранённой карты отклонено',
    whChargeRequiresAction: 'Списание требует подтверждения от держателя карты',
    whLeadCaptured: 'Посетитель оставил почту',

    whAnQuizView: 'Просмотр квиза',
    whAnQuizStart: 'Начало квиза',
    whAnScreenView: 'Просмотр экрана',
    whAnScreenAnswer: 'Ответ на экране',
    whAnQuizComplete: 'Квиз пройден до конца',
    whAnEmailSubmit: 'Оставлена почта',
    whAnPaywallView: 'Просмотр пейволла',
    whAnPriceClick: 'Клик по цене',
    whAnCheckoutStart: 'Переход к оплате',
    whAnTraffback: 'Возврат трафика (траффбек)',
    whAnPaymentUnavailable: 'Оплата недоступна (приём платежей не подключён)',
    whAnCycleGuardTripped: 'Зацикливание переходов между экранами',

    colEvent: 'Событие',
    deliveryIncompleteTip:
      'Опознать покупателя не удалось, событие ушло, но поле с ним пустое. Это сбой на нашей стороне, не у получателя — искать у себя не нужно.',
    // WEB-2202 — причины «мы решили не отправлять» (закрытый перечень,
    // зеркало backend/src/webhooks/webhook-skip-reasons.ts).
    // WEB-2222: формулировки приведены к принятому владельцем макету —
    // человек читает их в журнале рядом с подсказкой «что делать».
    whSkipConsentDenied: 'посетитель отказался от отслеживания',
    whSkipNotSubscribed: 'тип события не включён у этого получателя',
    whSkipBridgeTierExpired: 'кончился оплаченный период',
    whSkipDuplicateEvent: 'это событие уже поставлено в доставку',
    whSkipNotLiveTraffic: 'служебный трафик (замер скорости) — не отправлено',
    whSkipRateLimited: 'потолок частоты: больше 300 событий в минуту на адрес',
    // WEB-2222 — «и что теперь». Причина сама по себе не говорит, что делать:
    // «отказался от отслеживания» — повод НЕ делать ничего, «тип не включён» —
    // повод пойти в настройки получателя.
    whSkipFixConsentDenied: 'ничего делать не нужно — так работает согласие',
    whSkipFixNotSubscribed:
      'включите его в настройках получателя, если он вам нужен',
    whSkipFixBridgeTierExpired: 'события возобновятся после оплаты тарифа',
    whSkipFixRateLimited: 'события сверх потолка пропускаются без повторов',
    whSkipFixDuplicateEvent: 'ничего делать не нужно — событие уже в доставке',
    whSkipFixNotLiveTraffic:
      'ничего делать не нужно — служебные проверки скорости воронки получателям не отправляются',
    deliverySkipTimes: (count: number) => `×${count}`,
    deliverySkipTag: 'не отправлено',
    colOutcome: 'Исход',
    delivFilterAll: 'Все',
    delivFilterDelivered: 'Доставлено',
    delivFilterSkipped: 'Не отправлено',
    delivFilterFailed: 'Ошибка',
    delivPeriodDay: 'Сутки',
    delivPeriodWeek: '7 дней',
    delivPeriodMonth: '30 дней',
    deliveriesMore: 'Показать ещё',
    deliveriesEmptyPeriod: 'За период таких доставок не было',
    deliveriesKeepHint: (days: number, attempts: number) =>
      `Храним ${days} ${plural('ru', days, { one: 'сутки', few: 'суток', many: 'суток' })}. ` +
      `Ошибку доставки повторяем ${attempts} ${plural('ru', attempts, { one: 'раз', few: 'раза', many: 'раз' })} в течение часа.`,
    deliveriesLoadFailed: 'Не удалось загрузить журнал доставок',
    // WEB-2955: класс отказа `ssrf_blocked` (адрес получателя ведёт во
    // внутреннюю сеть, WEB-2731) — человеческая подпись вместо машинного кода.
    deliveryErrorSsrfBlocked: 'адрес во внутренней сети — не отправляем',
    webhookUrlRequired: 'Укажите URL',
    webhookEventRequired: 'Выберите хотя бы одно событие',
    webhookAnalyticsAllowlistRequired:
      'Для «аналитики воронки» выберите хотя бы одно разрешённое событие',
    webhookCreateFailed: 'Не удалось создать endpoint',
    webhookUpdateFailed: 'Не удалось обновить',
    webhookRotateFailed: 'Не удалось ротировать секрет',
    webhookDeletedToast: 'Endpoint удалён',
    webhookDeleteFailed: 'Не удалось удалить',
    webhookEventsHint: 'Выбрать нужно хотя бы одно.',
    webhookEventGroupAccess: 'Доступ',
    webhookEventGroupMoney: 'Деньги',
    webhookEventGroupSubscription: 'Подписка',
    webhookEventGroupBehaviour: 'Поведение',
    webhookAnalyticsOff: ' (выключен: нет разрешённых событий)',
    webhookAllProperties: ' (все свойства)',
    webhookRotateSecret: 'Ротировать секрет',
    webhookLoadFailed: 'Не удалось загрузить получателей',
    webhookAdd: 'Добавить получателя',
    webhookEnable: 'Включить',
    webhookDisable: 'Выключить',
    webhookLogOpen: 'Журнал',
    webhookLogClose: 'Скрыть журнал',
    // WEB-2224 п.2: один модал на создание и на изменение — заголовок и
    // кнопка переключаются, поля те же.
    webhookCreateTitle: 'Добавить получателя событий',
    webhookEditTitle: 'Изменить получателя событий',
    webhookEditAction: 'Изменить',
    webhookUpdatedToast: 'Изменения сохранены',
    webhookSecretStaysHint: `Секрет подписи остаётся прежним. Заменить его — «Ротировать секрет» в меню строки: новый покажем один раз. ${ROTATE_GRACE_RU}`,
    create: 'Создать',
    webhookUrlLabel: 'Адрес вашего сервера',
    webhookVolumeWarningTitle: 'Это очень частые события.',
    webhookVolumeWarningText:
      'На ваш сервер полетит по одному запросу на каждое подписанное событие каждого посетителя; сверх 300 в минуту на адрес события пропускаются без повторов.',
    webhookSecretShownOnce: 'Секрет подписи покажем один раз после создания.',
    webhookRevealTitle: 'Секрет подписи вебхука',
    webhookRevealSaveText:
      'Повторно этот секрет мы не покажем. Им вы проверяете подпись входящих от нас вебхуков.',
    webhookDeleteConfirmTitle: 'Удалить получателя?',
    webhookRotateConfirmTitle: 'Ротировать секрет получателя?',
    webhookDeleteConfirmOk: 'Да, продолжить',
    webhookDeleteConfirmBody:
      'Мы перестанем слать события на этот адрес. Действие необратимо.',
    webhookRotateConfirmBody: ROTATE_GRACE_RU,

    mmpHintAppsflyer:
      'AppsFlyer — ожидается *.onelink.me или app.appsflyer.com',
    mmpHintAdjust: 'Adjust — ожидается app.adjust.com, *.go.link или *.adj.st',
    bridgeSettingsSaved: 'Настройки сохранены',
    saved: 'Сохранено',
    connectionMethodSaveFailed: 'Не удалось сохранить метод подключения',
    copied: 'Скопировано',
    copy: 'Копировать',
    portalPaywallHint:
      'Пейволл-портал отмены подписки — на него ведёт ссылка управления подпиской из API и письма. Соберите его из блоков «Портал подписки» и опубликуйте: неопубликованный пейволл выбрать нельзя — ссылка вела бы на страницу, которую покупатель не откроет.',
    portalNotAssigned: 'Портал не назначен',
    portalUnpublishedOption: (name: string) => `${name} — не опубликован`,
    portalUnpublishedWarning:
      'Выбранный пейволл не опубликован — опубликуйте его или выберите другой, иначе настройки не сохранятся.',
    portalUnavailableWarning:
      'Назначенный пейволл-портал недоступен — скорее всего он удалён. Выберите другой или очистите поле, иначе настройки этой вкладки не сохранятся.',
    portalListFailed:
      'Список пейволлов не загрузился — выбрать портал и проверить уже назначенный сейчас нельзя. Обновите страницу.',
    mmpPairNoUrl:
      'Заполните вторую половину пары: у выбранного трекера нет ссылки — ссылка «Скачать» для iPhone не соберётся, покупатель на iPhone будет вводить почту.',
    mmpPairNoProvider:
      'Заполните вторую половину пары: у ссылки не выбран трекер — она не используется, ссылка «Скачать» для iPhone не соберётся.',
    mmpPairMismatch: (hint: string) =>
      `Ссылка не соответствует выбранному трекеру. ${hint}`,
    mmpAdjustNoAdjT:
      'В ссылке нет adj_t — Adjust ответит 404. Скопируйте полную ссылку из Campaign Lab.',
    // WEB-1930: цена выбирается из каталога проекта, а не печатается руками.
    priceLevelPricePlaceholder: 'Выберите цену',
    priceLevelPriceMissing: (priceId: string) => `${priceId} · нет в каталоге`,
    priceLevelPriceInactive: 'неактивна',
    priceLevelPriceTaken: 'уже назначена',
    sdkLogLevelDebug: 'Отладка',
    sdkLogLevelInfo: 'Информация',
    sdkLogLevelWarn: 'Предупреждения',
    sdkLogLevelError: 'Ошибки',
    colTimeUtc: 'Время (UTC)',
    colLevel: 'Уровень',
    colMessage: 'Сообщение',
    colPlatform: 'Платформа',
    colDeviceGuid: 'Устройство (guid)',
    sdkLogsLoadFailed: 'Не удалось загрузить журнал SDK',
    sdkLogsNothingMatches: 'Под фильтр ничего не попало',
    sdkLogsNothingMatchesText:
      'Смягчите условия или сбросьте фильтры, чтобы увидеть весь журнал.',
    resetFilters: 'Сбросить фильтры',
    sdkLogsEmpty: 'Записей журнала пока нет',
    sdkLogsEmptyText:
      'SDK начнёт присылать шаги после первого запуска приложения с подключённым проектом.',
    sdkLogsAllLevels: 'Все уровни',
    sdkLogsEventPlaceholder: 'Событие, напр. identify.',
    sdkLogsGuidPlaceholder: 'guid устройства',
    sdkLogsContext: 'Контекст',
    sdkLogsDevice: 'Устройство',
    sdkLogsClientClock: (moment: string) =>
      `Часы устройства: ${moment} (могут расходиться с серверным временем)`,

    // WEB-1198 — подвкладка «Логи SDK»; с WEB-3757 юнит 6 — на ките по эталону
    // settings-app.jsx, новые надписи — блоком «WEB-3757 юнит 6» ниже.
    acLogsModeDevices: 'По устройствам',
    acLogsWorstError: 'есть ошибки',
    acLogsWorstWarn: 'есть предупреждения',
    acLogsWorstOk: 'без ошибок',
    acLogsSessionEvents: (n: number) =>
      `${n} ${plural('ru', n, { one: 'событие', few: 'события', many: 'событий' })}`,
    acLogsLoadFailed: 'Не удалось загрузить логи.',

    sdkDrawerTitle: 'Как подключить приложение',
    sdkDrawerIntroBefore:
      'Наш SDK связывает вашу веб-воронку с приложением: пользователь проходит воронку, ставит приложение — и оно сразу знает,',
    sdkDrawerIntroWho: 'кто это',
    sdkDrawerIntroAnd: 'и',
    sdkDrawerIntroWhat: 'что он оплатил',
    sdkDrawerIntroAfter:
      ', чтобы открыть платный контент. Сопоставление «воронка → установка» делаем мы.',
    sdkDrawerProjectId: 'Ваш Project ID',
    sdkDrawerPasteOnInit: 'Вставьте при инициализации SDK',
    sdkDrawerForProject: (name: string) => ` — проект «${name}»`,
    sdkDrawerNoSdkBefore:
      'Интегрируетесь только через трекер (AppsFlyer/Adjust), без нашего SDK? Токен придёт в диплинке: AppsFlyer —',
    sdkDrawerNoSdkMiddle: '(Unified Deep Linking), Adjust —',
    sdkDrawerNoSdkAfter:
      '(разберите query входящего deferred-диплинка). Параметры и код-сэмплы — в полной документации ниже.',
    sdkDrawerInstall: (ios: string, android: string) =>
      `Установка SDK (iOS ${ios} / Android ${android}), инициализация, события воронки, встроенный показ пейвола и квиза — в полной документации для разработчика:`,
    sdkDrawerFullDocs: 'Полная документация для разработчика →',

    methodFullBridgeTitle: 'Наш SDK',
    methodFullBridgeText: 'Наш SDK + вход по email + (опц.) реклама. Бесшовно.',
    methodApiOnlyTitle: 'Своя интеграция по API',
    methodApiOnlyText: 'Право по API — интеграция на вашей стороне.',
    methodTestTitle: 'Демо / проверка',
    methodTestText: 'Фейк-доступ для проверки.',
    methodWebOnlyText:
      'Работает — настраивается во вкладке «Для разработчика».',
    methodGroupAria: 'Метод подключения приложения',
    methodTitle: 'Как приложение узнаёт об оплате',
    methodSubtitle: 'Определяет, как приложение узнаёт, что человек оплатил.',
    methodRecommended: 'Рекомендуем',
    methodSoon: 'Скоро',

    webPaywallForeignWarning:
      'Адрес не на домене этого проекта — проверьте, что это осознанно. Сохранить можно — так и должно быть у трекинговых доменов и редиректоров.',

    orgRenamed: 'Название организации обновлено',
    orgRenameFailed: 'Не удалось обновить название организации',
    orgDeletedToast: 'Организация удалена вместе с её проектами',
    orgDeleteFailed: 'Не удалось удалить организацию',
    projectsCount: (count: number) =>
      `${count} ${plural('ru', count, { one: 'проект', few: 'проекта', many: 'проектов' })}`,
    orgRenameOwnerOnly: 'Переименовать может только владелец организации',
    orgDeleteWithProjects: (projects: string) =>
      `Организация будет удалена безвозвратно вместе с ${projects} — их воронками, публикациями и настройками.`,
    orgDeleteNoProjects:
      'Организация будет удалена безвозвратно вместе с биллингом и доменами.',
    orgClosurePaymentHistoryRule:
      'Организацию, по проектам которой проходили платежи покупателей, можно ' +
      'закрыть через год после последнего платежа.',
    orgPreviewFailedTitle: 'Не удалось посчитать, что исчезнет',
    orgPreviewFailedText:
      'Пока перечень не загрузился, удалять вслепую нельзя. Закройте окно и попробуйте ещё раз.',
    orgDeleteBlockersTitle: 'Пока удалить нельзя',

    adminAdded: (email: string) => `Администратор добавлен: ${email}`,
    adminAddFailed: 'Не удалось назначить администратора (проверьте email)',
    adminInviteTitle: (org: string) => `Пригласить администратора в «${org}»`,
    adminAssign: 'Назначить администратором',
    adminEmailLabel: 'Email пользователя',
    adminEmailHelp: 'Пользователь должен быть зарегистрирован в Subster.ai',
    adminFullAccessNote:
      'Админ организации получает полный доступ к биллингу, доменам и всем проектам.',
    adminBillingAccessLabel: 'Доступ к платёжным данным',
    adminBillingAccessOn:
      'Видит и меняет платёжные ключи, возвраты, отмены подписок',
    adminBillingAccessOff:
      'Видит состояние подключения оплаты, но не ключи и не может ничего менять',
    orgTransferTitle: 'Передать владение организацией',
    orgTransferDone: 'Владение организацией передано',
    orgTransferNoticeTitle: 'После передачи вы станете администратором',
    orgTransferNoticeText:
      'Новый владелец получит эксклюзивные права: удаление организации, назначение администраторов и передачу владения.',
    orgTransferNoAdminsTitle: 'Нет доступных получателей',
    orgTransferNoAdminsText:
      'Сначала назначьте хотя бы одного администратора — только администраторы могут стать новым владельцем.',
    orgTransferNewOwner: 'Новый владелец',
    orgTransferPickAdmin: 'Выберите администратора',

    orgCreatedSwitchFailed: (name: string) =>
      `Организация «${name}» создана, но переключиться на неё не удалось — выберите её вручную в списке организаций.`,
    orgCreated: (name: string) => `Организация «${name}» создана`,
    orgCreateFailed: 'Не удалось создать организацию',
    orgCreateTitle: 'Создать рабочее пространство',
    orgCreateNameRequired: 'Введите название',
    orgCreateNameLabel: 'Название',
    orgCreateNamePlaceholder: 'Моя команда',
    orgCreateNoticeTitle: 'Вы станете владельцем нового пространства',
    // WEB-3796: бесплатного тарифа больше нет — строка по эталону (`ws.ownD`).
    orgCreateNoticeText:
      'Проекты, команда, тариф и баланс у пространств раздельные.',
    orgGateTitle: 'Создайте рабочее пространство',
    orgGateNameRequired: 'Укажите название организации',
    orgGateNameTooLong: 'Название не должно превышать 255 символов',
    orgGateNamePlaceholder: 'Название компании',
    orgGateSubmit: 'Создать рабочее пространство',
    orgBootstrapFailedTitle: 'Не удалось загрузить',
    orgBootstrapFailedHint: 'Данные на месте — попробуйте ещё раз.',
    orgBootstrapFailedSupport: 'Не проходит — напишите в поддержку.',
    orgBootstrapRetry: 'Повторить',
    orgBootstrapLogout: 'Выйти из аккаунта',

    orgLimitReason: (owned: number, max: number) =>
      `Организаций ${owned} из ${max}. Достигнут предел.`,
    workspaceSwitched: 'Рабочее пространство переключено',
    workspaceSwitchedLocally:
      'Рабочее пространство переключено, но выбор не сохранён: он действует до перезагрузки страницы',
    workspaceSwitchFailed: 'Не удалось переключить рабочее пространство',
    createWorkspace: 'Создать рабочее пространство',
    projectSwitchFailed: 'Не удалось переключить проект',
    createProject: 'Создать проект',
    allProjects: 'Все проекты',

    projectTeamLoadFailed: 'Не удалось загрузить список проектов организации',
    projectTeamLoadFailedText: 'Проверьте соединение и попробуйте ещё раз.',
    // WEB-3758 организация 2: «Команды проектов» и «Команда организации» на ките
    // (эталон design/cabinet-rescin/sections/me-k.jsx, стр. 125–129)
    projectTeamsTitle: 'Команды проектов',
    projectRowLabel: 'Проект',
    projectMembersOpen: 'Участники проекта',
    projectTeamNoProjects: 'В организации пока нет проектов',
    orgAssignAdminButton: 'Назначить администратора',
    orgColPaymentData: 'Платёжные данные',
    orgBillingOwnerAlways: 'У владельца доступ всегда',


    orgMembersLoadFailed: 'Не удалось загрузить список участников',
    orgAdminDemoted: 'Права администратора отозваны',
    orgRoleChangeFailed: 'Не удалось изменить роль участника',
    orgMemberRemoved: 'Участник удалён из организации',
    orgMemberRemoveFailed: 'Не удалось удалить участника из организации',
    orgBillingAccessChangeFailed:
      'Не удалось изменить доступ к платёжным данным',
    orgLeft: 'Вы покинули организацию',
    orgLeaveFailed: 'Не удалось покинуть организацию',
    colPayments: 'Платежи',
    orgOwnerImmutable: 'Владельца организации нельзя изменить',
    orgDemote: 'Снять роль',
    orgTeamTitle: 'Команда организации',
    roleOrgMemberShort: 'Участник',
    orgDemoteConfirmTitle: (email: string) =>
      `Снять роль администратора с ${email}?`,
    orgDemoteConfirmBody:
      'Пользователь потеряет доступ к биллингу, доменам и управлению проектами организации. Участие в проектах сохранится.',
    orgRemoveConfirmTitle: (email: string) =>
      `Удалить ${email} из организации?`,
    orgRemoveConfirmBody:
      'Пользователь потеряет доступ ко всем проектам организации — будет удалён из команды организации и из всех её проектов. Это действие нельзя отменить самостоятельно.',
    orgLeaveNote: 'Вы потеряете доступ ко всем проектам этой организации.',
    orgLeaveOwnerTip: 'Сначала передайте владение, чтобы покинуть организацию',
    orgLeaveButton: 'Покинуть организацию',
    orgLeaveConfirmTitle: 'Покинуть организацию?',
    orgLeaveConfirmBody:
      'Это действие нельзя отменить самостоятельно — потребуется новое приглашение.',

    ctxColorPrimary: 'Основной',
    ctxColorBg: 'Фон',
    ctxColorAccent: 'Акцент',
    ctxOriginAuto: 'авто',
    ctxOriginAi: 'ИИ',
    ctxOriginManual: 'вручную',
    ctxStoreDrift: (value: string) =>
      `Обновлено в сторе: ${value} — примените через редактирование или оставьте своё значение.`,
    ctxBrandColors: 'Цвета бренда',
    ctxScreenshots: 'Скриншоты из стора',
    ctxScreenshotsCount: (count: number) => `Скриншоты из стора (${count})`,
    ctxScreenshotAlt: (index: number) => `Скриншот ${index}`,
    ctxLoadFailed: 'Не удалось загрузить профиль контекста.',
    ctxUrlInvalid:
      'Нужна ссылка вида https://apps.apple.com/… или на сайт приложения',
    ctxProfileUpdated: (cost: string) => `Профиль обновлён${cost}`,
    ctxReanalyzeFailed: 'Не удалось обновить анализ. Попробуйте позже.',
    ctxSaved: 'Контекст сохранён',
    ctxSaveFailed: 'Не удалось сохранить изменения.',
    ctxTabIdentity: 'Профиль',
    ctxTabBranding: 'Брендинг',
    ctxTabAudience: 'Аудитория',
    ctxTabFeatures: 'Функции',
    ctxTabReviews: 'Отзывы',
    ctxTabMedia: 'Медиа',
    ctxAppName: 'Название приложения',
    ctxCategory: 'Категория',
    ctxTagline: 'Суть в одной фразе',
    ctxDescription: 'Описание',
    ctxWebsite: 'Сайт',
    ctxPlatforms: 'Платформы',
    ctxFont: 'Шрифт',
    ctxLogoUrl: 'Ссылка на логотип',
    ctxColorPrimaryPlaceholder: 'основной',
    ctxColorBgPlaceholder: 'фон',
    ctxColorAccentPlaceholder: 'акцент',
    ctxPersona: 'Портрет аудитории',
    ctxGeoLanguage: 'Гео / Язык',
    ctxPains: 'Боли и мотивация',
    ctxKeyFeatures: 'Ключевые функции',
    ctxUsp: 'Отличие от конкурентов',
    ctxMonetization: 'Модель монетизации',
    ctxRating: 'Рейтинг',
    ctxReviewCount: 'Количество отзывов',
    ctxReviewSamples: 'Примеры отзывов',
    ctxBestQuotes: 'Лучшие цитаты',
    ctxScreenshotsField: 'Скриншоты',
    ctxPromoVideo: 'Промо-видео',
    ctxNotUsedByAi: 'ИИ пока не использует',
    ctxCompleteness: (pct: number) => `Заполнено на ${pct} %`,
    ctxEditRbac: 'Редактировать контекст могут Owner и Admin',
    ctxEdit: 'Редактировать',
    // WEB-2385 п.2: см. английскую пару.
    ctxAnalyzeRbac: 'Нужно право редактировать воронки',
    ctxReanalyzePaid: (cost: string) =>
      `Обновление анализа платное${cost}: списывается при каждом запуске`,
    ctxReanalyze: 'Обновить анализ',
    ctxAnalyzeRunning:
      'Анализ приложения по ссылке выполняется — профиль появится здесь, когда он закончится.',
    ctxAnalyzeFailed: (reason: string) => `Анализ не выполнен: ${reason}`,
    ctxAnalyzeFailedText:
      'Профиль контекста можно заполнить вручную или запустить анализ ещё раз.',
    ctxEmpty:
      'Профиля контекста пока нет. Запустите анализ, чтобы заполнить его автоматически, или заполните вручную.',
    ctxStartAnalysis: 'Запустить анализ',
    ctxFillManually: 'Заполнить вручную',
    ctxProTitle: 'Профиль контекста — функция Pro',
    // WEB-3756 доводка 1 — см. английскую половину выше.
    ctxPayPlan: 'Оплатить тариф',
    ctxReanalyzeTitle: 'Обновить анализ стор-страницы?',
    ctxReanalyzeOk: 'Обновить',
    ctxReanalyzeNote: (cost: string) =>
      `Пересканирование${cost}. Поля «авто» будут перезаписаны, ручные правки сохранятся.`,
    ctxReanalyzeUrlLabel: 'Ссылка на приложение в сторе или сайт',
    ctxCostTag: (cost: number) => `≈ ${cost} Cr`,
    ctxCostSuffix: (cost: number) => ` (−${cost} Cr)`,
    ctxCostDash: (cost: number) => ` — ≈ ${cost} Cr`,
    // WEB-3756 юнит 15 — см. английскую половину выше.
    ctxSectionFilled: (filled: number, total: number) =>
      `заполнено ${filled} из ${total}`,

    wizardSourceWebsite: 'Сайт',
    wizardSourceNone: 'Не подключаю приложение',
    wizardStepAbout: 'О приложении',
    wizardStepAnalysis: 'Анализ',
    wizardStepConfirm: 'Подтверждение',
    wizardPreviewUnparseable:
      'Не удалось разобрать страницу приложения по ссылке. Проверьте ссылку.',
    wizardAnalysisFailed:
      'Не удалось выполнить анализ. Проверьте ссылку и попробуйте ещё раз.',
    wizardNameRequired: 'Укажите название проекта',
    wizardUrlRequired: 'Укажите корректную ссылку',
    wizardCreateFailed: 'Не удалось создать проект. Попробуйте ещё раз.',
    wizardNameLabel: 'Название проекта',
    wizardSourceLabel: 'Приложение, которое подключим к квизам',
    wizardUrlLabel: 'Ссылка на приложение',
    wizardUrlHint:
      'По ссылке автоматически заполним бренд-контекст (название, категория, описание, медиа).',
    wizardAnalysisPaidWithCost: (cost: number) =>
      `Разбор платный: спишется ≈ ${cost} кр. с баланса.`,
    wizardAnalysisPaid: 'Разбор платный: спишется с баланса кредитов.',
    wizardContinue: 'Продолжить',
    wizardContinueNoAnalysis: 'Продолжить без анализа',
    wizardAnalyzing:
      'Анализируем стор-страницу: название, категория, описание, скриншоты…',
    wizardSummaryNoApp: 'Приложение не подключаем — контекст заполните позже',
    wizardSummaryWillFill: (source: string) =>
      `${source} · контекст заполнится автоматически после создания`,
    // WEB-3760 юнит 2б — см. английскую половину выше.
    wizardNoBalanceTitle: (cost: number, have: number) =>
      `На разбор не хватает кредитов: нужно ≈ ${cost} кр., на балансе ${have} кр.`,
    wizardNoBalanceText:
      'Пополните баланс или создайте проект без разбора — контекст заполните позже во вкладке «Контекст».',
    wizardTopUp: 'Пополнить',
    wizardSummaryNoBalance: (source: string) =>
      `${source} · без разбора: не хватает кредитов`,
    wizardSummaryFailed: (source: string) =>
      `${source} · без разбора: страницу не разобрали`,
    wizardSumName: 'Название',
    wizardSumSource: 'Приложение',
    wizardSumMoney: 'Оплата',
    wizardCreated: 'Проект создан',
    wizardCreate: 'Создать проект',
    wizardTitle: 'Новый проект',
    wizardOrgLimit: (max: string) => `Лимит проектов организации — ${max}`,
    wizardLimitText: 'Удалите неиспользуемый проект или повысьте тариф.',
    wizardUpgrade: 'Повысить тариф',
    wizardCategorySuffix: (category: string) => ` · ${category}`,
    // WEB-2126: второй вопрос мастера — про деньги; умолчания нет ни у одного
    // из двух вопросов.
    wizardMoneyLabel: 'Как вы принимаете оплату?',
    wizardMoneyOurs: 'Через нас',
    wizardMoneyOwn: 'Сам, у себя',
    wizardSourceRequired: 'Выберите, подключаем ли приложение',
    wizardMoneyRequired: 'Выберите, как вы принимаете оплату',
    wizardOwnAllTitle: 'Кабинет подстроится под вас',
    wizardOwnAllText:
      'Денежные разделы и шаги воронки показывать не будем — вместо вечных нулей останется то, что мы действительно измеряем: показы, ответы, заявки и уход на вашу оплату.',
    // WEB-2234: «через нас» + «приложение не подключаю» — законное сочетание,
    // но покупателя после оплаты надо куда-то вести.
    wizardPayUsNoAppTitle: 'После оплаты покупателя нужно куда-то вести',
    wizardPayUsNoAppText:
      'Приложение вы не подключаете — значит доступ выдаёте у себя: событие о покупке придёт на ваш сервер. Куда отправить человека после оплаты, настраивается в пейволле.',

    ctxAnalyzeFailedFallback:
      'Не удалось разобрать приложение по ссылке. Попробуйте запустить анализ ещё раз.',

    seqTriggerLeadRegistration: 'Регистрация лида',
    seqTriggerAnyPurchase: 'Любая покупка',
    seqTriggerPurchase: 'Покупка',
    seqTriggerSubscribe: 'Подписка',
    seqTriggerStartTrial: 'Начало пробного периода',
    seqTriggerAppInstalled: 'Установка приложения',
    seqClassMarketing: 'Маркетинговое письмо (не уходит отписавшимся)',
    seqClassTransactional:
      'Транзакционное письмо (уходит всегда — доступ к покупке, чек)',
    seqTransactionalWarning:
      'Повод этой цепочки не связан с покупкой, а транзакционное письмо обходит список отписавшихся. Ставьте этот класс только письмам с тем, за что человек заплатил, — доступ, ссылка на скачивание, чек.',
    // WEB-2717: галочка «слать после покупки» и причины остановки цепочки.
    seqContinueAfterPurchaseLabel: 'Слать после покупки',
    seqContinueAfterPurchaseHelp:
      'Выключено: если адресат купит что-нибудь в этом проекте (разовая покупка или подписка) после запуска цепочки, оставшиеся письма не уйдут. Включите, если цепочка нужна и купившим.',
    seqContinueAfterPurchaseNotApplicable:
      'Не действует для этой цепочки: транзакционные письма и цепочки, запущенные покупкой, покупка не останавливает.',
    seqContinueAfterPurchaseSaved: 'Настройка сохранена',
    seqSkipStoppedInactive: 'Не отправлено: цепочка выключена',
    seqSkipStoppedPurchase: 'Не отправлено: адресат уже купил',
    seqCreated: 'Цепочка создана',
    seqCreateFailed: 'Не удалось создать цепочку',
    seqActivated: 'Цепочка активирована',
    seqDeactivated: 'Цепочка деактивирована',
    seqUpdateFailed: 'Не удалось обновить цепочку',
    seqDeleted: 'Цепочка удалена',
    seqDeleteFailed: 'Не удалось удалить цепочку',
    seqColTrigger: 'Триггер',
    seqTriggerUnsupported: 'повод не поддерживается — цепочка не запустится',
    seqColActive: 'Активна',
    seqColSteps: 'Шаги',
    seqEditAria: 'Редактировать цепочку',
    seqDeleteAria: 'удалить',
    seqPageTitle: 'Email-цепочки',
    seqCreate: 'Создать цепочку',
    seqEmpty: 'Пока нет email-цепочек',
    seqCreateFirst: 'Создать первую цепочку',
    seqCreateTitle: 'Создать email-цепочку',
    seqNameLabel: 'Название цепочки',
    seqNameRequired: 'Введите название',
    seqTriggerRequired: 'Выберите триггер',
    seqTriggerPlaceholder: 'Выберите триггер',
    seqTriggersOwnMoneyNote:
      'Поводов о покупке в списке нет: в настройках проекта включено «сам, ' +
      'у себя», и о покупке мы не узнаём. Изменить — в настройках платежей ' +
      'проекта.',
    seqClassLabel: 'Класс письма',
    seqDeleteConfirmTitle: 'Удалить эту цепочку?',
    seqDeleteConfirmBody: 'Все шаги и логи будут удалены. Действие необратимо.',
    seqClassSaved: 'Класс письма сохранён',
    seqLoadFailed: 'Не удалось загрузить цепочку',
    seqBackToList: 'Вернуться к цепочкам',
    seqNotFound: 'Цепочка не найдена',
    seqNotFoundText: 'Не удалось загрузить эту цепочку писем',
    seqTabSteps: 'Шаги',
    seqTabLogs: 'История отправок',
    seqBackAria: 'Назад к цепочкам',
    seqOn: 'Вкл',
    seqOff: 'Выкл',
    seqTestSendAria: 'Отправить тестовое письмо',
    seqTestSend: 'Отправить тестовое',
    seqLogFilterAll: 'Все',
    seqLogSent: 'Отправлено',
    seqLogFailed: 'Ошибка',
    seqLogSkipped: 'Пропущено',
    seqColRecipient: 'Получатель',
    seqColSubject: 'Тема',
    seqUnknownStep: 'Неизвестный шаг',
    seqColFailReason: 'Причина ошибки',
    seqStatusPlaceholder: 'Статус',
    seqRefreshLogsAria: 'Обновить логи',
    seqRefresh: 'Обновить',
    seqLogsEmpty: 'Логов пока нет',
    seqLogsFailed: 'Не удалось загрузить журнал отправок',
    seqRetry: 'Повторить',
    seqStepUpdated: 'Шаг обновлён',
    seqStepAdded: 'Шаг добавлен',
    seqStepSaveFailed: 'Не удалось сохранить шаг',
    seqSaveStepAria: 'Сохранить шаг',
    seqEditStepTitle: 'Редактировать шаг',
    seqAddStepTitle: 'Добавить шаг',
    seqSubjectLabel: 'Тема',
    seqSubjectRequired: 'Укажите тему',
    seqSubjectPlaceholder: 'Тема письма',
    // WEB-2311: сервер считает задержку каждого шага от события, запустившего
    // цепочку (все шаги встают в очередь разом), а не от предыдущего письма.
    seqDelayLabel: 'Задержка (часов после события)',
    seqDelayHelp:
      'Отсчёт идёт от события, запустившего цепочку (например, посетитель оставил почту или совершил покупку), а не от предыдущего письма. Чтобы письма шли раз в сутки, ставьте 24, 48, 72.',
    seqBodyLabel: 'Текст письма (HTML)',
    seqHidePreviewAria: 'Скрыть превью',
    seqShowPreviewAria: 'Показать превью',
    seqEditMode: 'Редактировать',
    seqPreviewMode: 'Просмотр',
    seqBodyRequired: 'Укажите текст письма',
    seqBodyNotEmpty: 'Текст письма не должен быть пустым',
    seqBodyPlaceholder: '<p>HTML-код письма...</p>',
    seqPreviewTitle: 'Предпросмотр письма',
    seqStepDeleted: 'Шаг удалён',
    seqStepDeleteFailed: 'Не удалось удалить шаг',
    seqStepsEmpty: 'Шагов пока нет',
    seqAddStepAria: 'Добавить шаг',
    seqAddStep: 'Добавить шаг',
    seqEditStepAria: 'Редактировать шаг',
    seqEditStep: 'Редактировать',
    seqDeleteStepAria: 'Удалить шаг',
    seqAfterHours: (hours: number) => `Через ${hours}h`,
    seqDeleteStepConfirmTitle: 'Удалить этот шаг?',
    seqDeleteStepConfirmBody: 'Это действие нельзя отменить.',
    seqTestSent: 'Тестовое письмо успешно отправлено',
    seqTestSendFailed: 'Не удалось отправить тестовое письмо',
    seqTestSendTitle: 'Отправить тестовое письмо',
    seqRecipientLabel: 'Email получателя',
    seqEmailRequired: 'Введите email адрес',
    seqEmailInvalid: 'Введите корректный email адрес',
    seqListLoadFailed: 'Не удалось загрузить цепочки писем',
    seqStepAddFailed: 'Не удалось добавить шаг',
    seqLogsLoadFailed: 'Не удалось загрузить историю отправок',

    capiTokenLabelMeta: 'Токен доступа (CAPI)',
    capiTokenLabelTiktok: 'Токен доступа (Events API)',
    capiOpenHint: 'Введите данные и нажмите «Подключить»',
    capiEventPurchase: 'Оплата',
    capiEventStartCheckout: 'Старт оплаты',
    capiEventProgress90: 'Прошёл 90% квиза',
    capiEventLead: 'Оставил почту',
    // WEB-2272 §9: строка больше не управляет событиями воронки — она задаёт
    // имя ПРОВЕРОЧНОГО события, и подписана теперь тем, чем управляет.
    capiEventView: 'Проверочное событие',
    // WEB-2413 п.12 — подписи остальных наших событий справочника сервера
    // (`GET /capi/event-catalog`); таблица кодов — capiEventLabels.ts.
    capiOurEventQuizStart: 'Начал квиз',
    capiOurEventQuizProgress50: 'Прошёл 50% квиза',
    capiOurEventQuizComplete: 'Прошёл квиз до конца',
    capiOurEventPaywallView: 'Открыл экран с ценами',
    capiOurEventPaywallHandoff: 'Ушёл на оплату на ваш сайт',
    capiOurEventTrialStart: 'Начал пробный период',
    capiOurEventRepeatPurchase: 'Продление, доплата или покупка в приложении',
    capiOurEventTrialConverted: 'Оплата после пробного периода',
    capiOurEventRefund: 'Возврат или проигранный спор',
    capiOurEventAppInstalled: 'Установил приложение',
    capiOurEventRegistration: 'Зарегистрировался в приложении',
    capiStatusLoadFailed: 'Не удалось загрузить статус подключения',
    capiStatusUnknownTitle: 'Не смогли проверить подключение',
    capiStatusUnknownText:
      'Сервер не ответил — это не значит, что пиксель отключён.',
    capiDisconnectTitle: 'Отключить провайдера?',
    capiDisconnectBody: 'События конверсий перестанут отправляться в эту сеть.',
    capiDisconnectOk: 'Отвязать',
    capiDisconnected: (network: string) => `${network} отвязан`,
    capiDisconnectFailed: 'Ошибка при отвязке',
    capiNotConnectedReadonly: (network: string) =>
      `${network} не подключён. Обратитесь к владельцу или администратору проекта для настройки.`,
    capiConnected: (network: string) => `${network} подключён`,
    // WEB-3757 (эталон спек-репо 8acd1074 settings-k.jsx NetView): режим одним
    // словом, пояснение — в «?» (capiAdvPiiHint / capiAdvPiiOff); «Подключено»
    // без двоеточия.
    capiPiiTitle: 'Персональные данные',
    capiPiiSuppressed: 'Подавляются',
    capiPiiNotSuppressed: 'Стандартно',
    capiEventMapping: 'Имена событий',
    capiConnectedAt: 'Подключено',
    capiEditSettings: 'Изменить настройки',
    capiAdvancedTitle: (network: string) => `Дополнительно \u00b7 ${network}`,
    // WEB-3756 сверка пикселей 1, М10 — подпись эталона settings-k.jsx Net.
    capiAdvPiiSwitch: 'Хеш почты и телефона',
    // WEB-1364 п.3 (решение владельца 13.09.2026): текст приведён к факту.
    // Почта, телефон и external_id хешируются; IP и строка браузера уходят в
    // сеть как есть — обе сети требуют их в сыром виде. Сторож —
    // src/i18n/__tests__/capi-pii-text.web1364.test.ts.
    capiAdvPiiHint:
      'Почта, телефон и external_id хешируются (SHA-256), IP-адрес и строка браузера уходят в сыром виде: так требуют Meta и TikTok.',
    // WEB-2413 п.14 (решение владельца 26.09.2026): external_id хешируется
    // для обеих сетей; при выключенном тумблере почта, телефон, IP и строка
    // браузера не уходят, а хеш идентификатора посетителя, метки клика, fbp/ttp и адрес страницы уходят
    // (capi-dispatch.processor.ts, suppressPii). Сторож —
    // src/i18n/__tests__/capi-pii-text.web1364.test.ts.
    capiAdvPiiOff:
      'Почта, телефон, IP-адрес и строка браузера не отправляются. Уходят хеш идентификатора посетителя (external_id, SHA-256), метки клика (fbc, ttclid), идентификаторы браузера рекламной сети (fbp, ttp) и адрес страницы — событие привязывается к клику, но почту, телефон и IP-адрес покупателя сеть не получает',
    capiAdvMappingHint: (network: string) =>
      `Наше событие → событие ${network}. Дефолты уже проставлены — переназначайте при необходимости.`,
    capiConnectFailed: 'Ошибка подключения',
    /**
     * WEB-2272: «Сохранить» шторки теперь делает ТО ЖЕ сохранение, что и
     * форма, поэтому и плашка «сами не сохраняются», и всплывающее
     * «применятся позже» убраны: они описывали двухступенчатое сохранение,
     * которого больше нет (а после WEB-2235 плашка ещё и врала про
     * обязательный токен доступа — человек читал «нужен токен», токена под
     * рукой не было, и он уходил, будучи уверен, что сохранил).
     */
    capiAdvancedNameSwitchNote:
      'Строка «Прошёл 90% квиза» теперь задаёт имя этого события сама. До обновления имя бралось из строки «Проверочное событие» — проверьте, что здесь стоит то имя, под которое настроена ваша кампания.',
    // WEB-2491 кабинетная часть (К6б): строки-пары «наше событие → имя в
    // сети» на серверном справочнике (`CapiEventPairsSection.tsx`).
    capiPairsLoading: 'Загружаем список событий…',
    capiPairsLoadFailed:
      'Не удалось загрузить список событий. Сохранённые имена показаны как есть и не изменятся.',
    capiPairAdd: '+ Добавить пару',
    capiPairAddPlaceholder: 'Выберите наше событие',
    capiPairRemove: 'Убрать',
    capiPairOutsideNote: 'сохранено раньше; в этой форме не меняется',
    capiPairCustomOption: (name: string) => `Своё событие: ${name}`,
    // WEB-3756 сверка пикселей 1, М10: пометка той же строкой « · своё» (эталон Net).
    capiPairCustomTag: 'своё',
    capiPairCustomHint:
      'Нужного имени нет в списке — начните печатать и впишите своё.',
    capiPairGroupCustom: (_network: string) => 'Свои события',
    capiPairGroupStandard: (network: string) =>
      `Стандартные события ${network}`,
    capiPairLegacyTag: 'прежнее имя',
    capiPairSimilarStandard: (name: string) =>
      `похоже на стандартное ${name} — проверьте написание: другое написание сеть считает отдельным событием`,
    capiPairNameEmpty: 'Укажите имя события',
    capiPairNameChars: 'Можно только латиницу, цифры, пробел, «_» и «-»',
    capiPairNameTooLong: (max: number) => `Не длиннее ${max} знаков`,
    capiPairNameTiktok:
      'TikTok: начинается с латинской буквы, дальше только латиница, цифры, «_» и «-»',
    capiPairNamePurchase: 'Имя покупки — только у событий с оплатой',
    capiPairTestEventNote: 'имя для кнопки «Отправить проверочное событие»',
    capiPairsFixBeforeSave:
      'Исправьте имена событий, подсвеченные выше, — сервер их не примет',
    capiPixelIdRequired: 'Укажите Pixel ID',
    capiPixelIdCopy: 'Скопировать',
    // WEB-3756 сверка пикселей 1 — подписи эталона settings-k.jsx Net.
    capiTestEventCode: 'Test Event Code (необязательно)',
    // WEB-2408 п.5: код уходит только с проверочным событием.
    capiTestEventCodeHint:
      'Код прикладывается только к проверочному событию (кнопка ниже); боевые конверсии уходят без него.',
    capiConnect: 'Подключить',
    // ── WEB-2218 / WEB-2220 / WEB-2224 / WEB-2235: строка сети и её форма ────
    pixelStateLiveTag: 'Работает',
    pixelStateLiveNote: (ago: string) => `последнее событие ${ago}`,
    pixelStateIdleTag: 'Событий ещё не было',
    pixelStateIdleNote: 'до сети пока не дошло ни одно событие',
    // WEB-2413 п.3 (пачка 49, А-8): пиксель, который РАБОТАЛ и замолчал
    // больше чем на сутки, не должен выглядеть как «ещё не было ни одного
    // события» — это гасит ровно тот сигнал поломки, ради которого статус
    // заводили (WEB-2220).
    pixelStateSilentTag: 'Замолчал',
    pixelStateSilentNote: (ago: string) => `последнее событие ${ago}`,
    pixelStateErrorTag: 'Ошибка',
    pixelStateErrorNote: (reason: string) => `сеть отказывает: ${reason}`,
    pixelStateUnknownTag: 'Не смогли проверить',
    pixelStateUnknownNote: 'подключение при этом на месте',
    pixelStateRetry: 'Повторить',
    pixelEditCount24h: (n: number) => `${n} за сутки`,
    pixelEditNoErrors: 'ошибок нет',
    pixelEditErrors: (n: number) => `ошибок: ${n}`,
    pixelTokenSaved: 'Сохранён',
    pixelTokenReplace: 'Заменить',
    pixelTokenNoReenter:
      'Вводить заново, чтобы поменять остальные настройки, не нужно.',
    pixelPurchasesTitle: 'Какие оплаты отправлять',
    // WEB-3756 сверка пикселей 1 — подписи эталона settings-k.jsx Net.
    pixelPurchasesAll: 'Все, с продлениями',
    pixelPurchasesFirst: 'Только первую',
    pixelRefundsTitle: 'Возвраты и проигранные споры — отменой конверсии',
    pixelRefundsHint:
      'Без этого сеть учится на покупках, за которые деньги вернулись.',
    pixelMultiplierTitle: 'Множитель ценности покупки',
    pixelMultiplierHint:
      'Сеть увидит сумму, умноженную на это число. Ставят больше единицы, когда за первой оплатой обычно идут продления.',
    pixelProbeSend: 'Отправить проверочное событие',
    // WEB-2413 п.10 — см. английскую половину.
    pixelProbeNeedsSave:
      'Сначала сохраните изменения — проверочное событие уходит по сохранённому пикселю.',
    pixelProbeSending: 'Отправляем…',
    pixelProbeSentTitle: 'Событие ушло, сеть ответила «принято»',
    pixelProbeSentText:
      'Это не значит, что оно уже видно в кабинете сети: проверьте его в разделе проверки событий — там оно появляется в течение нескольких минут.',
    pixelProbeFailed: 'Не удалось отправить проверочное событие',
    // ── WEB-2220/WEB-2218: шапка строки сети — живой статус, плашка ошибки ──
    // Причина отказа приходит машинной строкой `<статус>[:<код>]`; словами её
    // переводит `components/capi/capiPixelHealth.ts`.
    pixelFailureAuth: (code: string) => `токен недействителен (код ${code})`,
    pixelFailureAuthNoCode: 'токен недействителен',
    pixelFailureRejected: (code: string) => `отправка отклонена, код ${code}`,
    pixelFailureRejectedNoCode: 'отправка отклонена',
    pixelFailureTransport: 'до сети не достучались',
    // WEB-2413 п.4 (пачка 49, А-7): сеть ОТВЕТИЛА временным отказом (429/5xx)
    // — это не «до сети не достучались», код обязан остаться видимым.
    pixelFailureTransientCode: (code: string) =>
      `сеть временно отказывает (код ${code})`,
    // WEB-2413 п.15 (пачка 49, А-6 остаток): сеть ответила 2xx, но приём не
    // подтвердила (TikTok без `code`) — отдельно и от «не достучались», и от
    // «отказано».
    pixelFailureUnconfirmed: (code: string) =>
      `ответ сети не подтверждён (код ${code})`,
    pixelErrorSince: (when: string) => `События не доходят с ${when}`,
    pixelErrorAdviceAuth: (reason: string) =>
      `Сеть отвечает: ${reason}. Выпустите токен заново в кабинете сети и вставьте его здесь.`,
    // Сервер отложил отправки до нового токена (`failed_awaiting_token`):
    // кроме «замените токен» клиент обязан узнать, что события пока не потеряны.
    pixelErrorAdviceAwaitingToken: (reason: string) =>
      `Сеть отвечает: ${reason}. Выпустите токен заново в кабинете сети и вставьте его здесь — события, которые ждут токена, мы дошлём сами.`,
    pixelErrorAdviceRejected: (reason: string) =>
      `Сеть отвечает: ${reason}. Проверьте идентификатор пикселя и токен в кабинете сети, затем отправьте проверочное событие.`,
    pixelErrorAdviceTransport:
      'Мы не достучались до сети. Обычно это проходит само; если держится — отправьте проверочное событие и посмотрите ответ.',
    pixelErrorAdviceTransientCode: (reason: string) =>
      `Сеть отвечает: ${reason}. Обычно это проходит само, когда снижается нагрузка или лимит сети; если повторяется — проверьте кабинет сети.`,
    pixelErrorAdviceUnconfirmed: (reason: string) =>
      `Сеть отвечает: ${reason}. Мы повторим отправку сами, делать ничего не нужно, если это не повторяется постоянно.`,
    pixelDeliveryLog: 'Журнал отправок',
    // Исход проверочного события судится по `outcome`, а не по коду ответа:
    // ручка отвечает 200 и на отказ сети, и на «не достучались» (WEB-2220).
    pixelProbeRejectedTitle: 'Сеть отклонила проверочное событие',
    pixelProbeRejectedText: (code: string) =>
      `Сеть ответила отказом: ${code}. Проверьте идентификатор пикселя и токен — до кабинета сети событие не дошло.`,
    pixelProbeRejectedTextNoCode:
      'Сеть ответила отказом и кода не назвала. Проверьте идентификатор пикселя и токен — до кабинета сети событие не дошло.',
    pixelProbeNoAnswerTitle: 'Событие не дошло до сети',
    pixelProbeNoAnswerText:
      'Ответа от сети мы не получили. Ключи при этом не менялись: попробуйте ещё раз через минуту.',
    // WEB-3756 сверка пикселей 1: «Дополнительно» одним словом (эталон Net).
    pixelAdvancedLinkNew: 'Дополнительно',
    pixelSaved: 'Сохранено',
    capiConnectTitle: (network: string) => `Подключите ${network}`,
    capiConnectSubtitle:
      'Введите данные для отправки событий конверсий через Conversions API.',

    createProjectDenied:
      'Создавать проекты может владелец или администратор организации',

    storeUrlMustBeHttps: 'Ссылки на стор должны начинаться с https://',
    webPaywallMustBeHttps: 'Адрес веб-пейвола должен начинаться с https://',
    returnSchemeInvalid:
      'Схема возврата: только строчные буквы/цифры/+-., начинается с буквы, без "://"',

    // ── WEB-1198, исполнитель B: группа веток «Основного» (блоки 6-7) ───────
    acBrGroupTitle: 'Что ещё умеет подключение',
    acBrExpandAll: 'Раскрыть всё',
    acBrCollapseAll: 'Свернуть всё',
    acBrForeignTag: 'не нужно при вашем пути',

    acBrRecogTitle: 'Как приложение узнает вашего покупателя',
    acBrRecogSummaryPlatform: 'платформа',
    acBrRecogSummaryTracker: 'трекер',
    acBrRecogSummaryOff: 'выключено',
    acBrRecogStoreRules:
      'При проверке App Store могут спросить об узнавании по устройству.',
    acBrRecogAutoTitle: 'Автоматически, силами платформы',
    acBrRecogTrackerTitle: 'Через рекламный трекер',
    acBrRecogTrackerChip: 'в дополнение',
    acBrRecogOffTitle: 'Не узнавать по устройству',
    acBrRecogMmpLabel: 'Какой у вас трекер',
    acBrRecogMmpLinkLabel: 'Ссылка приложения в трекере',
    acBrRecogPostbackLabel: 'Адрес для отчётов об установке',
    acBrRecogPostbackHint: 'Вставьте его в трекер — туда он сообщает нам об установках.',

    acBrProviderTitle: 'Куда доставлять доступ',
    acBrProviderSelectLabel: 'Какой сервис у вас подключён',
    acBrProviderAdaptyIosKey: 'Секретный ключ Adapty, iOS',
    acBrProviderAdaptyAndroidKey: 'Секретный ключ Adapty, Android',
    acBrProviderAdaptyLevel: 'Название уровня доступа в Adapty',
    acBrProviderAdaptyLevelHint: 'Так уровень называется у вас в Adapty — туда проставим оплату.',
    acBrProviderRcKey: 'Секретный ключ RevenueCat',
    acBrProviderRcEnt: 'Название права в RevenueCat',
    acBrProviderSandboxToggle: 'Ключи песочницы — необязательно',
    acBrProviderSandboxChip: 'для тестового приложения',
    acBrProviderTestIos: 'Тестовый ключ, iOS',
    acBrProviderTestAndroid: 'Тестовый ключ, Android',
    acBrProviderTestRc: 'Тестовый ключ RevenueCat',
    acBrProviderLoadFailed:
      'Не удалось прочитать ключи — сохранение выключено, чтобы не затереть сохранённые значения.',

    acBrDeeplinkTitle: 'Если приложение уже установлено — открывать его сразу',
    acBrDeeplinkIosLabel: 'iOS — Team ID и Bundle ID одной строкой',
    acBrDeeplinkAndroidLabel: 'Android — имя пакета',
    acBrDeeplinkShaLabel: 'Android — отпечаток ключа подписи (SHA-256)',
    acBrDeeplinkReturnLabel: 'Ссылка возврата в приложение',
    acBrDeeplinkOwnSchemeLink: 'У приложения уже есть своя схема?',
    acBrDeeplinkOwnSchemeLabel: 'Своя схема приложения',
    acBrDeeplinkOwnSchemeRestore: 'Вернуть нашу',
    acBrDeeplinkReturnCodePlaceholder: '<код>',

    acBrLevelsTitle: 'Что открывать в приложении после оплаты',
    acBrLevelsSummaryPairs: (n: number) =>
      `${n} ${plural('ru', n, { one: 'пара', few: 'пары', many: 'пар' })}`,
    acBrLevelsPairRemove: 'Убрать',
    acBrLevelsPairAdd: 'Добавить цену',

    acBrCheckTitle: 'Проверить связку до первой оплаты',
    acBrCheckSummaryOn: 'включено',
    acBrCheckEnable: 'Включить',
    acBrCheckDisable: 'Выключить режим проверки',
    // WEB-2261: путь, на котором сервер приводит запись к `testMode: false`.
    acBrCheckServerOff:
      'При этом способе подключения режим проверки держит выключенным сервер — включить его отсюда нельзя.',
    acBrCheckShowAnswer: 'Показать ответ, который получит приложение',
    // WEB-3757 юнит 3 — ветки «Основного» на ките, тексты эталона settings-app.jsx (ce823f8f)
    acBrProviderYourPath: 'ваш путь',
    acBrLevelsSummaryOf: (n: number, m: number) =>
      `${n} из ${m} ${plural('ru', m, { one: 'цены', few: 'цен', many: 'цен' })}`,
    acBrRecogAutoChip: 'включено',
    acBrRecogMmpLinkHelpAf: 'AppsFlyer → OneLink: ссылка шаблона целиком.',
    acBrRecogMmpLinkHelpAdj: 'Полная ссылка из Adjust вместе с adj_t.',
    acBrProviderEraseTitle: (service: string, platform: string | null, test: boolean) =>
      `Стереть ${test ? 'тестовый ключ' : 'ключ'} ${service}${platform ? ', ' + platform : ''}?`,
    acBrProviderEraseText: (service: string, platform: string | null, test: boolean) =>
      `Доступ в ${service}${test ? ' для тестового приложения' : ''} перестанет доставляться${platform ? ' для ' + platform : ''}, пока не зададите новый.`,
    acBrProviderKeyErased: 'Ключ стёрт',
    acBrDeeplinkShaHelp: 'Ключ подписи приложения в Google Play, не ключ загрузки.',
    acBrDeeplinkReturnHelp:
      'Отдайте разработчику — он зарегистрирует схему в сборках iOS и Android. По ней приложение получает одноразовый код и само узнаёт покупателя.',
    acBrDeeplinkOwnSchemeHelp:
      'Только имя, без «://». Должно совпадать в сборках iOS и Android.',
    acBrLevelsPriceLabel: 'Цена',
    acBrLevelsAddRest: (n: number) => `Добавить строки для остальных цен (${n})`,
    acBrLevelsNoPairTitle: 'Ни одной пары не задано.',
    acBrLevelsNoPairText:
      'Приложение получит идентификатор цены вида price_1Tyw… вместо слова, которое оно проверяет, — заплативший покупатель останется без доступа.',
    acBrLevelsNoNameTitle: 'Название есть не у каждой цены.',
    acBrLevelsNoRows: (n: number, names: string[], more: number) =>
      `У ${n} ${plural('ru', n, { one: 'цены', few: 'цен', many: 'цен' })} нет строки: ${names.join(', ')}${more > 0 ? ` и ещё ${more}` : ''}.`,
    acBrLevelsNoName: (n: number) =>
      n === 1
        ? 'В одной строке цена выбрана, а название пустое'
        : `В ${n} ${plural('ru', n, { one: 'строке', few: 'строках', many: 'строках' })} цена выбрана, а название пустое`,
    acBrLevelsNoNameText:
      'По этим ценам приложение получит идентификатор вместо слова — покупатель заплатит и не получит доступ.',
    acBrCheckAnswerTitleOn: 'Ответ приложению — сейчас в режиме проверки',
    acBrCheckAnswerTitleOff: 'Ответ приложению — обычный режим',

    // WEB-3757 юнит 4 — «Для разработчика» на ките, тексты эталона settings-app.jsx (ce823f8f)
    acDevEntitlementRespLabel: 'Ответ',
    acDevSdkLabel: 'Библиотека (SDK)',
    acDevSecretRotateTitle: 'Ротировать секрет?',
    acDevSecretRotateBody: `Новый секрет покажем один раз. Старый принимается ещё ${GRACE_HOURS_RU} — успеете обновить сервер без простоя.`,
    acDevSecretRotateOk: 'Да, ротировать',
    acDevSecretShownNewTitle: 'Секрет подписи выпущен',
    acDevSecretShownReTitle: 'Секрет перевыпущен',
    acDevSecretShownText: 'Покажем один раз. Сохраните его на своём сервере — второй раз мы его не покажем.',
    acDevSecretShownGrace: `Старый секрет ещё поработает ${GRACE_HOURS_RU}, чтобы ваш сервер успел обновиться.`,
    acDevKeysEmpty: 'Ключей пока нет. Создайте первый — без него ваш сервер не сможет нас спросить.',
    acDevKeysLoadFailed: 'Не удалось загрузить ключи',
    apiKeyNameLabel: 'Название',
    // Эталон называет запретом тестового ключа только деньги; сервер (api-key.guard.ts,
    // TEST_KEY_BLOCKED_SCOPES) запрещает ему и управление подпиской — текст по серверу.
    apiKeyModeHelp:
      'Боевой двигает настоящие деньги и подписки: списания, возвраты, отмену, паузу и возобновление подписки. Тестовый делает всё то же, кроме этого — отказ test_key_not_allowed. Ключ покажем один раз.',
    apiKeyCreateOk: 'Создать',
    apiKeyShownOnce: 'Значение ключа показываем один раз',
    acDevPortalLabel: 'Пейволл-портал',

    // WEB-3757 юнит 5 — «События на ваш сервер» и постбэк на ките, тексты эталона
    // settings-app.jsx; числа (повторы, срок хранения) — из констант кода.
    webhooksGroupHelp: (days: number, attempts: number) =>
      `Вебхуки: шлём события на ваш адрес, подписываем секретом получателя. Повторяем ${attempts} ${plural('ru', attempts, { one: 'раз', few: 'раза', many: 'раз' })} в течение часа, журнал храним ${days} ${plural('ru', days, { one: 'сутки', few: 'суток', many: 'суток' })}.`,
    webhooksEmptyNote:
      'Получателей пока нет. Добавьте адрес, если вашему серверу нужно узнавать о покупках и доступах.',
    webhooksPausedTitle: 'Доставка приостановлена: тариф понижен',
    webhooksPausedTitleOn: (date: string) => `Доставка приостановлена: тариф понижен ${date}`,
    // Эталон обещает письмо «один раз»; сервер шлёт его не чаще раза в сутки
    // (webhook-emit.service.ts, BRIDGE_PAUSED_MAIL_TTL_MS) — текст по серверу.
    webhooksPausedText:
      'Получатели и их настройки сохранены — доставка возобновится сама, как только вернётесь на Pro. События, выпавшие за это время, не копятся и досланы не будут. Владельцу проекта приходит письмо об этом — не чаще раза в сутки, а не на каждое событие.',
    webhooksBackToPro: 'Вернуться на Pro',
    webhookDotOn: 'Включён',
    // WEB-4090: точка по исходу последней доставки (эталон settings-app.jsx:328-329).
    webhookDotErr: (code: string) => `Последняя доставка — ошибка ${code}`,
    webhookDotDelivering: 'Доставляется',
    webhookDotOff: 'Выключен',
    webhookDotPaused: 'На паузе: тариф понижен',
    webhookTestOffHint: 'Получатель выключен',
    webhookTestProHint: 'Доступно на Pro',
    delivChipPeriod: 'Период',
    colAttempts: 'Попыток',
    delivRetryRow: 'Отправить заново',
    delivRetryRowSoon: 'Повторная отправка события появится позже',
    webhookEventsTitle: 'События',
    webhookAnalyticsTitle: 'События аналитики воронки',
    webhookUserPropsTitle: 'Разрешённые свойства покупателя',
    webhookUserPropsHelp: 'Через запятую. Пусто — шлём все свойства.',
    webhookUrlHttpsErr: 'Адрес должен начинаться с https://',
    postbackTagsHelp: 'Метки подставим сами: {click_id}, {payout}, {status}, {txid}.',

    // WEB-3757 юнит 6 — «Логи SDK» на ките, тексты эталона settings-app.jsx (раздел Logs).
    // Подсказка у времени — по коду: время записи серверное (createdAt), печатаем в UTC.
    acLogSearchPlaceholder: 'Событие или guid',
    acLogChipLevel: 'Уровень',
    acLogChipPeriod: 'Период',
    acLogLevelAll: 'Все',
    acLogLevelDebug: 'Отладка',
    acLogLevelInfo: 'Информация',
    acLogLevelWarn: 'Предупреждение',
    acLogLevelError: 'Ошибка',
    acLogPeriodAll: 'Всё время',
    acLogPeriod7: '7 дней',
    acLogPeriod30: '30 дней',
    acLogReset: 'Сбросить',
    acLogModeRecords: 'Записи',
    acLogColTime: 'Время',
    acLogTimeHelp:
      'Когда запись дошла до нашего сервера, по всемирному времени (UTC). Часы самого устройства — в раскрытой записи.',
    acLogRowMeta: (clock: string, platform: string) =>
      `Часы устройства: ${clock} (могут расходиться с нашим временем) · ${platform}`,
    acLogsNone: 'Записей пока нет — появятся после первого запуска приложения.',
    acLogsShowMore: 'Показать ещё',
    acLogsOf: (shown: number, total: number) => `${shown} из ${total}`,

    // WEB-1198, исполнитель C — подвкладка «Для разработчика», задание 24 §6
    // (блоки 9, 10 и переходный блок 12 карты вёрстки).
    acDevWhyCollapse: 'Свернуть',
    acDevStatusConnected: 'Приложение обращается к нам.',
    acDevStatusNever: 'Приложение ещё не подключалось.',
    acDevStatusLastInstall: (moment: string) =>
      `Последняя установка: ${moment}`,
    acDevStatusFailed: 'Не удалось загрузить состояние связки.',
    acDevRetry: 'Повторить',
    acDevUpsellTitle: 'Подключение приложения доступно на Pro.',
    acDevUpsellText:
      'Ключи проекта, подпись входящих событий и получатели событий — часть машинного моста «сайт → приложение», он работает на платных тарифах.',
    acDevUpsellButton: 'Перейти на Pro',
    acDevStartTitle: 'Начало работы',
    acDevPlatformIos: 'iOS',
    acDevPlatformAndroid: 'Android',
    acDevPlatformServer: 'Свой сервер',
    acDevApiLabel: 'Адрес API',
    acDevProjectIdLabel: 'ID проекта',
    acDevSecretLabel: 'Секрет подписи входящих запросов',
    acDevSecretNotConfigured: 'секрет не настроен',
    acDevSecretIssue: 'Выпустить',
    acDevSecretRotate: 'Ротировать',
    acDevSecretConfigured: 'подключён',
    acDevSecretRotatedAt: (moment: string) => `последняя ротация ${moment}`,
    acDevSecretWhyGrace: `Им ваш сервер подписывает запросы к нам — это не секрет получателя событий. После ротации старый работает ещё ${GRACE_HOURS_RU}.`,
    acDevEntitlementLabel: 'Адрес запроса права',
    acDevEntitlementHint:
      'Один GET без ключа. Доступ есть, если в grants есть запись со status: "active".',
    acDevInstallHintIos: (version: string) =>
      `iOS, Swift Package Manager. Версия ${version} — вставьте в зависимости проекта.`,
    acDevInstallHintAndroid: (version: string) =>
      `Android, Gradle (JitPack). Версия ${version} — вставьте в зависимости проекта.`,
    acDevInstallOpen: 'Открыть →',
    acDevSnippetIos: (projectId: string, apiBase: string) =>
      `${SDK_IOS_IMPORT_LINE}\n\n// один раз при старте\n${sdkIosConfigureCall(projectId, apiBase)}\n\n// проверить, есть ли доступ\nWeb2App.entitlement { grant in\n    let isPaid = grant?.isActive ?? false\n}`,
    acDevSnippetAndroid: (projectId: string, apiBase: string) =>
      `// один раз при старте\n${sdkAndroidConfigureCall(projectId, apiBase)}\n\n// опознание: referrer читаем сами, аргументов не нужно\nWeb2AppSdk.identify()`,
    acDevSnippetServer: (apiBase: string) =>
      `curl ${apiBase}/s2s/v1/entitlement?guid=${SDK_SERVER_SNIPPET_GUID} \\\n  -H "Authorization: Bearer sk_live_8fK2…"\n\n# 200 OK\n# { "grants": [{ "level": "premium", "status": "active" }] }`,
    acDevSnippetLabelIos: 'Подключение библиотеки, iOS',
    acDevSnippetLabelAndroid: 'Подключение библиотеки, Android',
    acDevSnippetLabelServer: 'Запрос с вашего сервера',
    acDevCopyCode: 'Копировать код',
    acDevCodeCopied: 'Код скопирован',
    acDevFullDocs: 'Полная документация →',
    acDevKeysTitle: 'Ключи доступа',
    acDevKeysTech: 'Bearer sk_…',
    acDevHooksTitle: 'События на ваш сервер',
    acDevPostbackTitle: 'Отчёты о продажах в трекер',
    acDevPostbackTech: 'постбэк S2S',
    acDevPostbackLabel: 'Адрес постбэка',
    acDevDocsTitle: 'Справочник',
    acDevDocsRowTitle: 'Документация для разработчика',
    acDevDocsRowHint:
      'Запросы, поля ответов, коды отказа, примеры на Swift, Kotlin и curl.',
    acDevDocsOpen: 'Открыть →',
    acDevMcpRowTitle: 'MCP-сервер для ИИ-ассистента',
    acDevMcpRowHint: 'Подключите в Cursor или Claude Code.',
    acDevPortalTitle: 'Портал управления подпиской',
    acDevPortalMovingChip: 'переедет в «Подписки»',
    acDevPortalNote:
      'Блок стоит здесь, пока его не примет раздел «Подписки»: по ссылке из письма покупатель приходит на этот пейволл и управляет подпиской.',
    // WEB-3413: ссылка «Управлять подпиской» в письме после оплаты — только при назначенном портале.
    acDevPortalNoManageLink:
      'Без портала письмо после оплаты уходит без ссылки на управление подпиской.',
    // WEB-2458 п.3: на бесплатном тарифе портал виден, но не правится.
    acDevPortalProOnly: 'Настройка доступна на Pro',

    // ── WEB-2027, исполнитель A: каркас раздела «Платежи» ──────────────────
    // WEB-2126: взаимоисключающий выбор способа приёма оплаты.
    payMoneyModeTitle: 'Как вы принимаете оплату',
    payMoneyModeOursTitle: 'Через нас',
    payMoneyModeOursDesc:
      'Подключаете платёжную систему, продаёте на нашем пейволле. Товары, цены и покупатели — в «Подписках».',
    payMoneyModeOwnTitle: 'Сам, у себя',
    payMoneyModeOwnDesc:
      'Деньги принимаете на своей стороне, у нас собираете квизы и данные. Денежные разделы кабинета показывать не будем.',
    payMoneyModeOwnNote:
      'Скрыто то, что у вас не наполнится: денежные шаги воронки, денежные колонки статистики, «Когорты», «Подписки», платёжные показатели над заявками и поводы писем про оплату. Выбор виден в разделе «Аудит» → «Команда проекта».',
    payMoneyOwnKeptAnd: ' и ',
    payMoneyOwnKeptOne: (providers: string) =>
      `${providers} остаётся подключённым.`,
    payMoneyOwnKeptMany: (providers: string) =>
      `${providers} остаются подключёнными.`,
    payMoneyOwnKeptRest:
      ' Ранее оформленные подписки продолжают списываться — мы ничего не обрываем. ',
    payMoneyOwnDisconnectOne: 'Отключить платёжную систему',
    payMoneyOwnDisconnectMany: 'Отключить платёжные системы',
    payMoneyModeOwnToast: 'Денежные разделы скрыты. Приём оплаты не тронут',
    payMoneyModeOursToast: 'Денежные разделы вернулись',
    payMoneyModeSaveFailed: 'Не удалось сохранить способ приёма оплаты',
    payWarnPayUsNoAppTitle: 'После оплаты покупателя нужно куда-то вести.',
    payWarnPayUsNoAppText:
      'Приложение вы не подключаете, значит доступ выдаёте у себя: событие о покупке придёт на ваш сервер. Куда отправить человека после оплаты — настраивается в пейволле.',
    payOtherSoon: 'скоро',
    payOtherNotConnected: 'не подключено',
    payNoBillingRowText:
      'Оплата подключена. Подробности и управление доступны владельцу',
    payNoBillingRowNoneText:
      'Оплата не подключена. Подробности и управление доступны владельцу',
    payNoBillingRowFailText:
      'Не смогли проверить подключение. Подробности и управление доступны владельцу',
    payNoBillingGroupText:
      'Платёжные данные вам недоступны — доступ к ним выдаёт владелец организации',

    payRulesTitle: 'Правила подписки',
    payBranchOff: 'выключено',
    payBranchOn: 'включено',
    payGraceIntro:
      'Сколько дней покупатель сохраняет доступ, если очередное списание не прошло — карта отклонена, не хватило средств и так далее.',
    payGraceDefaultWord: 'три дня',
    payGraceRangeHint: (min: number, max: number, defaultDays: string) =>
      `Целое число от ${min} до ${max}. Пустое поле возвращает значение по умолчанию — ${defaultDays}.`,

    payMoneyTitle: 'Деньги покупателей',
    payMoneyChipReal: 'настоящие списания',
    payMoneyChipNote: 'нужно, только если у вас свой сервер',
    payMoneyLockedTitle:
      'Откроется, когда подключите платёжную систему. Здесь будут списания с сохранённых карт по вашему серверному ключу, возвраты и журнал списаний.',
    payMoneyLockedText:
      'Нужно, только если у вас свой сервер. Если продаёте через наши экраны оплаты — эта часть вам не понадобится вовсе.',
    payMoneyTierTitle: 'Деньги покупателей доступны на Pro.',
    payMoneyTierText:
      'Списания, возвраты и управление подписками через ваш сервер — часть машинного моста «сайт → приложение», он работает на платных тарифах.',

    payChargesBranch: 'Списания через API',
    payRefundsBranch: 'Возвраты через API',
    paySubsBranch: 'Управление подписками через API',
    payJournalBranch: 'Журнал списаний',

    payCapBuyerDailyLabel: 'Списаний на покупателя за сутки',
    payCapProjectDailyLabel: 'Операций на проект за сутки',
    payCapProjectAmountLabel: 'Сумма на проект за сутки',
    payCapPlaceholder: (value: string) => `по умолчанию ${value}`,

    // ── WEB-3756 платежи 29.10 (правка эталона спек-репо 46e4be66, settings-k.jsx Rules; владелец:
    // «по текстам тут просто жесь»): одно короткое предупреждение на право сервера, подробности —
    // в «?» у названия строки и у полей ограничений; числа «?» — постоянные ChargesPanel.tsx.
    payChargesNoticeTitle: 'Ваш сервер сможет списывать с карт покупателей без их участия',
    payChargesNoticeText:
      'Списывайте только то, на что покупатель согласился. Иначе он оспорит платёж в банке, а платёжная система урежет выплаты.',
    payChargesHelp:
      'Только с покупателей с подпиской. Ключ sk_… утёк — выключите: право снимется со всех ключей сразу. На кабинет не влияет.',
    payCapOpHelp: (max: string) => `В валюте ваших цен, не в копейках. До ${max}`,
    payCapBuyerDailyHelp: (min: string, max: string, hard: string) =>
      `${min}–${max}. Больше ${hard} с одного покупателя за сутки не спишется в любом случае`,
    payCapProjectDailyHelp: (min: string, max: string) => `${min}–${max}`,
    payCapProjectAmountHelp: 'Общая на все валюты проекта',
    payCapsSave: 'Сохранить',
    payRefundsNoticeTitle: 'Ваш сервер сможет возвращать деньги и снимать доступ',
    payRefundsNoticeText: 'Включайте, только если возвраты идут через ваш сервер.',
    payRefundsHelp:
      'Включается отдельно от списаний. Возвраты в кабинете работают и без него. Ключ утёк — выключите: право снимется со всех ключей сразу.',
    paySubsNoticeTitle: 'Ваш сервер сможет останавливать, ставить на паузу и возобновлять подписки',
    paySubsHelp:
      'Ключ утёк — выключите: право снимется со всех ключей сразу. На кабинет не влияет.',

    payJournalHintBefore:
      'Списания, которые выполнил ваш сервер с сохранённых карт покупателей (серверный API). ',
    payJournalHintStrong: 'Возврат — полной суммой.',
    payJournalSearchPlaceholder: 'Покупатель или почта',
    payJournalStatusAny: 'Любой исход',
    payJournalPeriod30: 'За 30 дней',
    payJournalExport: 'Выгрузить',
    payJournalEmpty:
      'Списаний пока не было. Здесь появятся списания, выполненные вашим сервером через серверный API',
    payJournalLoading: 'Загружаем списания…',
    payJournalOutcomeUnknownTip:
      'Связь с провайдером оборвалась, деньги, возможно, списаны. Исход уточнит сверка.',
    payJournalNoteUnknownName: '«Исход неизвестен»',
    payJournalNoteUnknownA:
      ' — связь с провайдером оборвалась, деньги, возможно, списаны. Исход уточнит сверка. ',
    payJournalNoteUnknownB: 'В пределах суток',
    payJournalNoteUnknownC: ' повторять нужно ',
    payJournalNoteUnknownD: 'тот же самый запрос',
    payJournalNoteUnknownE:
      ': он уйдёт в платёжную систему ключом первой попытки, и второго списания не будет. Позже сервер откажет сам и подскажет: сверьте платёж в платёжной системе и, если списания не было, повторите с новым ключом.',
    payJournalNoteChargebackName: '«Отнято банком»',
    payJournalNoteChargebackText:
      ' — покупатель оспорил списание в банке и спор выигран им: деньги изъяты у вас. Это не добровольный возврат, поэтому ярлык отдельный.',
    payJournalExportTruncated: (rows: string) =>
      `Выгрузка обрезана: в файл попали первые ${rows} строк.`,

    payJournalPeriod7: 'За 7 дней',
    payJournalPeriod90: 'За 90 дней',
    payJournalPeriodAll: 'За всё время',
    payJournalAmountMin: 'Сумма от',
    payJournalAmountMax: 'Сумма до',
    payJournalAmountHint:
      'Сумма в наименьших единицах валюты, как её хранит платёжная система (1999 = 19,99).',
    payJournalAmountBad:
      'Введите целое число в наименьших единицах валюты (например, 1999 для 19.99) — отбор не применён.',
    payJournalSortNewest: 'Сначала свежие',
    payJournalSortOldest: 'Сначала старые',
    payJournalSortAmountDesc: 'Сначала крупные',
    payJournalSortAmountAsc: 'Сначала мелкие',
    colFailureReason: 'Причина',
    colInitiator: 'Кто запустил',
    payJournalRunTip: (runId: string) => `В составе пачки ${runId}`,
    // WEB-3756 юнит 10 — см. английскую половину выше.
    payJournalRowActions: (id: string) => `Действия со списанием ${id}`,
    payJournalRefundFull: 'Полный возврат',
    payJournalPager: (from: number, to: number, total: number) =>
      `${from}–${to} из ${total}`,
    chargeActorOwner: 'Владелец проекта',
    chargeActorAdmin: 'Администратор организации',
    chargeActorServerKey: 'Серверный ключ',
    chargeActorCron: 'Фоновая задача',
    chargeActorUnknown: 'Не записано',
    declineInsufficientFunds: 'На карте не хватило денег',
    declineDoNotHonor: 'Банк отклонил списание без объяснения причины',
    declineExpiredCard: 'Срок действия карты истёк',
    declineLostCard: 'Карта заявлена как утерянная',
    declineStolenCard: 'Карта заявлена как украденная',
    declineIncorrectCvc: 'Неверный код проверки карты',
    declineIncorrectNumber: 'Неверный номер карты',
    declineProcessingError: 'Сбой платёжной системы — повтор имеет смысл',
    declineCardDeclined: 'Банк отклонил списание',
    declineGenericDecline: 'Банк отклонил списание',
    declineTryAgainLater: 'Банк попросил повторить позже',
    declineAuthenticationRequired: 'Банк требует подтверждения покупателем',
    declineCardNotSupported: 'Карта не поддерживает такое списание',
    declineCurrencyNotSupported: 'Карта не поддерживает эту валюту',
    declineWithdrawalCountExceeded: 'Исчерпан лимит списаний по карте',
    declinePickupCard: 'Карта заблокирована банком',
    declineRepeatNever: 'Повторять не нужно: карта больше не сработает',
    declineRepeatLater: 'Можно повторить позже',

    payUnbindAllTitle: 'Отключить Stripe?',
    payUnbindStillBillingBefore: 'Из них продолжат списывать деньги: ',
    // WEB-3756 юнит 8: абзацы окна полной отвязки сняты — см. английскую половину.

    payUnbindOneTitle: (keySet: string) => `Отвязать ${keySet}?`,
    payUnbindOneOnlySetBefore: 'Отключение убирает ',
    payUnbindOneOnlySetStrong: (keySet: string) => `ТОЛЬКО набор «${keySet}»`,
    payUnbindOneOnlySetRest:
      '. Второй набор и его точка приёма событий не тронуты.',
    payUnbindOneActiveCountBefore: 'Действующих подписок этой среды: ',
    payUnbindOneBuyersStrong: 'Что будет с покупателями.',
    payUnbindOneBuyersRest:
      ' Подписки, оформленные этим набором ключей, будут отозваны. Доступ отзывается только у покупателей этой среды — у покупателей второй среды он сохраняется.',
    payUnbindOneOneTimeStrong: 'Разовые покупки останутся у покупателей:',
    payUnbindOneOneTimeRest:
      ' деньги за них не возвращаются, и доступ мы не отбираем.',
    payUnbindOneSoleSetStrong: 'Если это ваш единственный подключённый набор',
    payUnbindOneSoleSetRest:
      ' — отключение равносильно полному: тогда разовые покупки тоже погаснут, а вместе с ними уйдёт и второй вебхук.',
    payUnbindOneDone: (keySet: string) => `${keySet} отвязаны`,

    payToLiveBefore: 'С покупателей начнут списываться ',
    payToLiveStrong: 'настоящие деньги',
    payToLiveRest:
      ' — тестовые платежи (песочница) больше приниматься не будут.',

    payRefundBodyBefore: 'Покупателю ',
    payRefundBodyMid: ' вернётся ',
    payRefundBodyTail: '. Частичный возврат не поддерживается.',

    payImportedCountsBefore: 'Перенесено подписок: ',
    payImportedCountsOf: ' из ',
    payImportedCountsSkipped: '. Пропущено: ',

    // ── WEB-2027, исполнитель B: строка Stripe ──────────────────────────────
    payWarnAcceptOffTitle:
      'Приём оплаты выключен — покупатели видят ошибку на пейволле.',
    payWarnSandboxTitle: 'Сейчас активна песочница — реальных списаний нет.',
    payConnFailTitle: 'Не смогли проверить подключение.',
    payConnFailText: 'Сервер не ответил — это не значит, что оплата отключена',
    payStripePowerOn: 'Отключить Stripe',
    payLoadingHint: 'Ждём ответ сервера.',
    payWebhookLabel: 'Адрес для событий',
    payWebhookNote:
      'Точку приёма событий мы создаём в вашем аккаунте сами при подключении и подписываем на двадцать один тип. Настраивать в кабинете Stripe ничего не нужно, список не редактируется.',
    payEvCheckoutCompleted: 'Оплата прошла',
    payEvCheckoutExpired: 'Сессия оплаты истекла',
    payEvAsyncSucceeded: 'Отложенная оплата прошла',
    payEvAsyncFailed: 'Отложенная оплата не прошла',
    payEvSubCreated: 'Подписка оформлена',
    payEvSubUpdated: 'Подписка изменена',
    payEvSubDeleted: 'Подписка отменена',
    payEvInvoicePaid: 'Счёт оплачен',
    payEvInvoiceFailed: 'Счёт не оплачен',
    payEvRefundCreated: 'Возврат создан',
    payEvRefundUpdated: 'Возврат обновлён',
    payEvDisputeCreated: 'Спор открыт',
    payEvDisputeClosed: 'Спор закрыт',
    payEvIntentSucceeded: 'Списание прошло',
    payEvIntentFailed: 'Списание не прошло',
    payEvProductCreated: 'Товар создан',
    payEvProductUpdated: 'Товар изменён',
    payEvProductDeleted: 'Товар удалён',
    payEvPriceCreated: 'Цена создана',
    payEvPriceUpdated: 'Цена изменена',
    payEvPriceDeleted: 'Цена удалена',
    payKeysLive: 'Боевые ключи',
    payKeysTest: 'Тестовые ключи (sandbox)',
    payKeysNote:
      'Переподключение открывает пустую форму — старые секреты не подставляются. «Отмена» сохраняет текущие ключи.',
    // WEB-3756 доводка 1: одна строка пояснения эталона — intro + образцы pk_test_ / sk_test_.
    payConnectIntro: 'Вставьте ключи из кабинета Stripe.',
    payConnectPkWarnLive:
      'Ключи боевые — оплаты будут списывать реальные деньги.',
    payConnectMismatchPkLive:
      'Ключи из разных сред: публикуемый боевой, секретный тестовый',
    payConnectMismatchPkTest:
      'Ключи из разных сред: публикуемый тестовый, секретный боевой',
    payConnectSkHint:
      'Хранится в зашифрованном виде, никогда не показывается повторно.',
    payConnectTestHintBefore: 'Хотите начать с тестовых — введите здесь пару ',
    payConnectTestHintMid: ' и ',
    payConnectTestHintAfter: '.',
    payConnectPending: 'Идёт проверка ключей…',
    payConnectFailedStrong: 'Не удалось подключиться.',
    payConnectFailedRest: ' Проверьте секретный ключ — Stripe отверг его.',

    /* WEB-2101 — см. пояснение у английской половины. */
    webhookTestSend: 'Отправить тестовое событие',
    webhookTestSending: 'Отправляем тестовое событие…',
    webhookTestWaiting: 'Тестовое событие отправлено, ждём ответ получателя…',
    webhookTestOk: (response: string) =>
      `Получатель ответил ${response} — доставка прошла`,
    webhookTestBad: (response: string) =>
      `Получатель ответил ${response} — доставка не прошла`,
    webhookTestDisabled:
      'Получатель выключен — тестовое событие не отправлено. Включите его и повторите.',
    webhookTestSsrfBlocked:
      'Адрес во внутренней сети — тестовое событие не отправлено: на адреса внутри частной сети мы события не отправляем. Укажите публичный адрес получателя.',
    webhookTestNoResponse: (errorClass: string) =>
      `Ответа от получателя нет — доставка не дошла до него (${errorClass}). Подробности в журнале доставок.`,
    webhookTestNoAnswerYet:
      'Ответа в журнале пока нет: ручная отправка повторяется до 5 раз. Откройте журнал доставок чуть позже.',
    webhookTestFailed: 'Не удалось отправить тестовое событие',

    // WEB-2069 — пара к английской половине выше.
    payGraceMaxLabel: 'Потолок льготного периода, дней',
    payGraceMaxIntro:
      'Если платёжная система сообщает, когда попробует списать снова, доступ живёт до этого момента — но не дольше потолка.',
    payGraceMaxDefaultWord: 'три недели',
    graceMaxSaved: 'Потолок льготного периода сохранён',
    graceMaxSaveFailed: 'Не удалось сохранить потолок льготного периода',
    // ── WEB-2121: пара к английской половине выше ───────────────────────────
    analyticsTabAnalytics: 'Аналитика',
    analyticsTabAdsIntro:
      'Пиксели и серверная отправка конверсий: сеть узнаёт, какая реклама привела к оплате.',
    analyticsSoonTag: 'В планах',
    analyticsSoonGaLabel: 'Google Analytics',
    // WEB-3756 юнит 11 — см. английскую половину выше.
    adsListAds: 'Реклама',
    adsListSoon: 'Скоро',
    adsNoteOn: 'подключён',
    adsNoteOff: 'не подключён',
    pixelSendingOn: 'Отправка включена',
    // WEB-3757 (эталон settings-k.jsx Ads, сверка 30.10 поломка 4): проверка
    // подключения сети не ответила — одна плашка вместо строки и формы.
    pixelLoadFailTitle: 'Не удалось загрузить подключения',
    pixelSendingOff: 'Выключено',
    // WEB-3756 доводка 1 — см. английскую половину выше.
    capiSwitchConnectFirst: 'Сначала подключите сеть: заполните поля ниже',

    amplitudeNotConnected: 'Не подключено',
    amplitudeOffKeySaved: 'Отключено · ключ сохранён',
    amplitudeLogButton: 'Журнал отправок',
    amplitudeToggleEnableHint: 'Включить',
    amplitudeToggleDisableHint: 'Отключить. Ключ сохранится',
    amplitudeNoRightsHint:
      'Подключением Amplitude управляют владелец и администратор проекта',
    amplitudeStatusUnknown: 'Не смогли проверить подключение',
    amplitudeRetry: 'Повторить',
    amplitudeReadOnlyHint:
      'Ваша роль позволяет смотреть настройку, но не менять её. Ключ не показывается.',

    amplitudeKeyLabel: 'Ключ проекта',
    amplitudeKeyHint:
      'Настройки → Проекты → ваш проект → General. Нужен API Key, не Secret Key.',
    amplitudeKeySaved: 'Ключ сохранён',
    amplitudeKeySavedTail: (tail: string) => `Ключ сохранён · …${tail}`,
    amplitudeKeyReplace: 'Заменить',
    amplitudeRegionLabel: 'Регион',
    amplitudeRegionUs: 'US',
    amplitudeRegionEu: 'EU',
    amplitudeRegionHint:
      'EU — если в адресе вашей Amplitude есть .eu. (app.eu.amplitude.com). Ошибётесь — события молча потеряются.',
    amplitudeEventsTitle: 'События',
    amplitudeEventsCount: (chosen: number, total: number) =>
      `${chosen} из ${total}`,
    amplitudeEventsSelectAll: 'Выбрать все',
    amplitudeEventsClearAll: 'Снять все',
    amplitudeEventOpen: 'Открыл воронку',
    amplitudeEventStart: 'Начал квиз',
    amplitudeEventStep: 'Показан экран квиза',
    amplitudeEventAnswer: 'Ответил на вопрос',
    amplitudeEventEmail: 'Оставил почту',
    amplitudeEventFinish: 'Дошёл до конца квиза',
    amplitudeEventPaywall: 'Увидел экран оплаты',
    amplitudeEventPay: 'Оплатил',
    // Пара к английской половине выше (WEB-2413 Д1).
    amplitudeEventsAlways: (events: readonly string[]) =>
      `Уходят всегда: ${
        events.length > 1
          ? `${events.slice(0, -1).join(', ')} и ${events[events.length - 1]}`
          : events.join('')
      }.`,
    amplitudeAlwaysPriceClick: 'клик по плану',
    amplitudeAlwaysCheckoutStart: 'начало оплаты',
    amplitudeAlwaysCheckoutFailed: 'неудачная оплата',
    amplitudeAlwaysPaymentUnavailable: '«оплата недоступна»',
    amplitudeAlwaysPaywallHandoff: 'уход на свою оплату',
    amplitudeLogTitle: 'Журнал отправок',
    // Пара к английской половине выше (WEB-2413 п.16).
    amplitudeLogReasonAccepted: 'принято Amplitude',
    amplitudeLogReasonPartial: (ingested: string, sent: string) =>
      `Amplitude приняла ${ingested} из ${sent}: остальное отброшено — скорее всего, тип события заблокирован или включена строгая схема в Amplitude`,
    amplitudeLogReasonRateLimited: 'Amplitude ограничила частоту отправки',
    amplitudeLogReasonRejected: 'Amplitude отклонила событие',
    amplitudeLogReasonTransport: 'Amplitude не ответила или ответила сбоем',
    amplitudeLogReasonStale:
      'событие потеряно: доставка не работала дольше суток',
    amplitudeLogReasonConsent: 'посетитель не дал согласия на аналитику',
    amplitudeLogReasonNoConnection: 'Amplitude не подключена в этом проекте',
    amplitudeLogReasonDisabled: 'отправка была выключена переключателем',
    amplitudeLogReasonProviderPaused:
      'отправка в Amplitude приостановлена сервисом — событие уйдёт после возобновления',
    amplitudeLogReasonEventDisabled:
      'это событие выключено в настройках подключения',
    amplitudeLogReasonUnknownVisitor:
      'посетитель не опознан — такие события не пересылаем',
    amplitudeLogReasonNotLive: 'служебный трафик (замер скорости воронки)',
    amplitudeLogReasonTestMode: 'тестовый платёж — в аналитику не отправляем',
    amplitudeLogReasonForeignPaywall:
      'покупка с вашего собственного пейволла — в аналитику не отправляем',
    amplitudeLogResponse: (httpCode: string, code: string | null) =>
      code ? ` · ответ ${httpCode}, код ${code}` : ` · ответ ${httpCode}`,
    amplitudeLogTestMark: ' · проверочное',
    amplitudeLogAttempts: (attempts: number) => `попыток: ${attempts}`,
    // WEB-2119: плашка больше не печатает машинную строку сервера и не даёт
    // один и тот же совет на все случаи. Фраза своя на каждый исход, а
    // «проверьте ключ и регион» — только там, где это правда.
    amplitudeFailureRejectedPermanent:
      'Amplitude отказала событиям наотрез: ключ проекта не подходит к адресу, на который мы отправляем. Проверьте ключ и регион в настройках проекта — ключ проекта из другого региона отвергается так же, как неверный.',
    amplitudeFailureTransportFailed:
      'Мы вообще не смогли достучаться до Amplitude: запрос не прошёл или её сервер ответил ошибкой. Со своей стороны чинить нечего — мы повторяем попытки сами в течение суток.',
    amplitudeFailureRateLimited:
      'Amplitude придушила нашу частоту: она считает ограничение по устройству, и один посетитель дал события быстрее, чем она их принимает. События не потеряны — мы замедляемся и повторяем.',
    amplitudeFailurePartiallyAccepted:
      'Amplitude ответила успехом, но события после приёма отбросила. Так бывает, когда тип события заблокирован в вашем проекте или включён строгий режим схемы.',
    // WEB-2412 п.8: отказ по полям НАШЕГО конверта — не «ключ не подходит».
    amplitudeFailureRejectedInvalidEvent:
      'Amplitude отклонила сам запрос — дело не в настройках вашего проекта, а в дефекте на нашей стороне: в данных, которые мы отправляем. Мы уже знаем об этом и разбираемся. Проверять на своей стороне нечего.',
    amplitudeFailureUnknown:
      'Amplitude не принимает события, и распознать её ответ мы не смогли. Проверьте ключ и регион в настройках проекта.',
    amplitudeFailureCode: (code: string) => `Код ответа: ${code}.`,
    deliveryAlertOpen: 'Открыть настройки',
    amplitudeEventsNote:
      'Текст ответов и почта не уходят — только факт события.',
    amplitudeServiceTitle: 'Служебные события',
    amplitudeServiceHint:
      'Клик по смарт-ссылке, установка приложения, возврат трафика.',
    amplitudeRevenueTitle: 'Оплаты как выручка',
    amplitudeRevenueHint: 'Оплаты попадут в отчёты по выручке Amplitude.',
    amplitudeRevenueWarn:
      'Если оплаты уже приходят в эту Amplitude из другого места — выручка удвоится.',
    amplitudeHonestStart: 'Данные идут с момента подключения.',
    amplitudeHonestSchema:
      'Включён строгий режим схемы — внесите наши имена в план событий, иначе Amplitude их отбросит.',
    // WEB-2292 п.2: строка обещала, что продления не приходят. После WEB-2251
    // они приходят — из трёх дверей (чекаут, оплаченный счёт подписки,
    // серверное списание), и различаются свойством revenueType. Оставить
    // прежний текст значило бы врать человеку прямо в форме.
    amplitudeHonestRenewals:
      'Продления тоже приходят — делите выручку по свойству revenueType.',
    amplitudeConnectButton: 'Подключить',
    amplitudeCheckButton: 'Проверить',
    amplitudeWhyDisabled: 'Заполните ключ и выберите регион.',
    amplitudeCheckNeedsSaveChanges:
      'Сначала сохраните изменения — проверка идёт по сохранённым настройкам подключения, а не по тому, что в форме.',
    amplitudeCheckRunning: 'Отправляем пробное событие…',
    amplitudeCheckOkTitle: 'Пробное событие ушло в ваш проект',
    amplitudeCheckOkText:
      'Ищите событие «[Funnel] Connection Check» у себя в Amplitude в течение минуты. Имя события английское и одно для всех языков — в Amplitude уезжает одна строка, а не перевод. Если события там нет — проверьте, не заблокирован ли этот тип события и не включён ли строгий режим схемы.',
    amplitudeCheckPartialTitle:
      'Amplitude приняла меньше событий, чем мы отправили',
    amplitudeCheckPartialText:
      'Ответ успешный, но часть событий отброшена. Обычно так бывает, когда тип события заблокирован или включён строгий режим схемы.',
    amplitudeCheckRejectedTitle: 'Amplitude отказала пробному событию',
    amplitudeCheckRejectedText:
      'Проверьте ключ и регион: чаще всего причина — ключ от другого проекта или другой регион. Мы не будем делать вид, что всё хорошо.',
    amplitudeCheckRateLimitedTitle: 'Amplitude попросила сбавить темп',
    amplitudeCheckRateLimitedText:
      'Само подключение работает: Amplitude отклонила пробное событие по ограничению частоты. Повторите через минуту.',
    amplitudeCheckTransportTitle: 'Не дозвонились до Amplitude',
    amplitudeCheckTransportText:
      'Запрос до Amplitude не дошёл вовсе — это про связь, а не про ваш ключ.',
    amplitudeCheckFailed: 'Не удалось отправить пробное событие',
    amplitudeConnectedToast: 'Amplitude подключена',
    amplitudeSavedToast: 'Сохранено',
    amplitudeSaveFailed: 'Не удалось сохранить подключение Amplitude',
    amplitudeDisableConfirmTitle: 'Отключить Amplitude?',
    amplitudeDisableConfirmOk: 'Отключить',
    amplitudeDisableConfirmBody:
      'Отправка событий остановится. Ключ сохранится — включить обратно можно тем же переключателем, вводить заново ничего не придётся.',
    amplitudeDisabledToast: 'Отправка в Amplitude остановлена',
    amplitudeEnabledToast: 'Отправка в Amplitude возобновлена',
    amplitudeToggleFailed: 'Не удалось переключить отправку в Amplitude',
    // WEB-2580: отвязать насовсем — ссылка в форме «Настроить», ключ стирается.
    amplitudeRemoveKey: 'Отвязать и удалить ключ',
    amplitudeRemoveConfirmTitle: 'Отвязать Amplitude?',
    amplitudeRemoveConfirmBody:
      'Ключ и настройки будут удалены, отправка событий остановится. Чтобы подключить снова, ключ придётся ввести заново.',
    amplitudeRemoveConfirmOk: 'Отвязать',
    amplitudeRemovedToast: 'Amplitude отвязана, ключ удалён',
    amplitudeRemoveFailed: 'Не удалось отвязать Amplitude',
    // WEB-3756 юнит 14 — см. английскую половину выше.
    amplitudeSendingOn: 'Отправка включена',
    amplitudeSendingOff: 'Выключено',

    // ── Журнал отправок в сеть (WEB-2219) ───────────────────────────────────
    // WEB-3756 юнит 13: заголовок окна по эталону — без имени сети.
    deliveryLogTitle: (_network: string) => 'Журнал отправок',
    deliveryLogFilterAll: 'Все',
    deliveryLogFilterDelivered: 'Доставлено',
    deliveryLogFilterSkipped: 'Решили не слать',
    deliveryLogFilterFailed: 'Ошибка',
    deliveryLogPeriodDay: 'За сутки',
    deliveryLogPeriodWeek: 'За 7 дней',
    deliveryLogPeriodMonth: 'За 30 дней',
    deliveryLogRetention: 'Журнал хранится 30 суток.',
    // WEB-2305 / WEB-2413 п.1, WEB-2631: сроки — копии констант сервера
    // (api/capiDispatchAttempts.ts); отмена живёт столько же, сколько её покупка.
    capiDeliveryLogRetention: (days: number, purchaseDays: number) =>
      `В журнале показаны отправки за последние ${days} суток. Отправленные покупки хранятся ${purchaseDays} суток — по ним отменяется конверсия при возврате и работает «только первая покупка»; отмена хранится столько же, сколько её покупка, и поэтому не уходит в сеть дважды. Остальное хранится ${days} суток.`,
    deliveryLogOutcomeDelivered: 'Доставлено',
    deliveryLogOutcomeSkipped: 'Решили не слать',
    deliveryLogOutcomeFailed: 'Ошибка',
    // WEB-3756 сверка пикселей 1, М12: ключ склейки и попытки — отдельными строками.
    deliveryLogMatchKey: (key: string) => `ключ склейки ${key}`,
    deliveryLogAttemptCount: (n: number) => `Попыток: ${n}`,
    deliveryLogEmpty: 'За период таких отправок не было',
    deliveryLogMore: 'Показать ещё',
    deliveryLogLoadFailed: 'Не удалось загрузить журнал отправок',
    deliveryLogEventUnnamed: 'Событие без имени',
    deliveryLogAccepted: (httpCode: string) => `${httpCode} \u00b7 принято`,
    deliveryLogRejected: (httpCode: string, code: string | null) =>
      code
        ? `${httpCode} \u00b7 сеть отвечает отказом (код ${code})`
        : `${httpCode} \u00b7 сеть отвечает отказом`,
    deliveryLogNoAnswer: 'сеть не ответила',
    // WEB-2413 п.5: сеть ответила кодом 2xx, но приём не подтвердила.
    deliveryLogUnconfirmed: (httpCode: string) =>
      `${httpCode} \u00b7 сеть не подтвердила приём, повторим`,
    deliveryLogReasonTestMode:
      'проверочная покупка — в рекламные сети не уходит',
    deliveryLogReasonErased: 'человек попросил удалить свои данные',
    deliveryLogReasonConsent: 'посетитель отказался от отслеживания',
    deliveryLogReasonConsentServerInferred:
      'согласие предположил отправитель, сам посетитель его не давал',
    deliveryLogReasonConsentForeignProject: 'согласие дано в другом проекте',
    deliveryLogReasonNoConsentBasis: 'записи о согласии этого посетителя нет',
    deliveryLogReasonNetworkDeferred: 'эта сеть пока не поддержана',
    deliveryLogReasonNoConnection: 'сеть не подключена в этом проекте',
    deliveryLogReasonFirstPurchaseOnly:
      'в настройках выбрано «только первая оплата человека» — повторную оплату не шлём',
    deliveryLogReasonNoIdentifier:
      'посетителя не по чему опознать — сеть не приняла бы событие',
    // WEB-2241 — решения по отмене конверсии (возврат, проигранный спор).
    deliveryLogReasonRefundsDisabled:
      'в настройках выключено «отправлять возвраты в рекламную сеть»',
    deliveryLogReasonRefundNotFull:
      'возврат частичный — в сеть уходят только полный возврат и проигранный спор',
    deliveryLogReasonRefundScopeUnknown:
      'не удалось определить, полный ли возврат — платёж не прочитался',
    deliveryLogReasonPurchaseNotDispatched:
      'сама покупка в эту сеть не отправлялась — отменять нечего',
    // WEB-2249 — покупка из предпросмотра или тестовой версии воронки.
    deliveryLogReasonNotLive:
      'оплата пришла из предпросмотра или тестовой версии воронки — такую покупку в рекламную сеть не отправляем',
    // WEB-2407 пп.9, 1, 4 (пачка 49, U4 А3).
    deliveryLogReasonAlreadySent:
      'это событие в эту сеть уже доставлено — второй раз не отправляем',
    // WEB-2592 (пачка 49): одна отмена на покупку и сеть.
    deliveryLogReasonRefundAlreadySent:
      'отмена этой покупки в эту сеть уже доставлена (по другому возврату или спору) — вторую отмену не отправляем',
    deliveryLogFailureAwaitingToken: (retryHours: number, maxDays: number) =>
      `токен доступа истёк или отозван — ждём новый; повторяем раз в ${retryHours} ч, не дольше ${maxDays} сут., после замены токена событие дойдёт само`,
    deliveryLogFailureAbandoned:
      'отправка прекращена: событие не доставлено и само больше не отправится',
    deliveryLogReasonUnknown: (status: string) => `не отправлено (${status})`,
    // WEB-2304: строка про повтор у ошибки. Числа — аргументами, не текстом:
    // «из M» берётся из ответа сервера либо из копии константы очереди.
    deliveryLogRetryIn: (minutes: number, attempt: number, max: number) =>
      `повтор через ${minutes} ${plural('ru', minutes, { one: 'минуту', few: 'минуты', many: 'минут' })} (попытка ${attempt} из ${max})`,
    deliveryLogRetryPendingSince: (
      time: string,
      attempt: number,
      max: number,
    ) => `повтор ожидается с ${time} (попытка ${attempt} из ${max})`,
    deliveryLogRetryPending: (attempt: number, max: number) =>
      `повтор ожидается (попытка ${attempt} из ${max})`,
    deliveryLogRetryInHours: (hours: number, attempt: number, max: number) =>
      `повтор через ${hours} ${plural('ru', hours, { one: 'час', few: 'часа', many: 'часов' })} (попытка ${attempt} из ${max})`,
    deliveryLogTokenWaitInMinutes: (minutes: number) =>
      `ждём новый токен, следующая отправка через ${minutes} ${plural('ru', minutes, { one: 'минуту', few: 'минуты', many: 'минут' })}`,
    deliveryLogTokenWaitInHours: (hours: number) =>
      `ждём новый токен, следующая отправка через ${hours} ${plural('ru', hours, { one: 'час', few: 'часа', many: 'часов' })}`,
    deliveryLogTokenWaitPendingSince: (time: string) =>
      `ждём новый токен, следующая отправка ожидается с ${time}`,
    deliveryLogTokenWaitPending:
      'ждём новый токен, следующая отправка ожидается',
    deliveryLogTokenWaitCount: (waits: number) =>
      `(откладывалось ${waits} ${plural('ru', waits, { one: 'раз', few: 'раза', many: 'раз' })})`,
    deliveryLogFinal: 'окончательно не доставлено',
    // WEB-2304 хвосты (F102): попытки одного события свёрнуты в одну строку.
    deliveryLogDeliveredOnAttempt: (attempt: number, max: number) =>
      `доставлено с попытки ${attempt} из ${max}`,
    deliveryLogAttemptLine: (
      time: string,
      attempt: number,
      max: number,
      detail: string,
    ) => `${time} \u00b7 попытка ${attempt} из ${max} \u00b7 ${detail}`,
    orgTeamGuestNote:
      'Состав организации видят её владелец и администраторы. Вы позваны в проекты этой организации — их команда ниже.',
  },
);
